import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, LogOut, Users, Plus, Edit2, Trash2, KeyRound, CheckCircle,
  Clock, XCircle, Search, Camera, X, BarChart3, TrendingUp, TrendingDown, Award,
  Home, ChevronRight, Activity, Target, Medal, Star, ArrowUpRight, ArrowDownRight,
  Menu, XCircle as XIcon, Settings, Save, Phone, MapPin, Mail, FileText, Send,
  Dumbbell, Eye, Briefcase, User, ClipboardCheck, CalendarDays, ChevronDown, ChevronUp,
  Printer, PlusCircle, MinusCircle, FileBarChart, QrCode, RefreshCw, Bell,
  Wallet, CreditCard, Banknote, AlertTriangle, Check, UserPlus, Video, Sparkles
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  PieChart, Pie, Cell
} from 'recharts';
import imageCompression from 'browser-image-compression';
// PROXY: todos los servicios van por proxy para no exponer supabase.co
import {
  clubServiceProxy,
  eventServiceProxy,
  eventClubServiceProxy,
  gymnastServiceProxy,
  imageServiceProxy,
  coachClubAccessServiceProxy,
  eventAccessServiceProxy,
  storageServiceProxy,
  clubAuthServiceProxy,
  scheduleBlockServiceProxy,
  attendanceServiceProxy,
  clubPlanServiceProxy,
  clubMembershipServiceProxy,
  clubPagosServiceProxy,
  coachServiceProxy,
  jobPostingServiceProxy
} from '../services/proxyService';
import { useNotifications } from '../components/shared/useNotifications';
import { useConfirm } from '../components/shared/ConfirmContext';
import { useAuth } from '../context/AuthContext';
import { useGymnastAnalysis } from '../hooks/useGymnastAnalysis';
import { supabase } from '../config/supabase';
import db from '../services/dbProxy';
import { DISCIPLINE_TABS, LEVEL_OPTIONS, FRANJA_OPTIONS, CATEGORY_OPTIONS } from '../utils/constants';
import { calculateAge, formatPersonName } from '../utils/formatters';
import { generateWeeklyCalendarPDF, generateDailyAttendancePDF, generateWeeklyAttendancePDF, generateMonthlyAveragesPDF, generateCoachDailyAttendancePDF, generateCoachWeeklyAttendancePDF, generateCoachMonthlyAttendancePDF, generateCoachQRPDF, generatePagosDiaPDF, generateMensualidadesPDF, generateMembresiasPDF, generateDeudoresPDF, generateAnalyticsExecutivePDF } from '../utils/pdfGenerator';
import { QRCodeSVG } from 'qrcode.react';
import ClubEventAthleteManager from '../components/club/ClubEventAthleteManager';
import CategoryFooter from '../components/shared/CategoryFooter';
import GymnastAnalysisModal from '../components/club/GymnastAnalysisModal';
import IrmaFloatingButton from '../components/IrmaFloatingButton';
import ClubTopbar from '../components/club/ClubTopbar';
import ClubCalendar from '../components/club/ClubCalendar';
import './ClubDashboard.css';

const COLORS = ['#8b5cf6', '#7c3aed', '#818cf8', '#6d28d9', '#4338ca', '#6366f1', '#a78bfa', '#5b21b6'];

// Devuelve los días de la semana [0-6] en los que aplica un bloque.
// Prioriza days_of_week (nuevo, array) con fallback a day_of_week (viejo, int)
const getBlockDays = (block) => {
  if (!block) return [];
  if (Array.isArray(block.days_of_week) && block.days_of_week.length > 0) {
    return block.days_of_week.map(Number).filter(n => !isNaN(n) && n >= 0 && n <= 6);
  }
  if (block.day_of_week != null) return [Number(block.day_of_week)];
  return [];
};

// Verifica si un bloque aplica a un día específico (0-6)
const blockAppliesTo = (block, dayOfWeek) => getBlockDays(block).includes(Number(dayOfWeek));

const getDisciplineDisplay = (discipline, category) => {
  if (!discipline) return { fullName: 'General', logo: null };
  const match = DISCIPLINE_TABS.find(t =>
    t.discipline === discipline && (t.category === (category || '') || (!category && !t.category))
  );
  return match || { fullName: discipline, logo: null };
};

const ClubDashboard = () => {
  const navigate = useNavigate();
  const notify = useNotifications();
  const confirm = useConfirm();
  const { profile, signOut, isAuthenticated, loading: authLoading } = useAuth();

  // Helper to get club ID from auth context
  const getClubId = () => {
    const id = profile?.id;
    if (!id || id === 'undefined' || id === 'null') return null;
    return id;
  };

  const [clubInfo, setClubInfo] = useState(null);
  // Cuando clubInfo tarda más de 10s en cargar, recién ahí mostramos el
  // mensaje de error + botón recargar. Antes, mostramos loading.
  const [clubInfoTimeout, setClubInfoTimeout] = useState(false);
  const [approvedEvents, setApprovedEvents] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [athleteImages, setAthleteImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState('overview');
  const [eventImages, setEventImages] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('cd_sidebar_collapsed') === '1'; } catch { return false; }
  });
  // Dark mode temporalmente deshabilitado (se habilitará más adelante)
  const [darkMode, setDarkMode] = useState(false);
  const [selectedGymnastForAnalysis, setSelectedGymnastForAnalysis] = useState(null);
  const [analyticsTab, setAnalyticsTab] = useState('rendimiento');
  const [temporalScores, setTemporalScores] = useState([]); // scores históricos por copa
  const [temporalLoading, setTemporalLoading] = useState(false);
  const [selectedApparatusTab, setSelectedApparatusTab] = useState(null); // para pestaña Por Aparato
  const [solicitudesTab, setSolicitudesTab] = useState('pending'); // 'pending' | 'history'
  const [eventsTab, setEventsTab] = useState('my-events'); // 'my-events' | 'browse' | 'request'

  // Managing event athletes
  const [managingEventAthletes, setManagingEventAthletes] = useState(null);

  // Coach management
  const [coachRequests, setCoachRequests] = useState([]);
  const [approvedCoaches, setApprovedCoaches] = useState([]);
  const [coachesLooking, setCoachesLooking] = useState([]);
  const [coachActionLoading, setCoachActionLoading] = useState(null);
  const [coachProfileModal, setCoachProfileModal] = useState(null);  // coach object for profile modal
  const [coachCvModal, setCoachCvModal] = useState(null);  // cv_url string for PDF viewer
  const [coachPhotoMap, setCoachPhotoMap] = useState({});  // coachId -> photoUrl
  const [coachRequestData, setCoachRequestData] = useState({});  // coachId -> coach data from request
  const [loadedRequestPhotos, setLoadedRequestPhotos] = useState(false);  // track if photos loaded
  const [loadedApprovedPhotos, setLoadedApprovedPhotos] = useState(false);  // track if approved photos loaded
  const [loadingRequestPhotos, setLoadingRequestPhotos] = useState(false);  // loading state
  const [coachesTab, setCoachesTab] = useState('hired'); // 'hired' | 'applicants' | 'looking' | 'create'
  const [coachContactStatus, setCoachContactStatus] = useState({}); // coachId -> 'pending' | 'confirmed' | null
  const [contactingCoach, setContactingCoach] = useState(null); // coachId being contacted
  
  // Job posting form
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobForm, setJobForm] = useState({ title: '', description: '', disciplines: [], requirements: '', location: '', salary_range: '' });
  const [savingJob, setSavingJob] = useState(false);
  const [jobApplications, setJobApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [applicationActionLoading, setApplicationActionLoading] = useState(null);
  const [clubJobPostings, setClubJobPostings] = useState([]);
  const [loadingClubJobs, setLoadingClubJobs] = useState(false);

  // Event code request
  const [eventCode, setEventCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeResult, setCodeResult] = useState(null);

  // All events (browse)
  const [allPlatformEvents, setAllPlatformEvents] = useState([]);
  const [allEventsSearch, setAllEventsSearch] = useState('');
  const [allEventsLoading, setAllEventsLoading] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(null);

  // Athlete form
  const [showAthleteForm, setShowAthleteForm] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [athleteForm, setAthleteForm] = useState({ name: '', level: '', franja: '', birth_date: '', category: '', plan_id: '', guardian_name: '', guardian_phone: '' });
  const [athleteSearch, setAthleteSearch] = useState('');
  const [athleteLevelFilter, setAthleteLevelFilter] = useState('');
  const [athleteCategoryFilter, setAthleteCategoryFilter] = useState('');
  const [athletePhotoFile, setAthletePhotoFile] = useState(null);
  const [athletePhotoPreview, setAthletePhotoPreview] = useState(null);
  // Planes activos del club (para combo en form de atleta)
  const [clubPlans, setClubPlans] = useState([]);
  // Map: gymnast_id -> { plan_id, plan: { name, days_per_week } }
  const [athletePlanMap, setAthletePlanMap] = useState({});
  // Filtros de membresías (lazy load)
  const [membershipSearch, setMembershipSearch] = useState('');
  const [membershipStatusFilter, setMembershipStatusFilter] = useState('');

  // Analytics filter
  const [selectedEvent, setSelectedEvent] = useState('all');

  // Attendance
  const getLocalDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };
  const [attendanceDate, setAttendanceDate] = useState(getLocalDate());
  const [attendanceRecords, setAttendanceRecords] = useState({});

  // Finanzas
  const [finanzasActiveTab, setFinanzasActiveTab] = useState('planes');  // planes | membresias | pagos | alertas
  const [clubPlanes, setClubPlanes] = useState([]);
  const [clubMemberships, setClubMemberships] = useState([]);
  const [clubPagos, setClubPagos] = useState([]);
  const [alertasDeuda, setAlertasDeuda] = useState([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [financiaLoading, setFinanciaLoading] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [pagoForm, setPagoForm] = useState({ amount: 0, payment_method: 'efectivo', is_advance: false });
  const [deletePagoModal, setDeletePagoModal] = useState(null); // { id, athleteName, amountStr, dateStr }
  const [deletingPago, setDeletingPago] = useState(false);
  const getTodayISO = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };
  const [pagosFilterAthlete, setPagosFilterAthlete] = useState('');
  const [pagosFilterFrom, setPagosFilterFrom] = useState(() => getTodayISO());
  const [pagosFilterTo, setPagosFilterTo] = useState(() => getTodayISO());
  const [pagosFilterMethod, setPagosFilterMethod] = useState('');
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [justifyModal, setJustifyModal] = useState(null);
  const [justification, setJustification] = useState('');
  const [rescheduledDate, setRescheduledDate] = useState('');

  // Schedule
  const [scheduleBlocks, setScheduleBlocks] = useState([]);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [blockForm, setBlockForm] = useState({ title: '', duration_minutes: 60, color: '#8b5cf6', days_of_week: [1], start_time: '08:00', end_time: '09:00' });

  // Block Athletes Modal
  const [blockAthletesModal, setBlockAthletesModal] = useState(null); // block object
  const [blockAthletes, setBlockAthletes] = useState([]); // assigned athletes
  const [blockCoaches, setBlockCoaches] = useState([]); // assigned coaches
  const [blockAthleteSearch, setBlockAthleteSearch] = useState('');
  const [blockCoachSearch, setBlockCoachSearch] = useState('');
  const [blockAthleteLoading, setBlockAthleteLoading] = useState(false);

  // Attendance accordion
  const [allBlocks, setAllBlocks] = useState([]); // all blocks for the club (unfiltered)
  const [attendanceAccordion, setAttendanceAccordion] = useState({}); // blockId -> open/closed
  const [blockAttendanceMap, setBlockAttendanceMap] = useState({}); // blockId -> { athletes: [...], records: {} }
  const [justifiedRecords, setJustifiedRecords] = useState({}); // key -> boolean

  // Coach attendance
  const [coachAttendanceRecords, setCoachAttendanceRecords] = useState({}); // coachId -> 'present'|'absent'
  const [coachAttendanceHistory, setCoachAttendanceHistory] = useState([]);
  const [showGeneralCoachQR, setShowGeneralCoachQR] = useState(false); // show general QR modal
  const [coachQrToken, setCoachQrToken] = useState(null); // the club's QR token
  const [showCoachTodayModal, setShowCoachTodayModal] = useState(false); // modal asistencia de hoy
  const [showBlockSelectModal, setShowBlockSelectModal] = useState(null); // null or array of blocks for daily print selection
  const [coachAttPasswordModal, setCoachAttPasswordModal] = useState(false);
  const [coachAttPassword, setCoachAttPassword] = useState('');
  const [coachAttPasswordError, setCoachAttPasswordError] = useState('');

  // Reports
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());

  // Club settings
  const [clubForm, setClubForm] = useState({ name: '', email: '', phone: '', location: '', disciplines: [], facebook_url: '', instagram_url: '', twitter_url: '', is_federated: false });
  const [togglingPose, setTogglingPose] = useState(false);
  const [clubLogoFile, setClubLogoFile] = useState(null);
  const [clubLogoPreview, setClubLogoPreview] = useState(null);
  const [clubLogoUrl, setClubLogoUrl] = useState(null);
  const [savingClub, setSavingClub] = useState(false);

  // ============================================================
  // DEBUG LOGGING (remove in production)
  // ============================================================
  const _ = console;

  // ============================================================

  useEffect(() => {
    if (authLoading) return;
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    loadClubData(profile.id);
  }, [authLoading, profile?.id]);

  // Timer de 10s: si clubInfo sigue null pasado ese tiempo, mostrar el
  // mensaje de error + botón recargar. Se resetea en cuanto clubInfo carga.
  useEffect(() => {
    if (clubInfo) { setClubInfoTimeout(false); return; }
    setClubInfoTimeout(false);
    const t = setTimeout(() => setClubInfoTimeout(true), 10000);
    return () => clearTimeout(t);
  }, [clubInfo]);

const loadClubData = async (clubId) => {
    try {
      // Use profile directly as it IS the club data
      setClubInfo(profile);
      // Cargar logo desde tabla images
      const imgRes = await imageServiceProxy.getByEntity('club', clubId);
      if (imgRes.data) {
        setClubLogoUrl(imgRes.data);
      }
      setClubForm({ 
        name: profile.name || '', 
        email: profile.email || '', 
        phone: profile.phone || '', 
        location: profile.location || '', 
        disciplines: profile.disciplines || [], 
        facebook_url: profile.facebook_url || '', 
        instagram_url: profile.instagram_url || '', 
        twitter_url: profile.twitter_url || '',
        is_federated: profile.is_federated || false
      });

      // QR token
      if (profile.coach_qr_token) {
        setCoachQrToken(profile.coach_qr_token);
      } else {
        const token = Math.random().toString(36).substring(2, 26);
        await clubServiceProxy.update(profile.id, { coach_qr_token: token });
        setCoachQrToken(token);
      }

      // Load related data
      const [eventsRes, requestsRes, gymnastsRes, coachReqRes, coachApprovedRes, plansRes, membershipsRes] = await Promise.all([
        eventClubServiceProxy.getByClub(profile.id),
        eventClubServiceProxy.getAllRequests(profile.id),
        gymnastServiceProxy.getByClub(profile.id),
        coachClubAccessServiceProxy.getRequestsByClub(profile.id),
        coachClubAccessServiceProxy.getApprovedCoaches(profile.id),
        clubPlanServiceProxy.getByClub(profile.id),
        clubMembershipServiceProxy.getByClub(profile.id),
      ]);
      
setApprovedEvents(eventsRes.data || []);
        setAllRequests(requestsRes.data || []);
        const loadedAthletes = (gymnastsRes.data || []).map(g => ({
          ...g,
          name: formatPersonName(g.name),
          guardian_name: g.guardian_name ? formatPersonName(g.guardian_name) : g.guardian_name,
          scores: g.scores || {},
        }));
        setAthletes(loadedAthletes);
        // Poblar cache de avatares desde la columna photo_url (fast path)
        const imgMap = {};
        for (const a of loadedAthletes) {
          if (a.photo_url) imgMap[a.id] = a.photo_url;
        }
        setAthleteImages(prev => ({ ...prev, ...imgMap }));
        setCoachRequests((coachReqRes.data || []).map(r => ({
          ...r,
          coaches: r.coaches ? { ...r.coaches, name: formatPersonName(r.coaches.name) } : r.coaches,
        })));
        setApprovedCoaches((coachApprovedRes.data || []).map(c => ({
          ...c,
          name: formatPersonName(c.name),
          coaches: c.coaches ? { ...c.coaches, name: formatPersonName(c.coaches.name) } : c.coaches,
        })));

        // Planes y membresías activas
        setClubPlans(plansRes.data || []);
        const planMap = {};
        for (const m of (membershipsRes.data || [])) {
          if (m.status === 'active' && m.gymnast_id) {
            const g = m.gimnasts ? { ...m.gimnasts, name: formatPersonName(m.gimnasts.name) } : m.gimnasts;
            planMap[m.gymnast_id] = { plan_id: m.plan_id, plan: m.club_planes || null, membership_id: m.id, gymnast: g };
          }
        }
        setAthletePlanMap(planMap);
        
// Load photos for approved coaches and pending requests
        const requestCoachData = {};
        for (const req of (coachReqRes.data || [])) {
          const coachId = req.coach_id;
          if (coachId && !requestCoachData[coachId]) {
            try {
              const { data: coachData } = await coachServiceProxy.getById(coachId);
              if (coachData) {
                requestCoachData[coachId] = coachData;
              }
            } catch (e) { /* ignore */ }
          }
        }
        setCoachRequestData(prev => ({ ...prev, ...requestCoachData }));
        
        // OJO: en coach_club_access, `id` es del propio registro de acceso; el id
        // del entrenador está en `coach_id` (o en el join `coaches.id`).
        const allCoachIds = [
          ...(coachApprovedRes.data || []).map(c => c.coach_id || c.coaches?.id).filter(Boolean),
          ...(coachReqRes.data || []).flatMap(req => [req.coach_id, req.coaches?.id]).filter(Boolean)
        ];
        if (allCoachIds.length > 0) {
          const photoMap = { ...coachPhotoMap };
          for (const coachId of allCoachIds) {
            if (coachId && !photoMap[coachId]) {
              try {
                const { data: imgData } = await imageServiceProxy.getByEntity('coach', coachId);
                if (imgData) {
                  photoMap[coachId] = imgData;
                }
              } catch (e) { /* ignore */ }
            }
          }
          setCoachPhotoMap(prev => ({ ...prev, ...photoMap }));
        }
    } catch (err) {
      console.error('Error loading club data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadEntityImages = async (folder, items) => {
    const images = {};
    if (!items || items.length === 0) return images;

    // 1) FAST PATH: usar la columna de URL que viene con el registro
    //    (photo_url para gimnasts/coaches, image_url para events/clubs)
    const urlField = folder === 'gymnasts' || folder === 'coaches' ? 'photo_url' : 'image_url';
    const missing = [];
    for (const item of items) {
      const url = item?.[urlField];
      if (url) images[item.id] = url;
      else missing.push(item);
    }

    // 2) FALLBACK: si quedan items sin URL (legacy sin sync),
    //    buscarlos en storage por coincidencia de id en el nombre
    if (missing.length > 0) {
      try {
        const { data: files } = await storageServiceProxy.list('gymnastics-images', folder, 1000);
        if (files && files.length > 0) {
          for (const item of missing) {
            const match = files.find(f => f.name.includes(item.id));
            if (match) {
              const { data: urlData } = await storageServiceProxy.getPublicUrl('gymnastics-images', `${folder}/${match.name}`);
              if (urlData?.publicUrl) images[item.id] = urlData.publicUrl;
            }
          }
        }
      } catch (e) { console.error(`Error loading ${folder} images (fallback):`, e); }
    }
    return images;
  };

  const handleLogout = async () => { await signOut(); navigate('/clubes'); notify.success('Sesión cerrada'); };

  // Aplicar y persistir modo oscuro (DESHABILITADO: siempre light mode por ahora)
  useEffect(() => {
    const root = document.documentElement;
    root.removeAttribute('data-theme');
    try { localStorage.removeItem('cd_dark_mode'); } catch {}
    return () => { root.removeAttribute('data-theme'); };
  }, [darkMode]);

  // Persistir estado colapsado
  useEffect(() => {
    try { localStorage.setItem('cd_sidebar_collapsed', sidebarCollapsed ? '1' : '0'); } catch {}
  }, [sidebarCollapsed]);

  // Toggle inteligente: en desktop colapsa, en móvil abre off-canvas
  const handleToggleSidebar = () => {
    if (window.matchMedia('(max-width: 992px)').matches) {
      setSidebarOpen(v => !v);
    } else {
      setSidebarCollapsed(v => !v);
    }
  };

  // ============ Event Code Request ============
  const handleSubmitCode = async () => {
    if (!eventCode.trim()) { notify.warning('Ingresa un código de copa'); return; }
    setCodeLoading(true);
    setCodeResult(null);
    try {
      const { data: codeData, error: codeError } = await eventAccessServiceProxy.validateCode(eventCode);
      if (codeError || !codeData) { setCodeResult({ type: 'error', message: codeError || 'Código inválido o expirado' }); setCodeLoading(false); return; }
      const { error: requestError } = await eventAccessServiceProxy.requestEventAccess(getClubId(), codeData.event_id, eventCode.toUpperCase().trim());
      if (requestError) { setCodeResult({ type: 'error', message: requestError }); setCodeLoading(false); return; }
      setCodeResult({ type: 'success', message: `Solicitud enviada para "${codeData.events?.name || 'Copa'}". Espera aprobación.` });
      setEventCode('');
      const { data: requests } = await clubAuthServiceProxy.getAllEventRequests(getClubId());
      setAllRequests(requests || []);
    } catch (err) {
      setCodeResult({ type: 'error', message: 'Error al procesar la solicitud' });
    } finally { setCodeLoading(false); }
  };

  // ============ All Events (Browse) ============
  const loadAllPlatformEvents = useCallback(async () => {
    setAllEventsLoading(true);
    try {
      const { data } = await eventServiceProxy.getAll();
      setAllPlatformEvents((data || []).filter(e => e.is_active && (e.approval_status === 'approved' || !e.approval_status)));
    } catch (err) {
      console.error('Error loading all events:', err);
    } finally {
      setAllEventsLoading(false);
    }
  }, []);

  // Load all events when switching to browse tab
  useEffect(() => {
    if (activePage === 'events' && eventsTab === 'browse' && allPlatformEvents.length === 0) {
      loadAllPlatformEvents();
    }
  }, [activePage, eventsTab, allPlatformEvents.length, loadAllPlatformEvents]);

  const filteredAllEvents = useMemo(() => {
    let events = allPlatformEvents;

    // Filter by club's disciplines if any are set
    const clubDisciplines = clubInfo?.disciplines || [];
    if (clubDisciplines.length > 0) {
      // Build a set of discipline values the club cares about
      const matchDisciplines = new Set();
      clubDisciplines.forEach(key => {
        const tab = DISCIPLINE_TABS.find(t => t.key === key);
        if (tab) matchDisciplines.add(tab.discipline);
      });
      // Build a set of discipline+category combos for more precise matching (e.g. artistic-wag vs artistic-mag)
      const matchKeys = new Set(clubDisciplines);

      events = events.filter(e => {
        if (!e.discipline) return true; // show events with no discipline set
        // Check if there's a matching key (discipline + category combo)
        const eventKey = e.category ? `${e.discipline}-${e.category}` : e.discipline;
        if (matchKeys.has(eventKey)) return true;
        // Fallback: match just by discipline (e.g. club has 'artistic-wag', event has discipline='artistic' without category)
        if (matchDisciplines.has(e.discipline)) return true;
        return false;
      });
    }

    const q = allEventsSearch.toLowerCase().trim();
    if (!q) return events;
    return events.filter(e =>
      (e.name || '').toLowerCase().includes(q) ||
      (e.location || '').toLowerCase().includes(q) ||
      (e.discipline || '').toLowerCase().includes(q)
    );
  }, [allPlatformEvents, allEventsSearch, clubInfo?.disciplines]);

  const handleRequestAccessToEvent = async (eventId) => {
    setRequestingAccess(eventId);
    try {
      // Check if there's an active code for this event
      const { data: codeData } = await eventAccessServiceProxy.getEventCode(eventId);
      const code = codeData?.code || '';
      const { error } = await eventAccessServiceProxy.requestEventAccess(getClubId(), eventId, code);
      if (error) {
        notify.error(error);
      } else {
        notify.success('Solicitud enviada correctamente. Espera aprobación del organizador.');
        const { data: requests } = await clubAuthServiceProxy.getAllEventRequests(getClubId());
        setAllRequests(requests || []);
      }
    } catch (err) {
      notify.error('Error al solicitar acceso');
    } finally {
      setRequestingAccess(null);
    }
  };

  const getEventRequestStatus = (eventId) => {
    const req = allRequests.find(r => (r.event_id || r.event?.id) === eventId);
    return req?.status || null;
  };

  const handleDeleteRejectedRequest = async (eventId) => {
    try {
      const clubId = getClubId();
      const { error } = await db('event_clubs')
        .delete()
        .eq('club_id', clubId)
        .eq('event_id', eventId);
      if (error) throw error;
      setAllRequests(prev => prev.filter(r => (r.event_id || r.event?.id) !== eventId));
      notify.success('Solicitud eliminada');
    } catch (err) {
      notify.error('Error al eliminar la solicitud');
    }
  };

  // ============ Athlete Photo ============
  const handleAthletePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 0.1, maxWidthOrHeight: 400, useWebWorker: true });
      setAthletePhotoFile(compressed);
      const reader = new FileReader();
      reader.onloadend = () => setAthletePhotoPreview(reader.result);
      reader.readAsDataURL(compressed);
    } catch (err) { notify.error('Error al procesar la imagen'); }
  };

  const uploadAthletePhoto = async (athleteId) => {
    if (!athletePhotoFile) return null;
    try {
      // 1) Subir a storage (cliente directo Supabase)
      const fileExt = (athletePhotoFile.name?.split('.').pop() || 'jpg').toLowerCase();
      const filePath = `gymnasts/${athleteId}-${Date.now()}.${fileExt}`;
      const { error: upErr } = await supabase.storage
        .from('gymnastics-images')
        .upload(filePath, athletePhotoFile, { cacheControl: '3600', upsert: true });
      if (upErr) throw upErr;

      // 2) URL pública
      const { data: pub } = supabase.storage.from('gymnastics-images').getPublicUrl(filePath);
      const publicUrl = pub?.publicUrl;
      if (!publicUrl) throw new Error('No se obtuvo URL pública');

      // 3) Registrar en tabla images (eliminar antigua + insertar nueva)
      await supabase.from('images').delete().eq('entity_type', 'gymnast').eq('entity_id', String(athleteId));
      await supabase.from('images').insert([{
        entity_type: 'gymnast',
        entity_id: String(athleteId),
        image_url: publicUrl,
        file_path: filePath,
      }]);

      // 4) Sincronizar columna gimnasts.photo_url
      await supabase.from('gimnasts').update({ photo_url: publicUrl }).eq('id', athleteId);

      return publicUrl;
    } catch (err) {
      console.error('Error subiendo foto:', err);
      notify.error('Error al subir la foto: ' + (err.message || 'desconocido'));
      return null;
    }
  };

  // ============ Athletes CRUD ============
  const handleSaveAthlete = async () => {
    if (!athleteForm.name.trim()) { notify.warning('El nombre es obligatorio'); return; }
    try {
      const athleteData = {
        name: formatPersonName(athleteForm.name.trim()),
        level: athleteForm.level,
        franja: athleteForm.franja,
        birth_date: athleteForm.birth_date || null,
        category: athleteForm.category || null,
        guardian_name: athleteForm.guardian_name?.trim() ? formatPersonName(athleteForm.guardian_name.trim()) : null,
        guardian_phone: athleteForm.guardian_phone?.trim() || null,
        scores: athleteForm.scores || {},
        club_id: getClubId()
      };
      const selectedPlanId = athleteForm.plan_id || null;
      const clubIdForMembership = getClubId();

      const syncPlan = async (gymnastId) => {
        // Solo cambiar si el plan seleccionado es distinto del actual
        const currentPlanId = athletePlanMap[gymnastId]?.plan_id || null;
        if (selectedPlanId === currentPlanId) return;
        const { data: newMembership, error: mErr } = await clubMembershipServiceProxy.setActivePlanForGymnast(gymnastId, clubIdForMembership, selectedPlanId);
        if (mErr) {
          notify.warning('Atleta guardada, pero no se pudo actualizar el plan: ' + mErr);
          return;
        }
        const planObj = selectedPlanId ? (clubPlans.find(p => p.id === selectedPlanId) || null) : null;
        setAthletePlanMap(prev => {
          const next = { ...prev };
          if (selectedPlanId) {
            next[gymnastId] = {
              plan_id: selectedPlanId,
              plan: planObj,
              membership_id: newMembership?.id || null,
            };
          } else {
            delete next[gymnastId];
          }
          return next;
        });
      };

      if (editingAthlete) {
        const result = await gymnastServiceProxy.update(editingAthlete.id, athleteData);
        if (result.error) throw new Error(result.error);
        let newPhotoUrl = null;
        if (athletePhotoFile) {
          newPhotoUrl = await uploadAthletePhoto(editingAthlete.id);
          if (newPhotoUrl) setAthleteImages(prev => ({ ...prev, [editingAthlete.id]: newPhotoUrl }));
        }
        setAthletes(prev => prev.map(a => a.id === editingAthlete.id
          ? { ...a, ...athleteData, photo_url: newPhotoUrl || a.photo_url }
          : a));
        await syncPlan(editingAthlete.id);
        notify.success('Atleta actualizada');
      } else {
        const result = await gymnastServiceProxy.create(athleteData);
        if (result.error) throw new Error(result.error);
        let newPhotoUrl = null;
        if (athletePhotoFile && result.data?.id) {
          newPhotoUrl = await uploadAthletePhoto(result.data.id);
          if (newPhotoUrl) setAthleteImages(prev => ({ ...prev, [result.data.id]: newPhotoUrl }));
        }
        setAthletes(prev => [...prev, { ...(result.data || {}), photo_url: newPhotoUrl || result.data?.photo_url || null }]);
        if (result.data?.id) await syncPlan(result.data.id);
        notify.success('Atleta creada');
      }
      resetAthleteForm();
    } catch (err) { notify.error('Error al guardar: ' + err.message); }
  };

  const handleEditAthlete = (athlete) => {
    setEditingAthlete(athlete);
    const currentPlanId = athletePlanMap[athlete.id]?.plan_id || '';
    setAthleteForm({
      name: athlete.name,
      level: athlete.level || '',
      franja: athlete.franja || '',
      birth_date: athlete.birth_date || '',
      category: athlete.category || '',
      plan_id: currentPlanId,
      guardian_name: athlete.guardian_name || '',
      guardian_phone: athlete.guardian_phone || '',
    });
    setAthletePhotoFile(null);
    setAthletePhotoPreview(athleteImages[athlete.id] || null);
    setShowAthleteForm(true);
  };

  const handleDeleteAthlete = async (athleteId) => {
    const ok = await confirm({
      title: 'Eliminar Atleta',
      message: '¿Eliminar esta atleta?',
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;
    try {
      const result = await gymnastServiceProxy.delete(athleteId);
      if (result.error) throw new Error(result.error);
      setAthletes(athletes.filter(a => a.id !== athleteId));
      notify.success('Atleta eliminada');
    } catch (err) { notify.error('Error: ' + err.message); }
  };

  const resetAthleteForm = () => {
    setEditingAthlete(null);
    setAthleteForm({ name: '', level: '', franja: '', birth_date: '', category: '', plan_id: '', guardian_name: '', guardian_phone: '' });
    setShowAthleteForm(false);
    setAthletePhotoFile(null);
    setAthletePhotoPreview(null);
  };

  // Mapa gymnast_id -> fecha del último pago (ISO date string)
  const lastPaymentByGymnast = useMemo(() => {
    const map = {};
    for (const p of (clubPagos || [])) {
      const gid = p.gymnast_id || p.gimnasts?.id;
      if (!gid || !p.payment_date) continue;
      if (!map[gid] || new Date(p.payment_date) > new Date(map[gid])) {
        map[gid] = p.payment_date;
      }
    }
    return map;
  }, [clubPagos]);

  // ¿La atleta pagó ya el mes/periodo en curso?
  const isPaidThisMonth = (gymnastId) => {
    const last = lastPaymentByGymnast[gymnastId];
    if (!last) return false;
    const d = new Date(last);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  };

  // Estado de membresía: al_dia (verde) | gracia (amarillo) | moroso (rojo) | sin_membresia
  const GRACE_DAYS = 5;
  const membershipStatus = (gymnastId) => {
    if (!athletePlanMap[gymnastId]?.membership_id) return 'sin_membresia';
    const last = lastPaymentByGymnast[gymnastId];
    const now = new Date();
    if (!last) {
      // sin pagos aún: durante los primeros días del mes = gracia
      return now.getDate() <= GRACE_DAYS ? 'gracia' : 'moroso';
    }
    const d = new Date(last);
    // Al día: pago en el mes actual
    if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) return 'al_dia';
    // Pago del mes anterior + aún estamos en los primeros días del mes
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    if (d.getFullYear() === prev.getFullYear() && d.getMonth() === prev.getMonth() && now.getDate() <= GRACE_DAYS) {
      return 'gracia';
    }
    return 'moroso';
  };

  const filteredAthletes = useMemo(() => {
    let result = athletes;
    if (athleteSearch.trim()) {
      result = result.filter(a => a.name.toLowerCase().includes(athleteSearch.toLowerCase()));
    }
    if (athleteLevelFilter) {
      result = result.filter(a => a.level === athleteLevelFilter);
    }
    if (athleteCategoryFilter) {
      result = result.filter(a => a.category === athleteCategoryFilter);
    }
    return result;
  }, [athletes, athleteSearch, athleteLevelFilter, athleteCategoryFilter]);

  // ============ Coach Access Management ============
  const handleApproveCoach = async (accessId) => {
    setCoachActionLoading(accessId);
    try {
      const { error } = await coachClubAccessServiceProxy.approveRequest(accessId);
      if (error) { notify.error(error); return; }
      notify.success('Entrenador/a aprobado/a');
      // Refresh lists
      const [reqRes, approvedRes] = await Promise.all([
        coachClubAccessServiceProxy.getRequestsByClub(getClubId()),
        coachClubAccessServiceProxy.getApprovedCoaches(getClubId())
      ]);
      setCoachRequests(reqRes.data || []);
      setApprovedCoaches(approvedRes.data || []);
    } catch (err) { notify.error('Error al aprobar'); }
    finally { setCoachActionLoading(null); }
  };

  const handleRejectCoach = async (accessId) => {
    const ok = await confirm({
      title: 'Rechazar Entrenador',
      message: '¿Rechazar esta solicitud de entrenador/a?',
      type: 'danger',
      confirmText: 'Rechazar'
    });
    if (!ok) return;
    setCoachActionLoading(accessId);
    try {
      const { error } = await coachClubAccessServiceProxy.rejectRequest(accessId);
      if (error) { notify.error(error); return; }
      notify.success('Solicitud rechazada');
      const { data } = await coachClubAccessServiceProxy.getRequestsByClub(getClubId());
      setCoachRequests(data || []);
    } catch (err) { notify.error('Error al rechazar'); }
    finally { setCoachActionLoading(null); }
  };

  const handleRemoveCoach = async (accessId) => {
    const ok = await confirm({
      title: 'Remover Entrenador',
      message: '¿Remover este entrenador/a del club?',
      type: 'danger',
      confirmText: 'Remover'
    });
    if (!ok) return;
    try {
      const { error } = await coachClubAccessServiceProxy.removeCoach(accessId);
      if (error) { notify.error(error); return; }
      notify.success('Entrenador/a removido/a');
      setApprovedCoaches(prev => prev.filter(c => c.access_id !== accessId));
    } catch (err) { notify.error('Error al remover'); }
  };

  const loadCoachesLookingForClub = useCallback(async () => {
    try {
      const { data, error } = await coachClubAccessServiceProxy.getCoachesLookingForClub();
      if (error) { console.error('Error loading looking coaches:', error); return; }
      setCoachesLooking(data || []);
      
      // Load photos for looking coaches
      if (data && data.length > 0) {
        const photoMap = { ...coachPhotoMap };
        for (const coach of data) {
          if (coach.id && !photoMap[coach.id]) {
            const { data: imgData } = await imageServiceProxy.getByEntity('coach', coach.id);
            if (imgData) {
              photoMap[coach.id] = imgData;
            } else if (coach.photo_url) {
              photoMap[coach.id] = coach.photo_url;
            }
          }
        }
        setCoachPhotoMap(prev => ({ ...prev, ...photoMap }));
      }
      
      // Load contact status for each coach
      const clubId = getClubId();
      if (clubId && data?.length > 0) {
        const statuses = {};
        for (const coach of data) {
          const { data: contact } = await coachClubAccessServiceProxy.getContactStatus(clubId, coach.id);
          if (contact) {
            statuses[coach.id] = contact.status;
          }
        }
        setCoachContactStatus(statuses);
      }
    } catch (err) { console.error('Error loading looking coaches:', err); }
  }, []);

  // Refresh contact status when tab is viewed
  useEffect(() => {
    if (activePage !== 'coaches') return;
    const clubId = getClubId();
    if (!clubId) return;
    
    const loadContactsForCoaches = async (coachesList) => {
      const statuses = {};
      for (const coach of coachesList) {
        if (coach.id) {
          const { data: contact } = await coachClubAccessServiceProxy.getContactStatus(clubId, coach.id);
          if (contact) {
            statuses[coach.id] = contact.status;
          }
        }
      }
      setCoachContactStatus(prev => ({ ...prev, ...statuses }));
    };
    
    // Load for different lists
    if (coachesTab === 'looking' && coachesLooking.length > 0) {
      loadContactsForCoaches(coachesLooking);
    } else if (coachesTab === 'hired' && approvedCoaches.length > 0) {
      loadContactsForCoaches(approvedCoaches);
    } else if (coachesTab === 'applicants' && jobApplications.length > 0) {
      const applicationCoaches = jobApplications.map(app => app.coaches).filter(Boolean);
      loadContactsForCoaches(applicationCoaches);
    }
  }, [activePage, coachesTab]);

  const loadJobApplications = useCallback(async () => {
    const clubId = getClubId();
    if (!clubId) return;
    setLoadingApplications(true);
    try {
      const { data, error } = await jobPostingServiceProxy.getApplicationsByClub(clubId);
      if (error) { console.error('Error loading applications:', error); return; }
      
      // Load coach photos
      if (data && data.length > 0) {
        console.log('[Postulaciones] Loading photos for', data.length, 'applications');
        console.log('[Postulaciones] App data:', JSON.stringify(data.map(a => ({ coachId: a.coaches?.id, photo_url: a.coaches?.photo_url }))));
        const photoMap = { ...coachPhotoMap };
        for (const app of data) {
          const coachId = app.coaches?.id;
          if (coachId && !photoMap[coachId]) {
            // First check imgData, then fallback to photo_url from coach
            const { data: imgData, error: imgError } = await imageServiceProxy.getByEntity('coach', coachId);
            console.log('[Postulaciones] Image for coach', coachId, '-> imgData:', imgData, 'coachPhotoUrl:', app.coaches?.photo_url);
            if (imgData) {
              photoMap[coachId] = imgData;
            } else if (app.coaches?.photo_url) {
              console.log('[Postulaciones] Falling back to photo_url:', app.coaches.photo_url);
              photoMap[coachId] = app.coaches.photo_url;
            }
          }
        }
        setCoachPhotoMap(prev => ({ ...prev, ...photoMap }));
        console.log('[Postulaciones] Photo map:', photoMap);
      }
      
      setJobApplications(data || []);
    } catch (err) { console.error('Error loading applications:', err); }
    setLoadingApplications(false);
  }, [getClubId]);

  // Load applications when switching to applicants tab
  useEffect(() => {
    if (activePage === 'coaches' && coachesTab === 'applicants') {
      loadJobApplications();
    }
  }, [activePage, coachesTab]);

  // Load approved coach photos when switching to hired tab
  const loadApprovedCoachPhotos = useCallback(async () => {
    const photoMap = { ...coachPhotoMap };
    const updatedCoaches = [];

    for (const item of approvedCoaches) {
      // El id del entrenador está en `coach_id` o en el join `coaches.id`
      // (NO en `item.id`, que pertenece a coach_club_access).
      const realCoachId = item.coach_id || item.coaches?.id;
      if (!realCoachId) continue;

      const { data: coachData } = await coachServiceProxy.getById(realCoachId);

      const existingCoach = item.coaches || {};
      const merged = {
        ...existingCoach,
        ...coachData,
      };
      // Mantener estructura esperada por el render: item con item.coaches
      updatedCoaches.push({ ...item, coaches: merged });

      if (merged.photo_url) {
        photoMap[realCoachId] = merged.photo_url;
      }
    }

    if (updatedCoaches.length > 0) {
      setApprovedCoaches(updatedCoaches);
    }
    setCoachPhotoMap(prev => ({ ...prev, ...photoMap }));
  }, [approvedCoaches]);

  useEffect(() => {
    if (activePage === 'coaches' && coachesTab === 'hired' && approvedCoaches.length > 0 && !loadedApprovedPhotos) {
      setLoadedApprovedPhotos(true);
      loadApprovedCoachPhotos();
    }
    if (coachesTab !== 'hired') {
      setLoadedApprovedPhotos(false);
    }
  }, [activePage, coachesTab, approvedCoaches, loadedApprovedPhotos]);

  // Load request photos when switching to requests tab
  const loadRequestPhotos = useCallback(async () => {
    if (loadingRequestPhotos) return;
    setLoadingRequestPhotos(true);
    const pendingRequests = coachRequests.filter(r => r.status === 'pending');
    const photoMap = { ...coachPhotoMap };
    const requestCoachData = { ...coachRequestData };
    let hasUpdates = false;
    
    for (const req of pendingRequests) {
      const coachId = req.coach_id || req.coaches?.id;
      if (coachId) {
        // Load coach data (includes photo) if not already loaded
        if (!requestCoachData[coachId]) {
          const { data: coachData, error } = await coachServiceProxy.getById(coachId);
          if (coachData && !error) {
            requestCoachData[coachId] = coachData;
            if (coachData.photo_url) {
              photoMap[coachId] = coachData.photo_url;
            }
            hasUpdates = true;
          }
        }
      }
    }
    
    if (hasUpdates) {
      setCoachRequestData(prev => ({ ...prev, ...requestCoachData }));
      setCoachPhotoMap(prev => ({ ...prev, ...photoMap }));
    }
    setLoadingRequestPhotos(false);
  }, [coachRequests, coachPhotoMap, loadingRequestPhotos]);

  useEffect(() => {
    if (activePage === 'coaches' && coachesTab === 'requests' && coachRequests.length > 0 && !loadedRequestPhotos) {
      setLoadedRequestPhotos(true);
      loadRequestPhotos();
    }
    if (coachesTab !== 'requests') {
      setLoadedRequestPhotos(false);
    }
  }, [activePage, coachesTab, coachRequests, loadedRequestPhotos]);

  const loadClubJobPostings = useCallback(async () => {
    const clubId = getClubId();
    if (!clubId) return;
    setLoadingClubJobs(true);
    try {
      const { data, error } = await jobPostingServiceProxy.getByClub(clubId);
      if (error) { console.error('Error loading club jobs:', error); return; }
      setClubJobPostings(data || []);
    } catch (err) { console.error('Error loading club jobs:', err); }
    setLoadingClubJobs(false);
  }, [getClubId]);

  useEffect(() => {
    if (activePage === 'coaches' && coachesTab === 'myjobs') {
      loadClubJobPostings();
    }
  }, [activePage, coachesTab]);

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobForm({
      title: job.title || '',
      description: job.description || '',
      disciplines: job.disciplines || [],
      requirements: job.requirements || '',
      location: job.location || '',
      salary_range: job.salary_range || ''
    });
    setCoachesTab('create');
  };

  const handleAcceptApplication = async (applicationId) => {
    setApplicationActionLoading(applicationId);
    try {
      const { error } = await jobPostingServiceProxy.updateApplicationStatus(applicationId, 'accepted');
      if (error) { notify.error(error); return; }
      notify.success('Postulación aceptada');
      loadJobApplications();
    } catch (err) { notify.error('Error al aceptar'); }
    setApplicationActionLoading(null);
  };

  const handleRejectApplication = async (applicationId) => {
    const ok = await confirm({
      title: 'Rechazar Postulación',
      message: '¿Rechazar esta postulación?',
      type: 'danger',
      confirmText: 'Rechazar'
    });
    if (!ok) return;
    setApplicationActionLoading(applicationId);
    try {
      const { error } = await jobPostingServiceProxy.updateApplicationStatus(applicationId, 'rejected');
      if (error) { notify.error(error); return; }
      notify.success('Postulación rechazada');
      loadJobApplications();
    } catch (err) { notify.error('Error al rechazar'); }
    setApplicationActionLoading(null);
  };

  const handleContactFromApplication = async (application) => {
    const clubId = getClubId();
    const coachId = application.coach_id;
    if (!clubId || !coachId) { notify.error('Error identificando'); return; }
    
    setContactingCoach(coachId);
    try {
      const { data, error } = await coachClubAccessServiceProxy.requestContact(clubId, coachId);
      if (error) { notify.error(error); return; }
      
      // Also update application status to 'contacted'
      await jobPostingServiceProxy.updateApplicationStatus(application.id, 'contacted');
      
      notify.success('Contacto solicitado al entrenador');
      loadJobApplications();
    } catch (err) { notify.error('Error al contactar'); }
    setContactingCoach(null);
  };

  const handleRequestContact = async (coachId) => {
    const clubId = getClubId();
    if (!clubId) { notify.error('Error identificando el club'); return; }
    setContactingCoach(coachId);
    try {
      const { data, error } = await coachClubAccessServiceProxy.requestContact(clubId, coachId);
      if (error) { notify.error(error); return; }
      if (data?.status) {
        setCoachContactStatus(prev => ({ ...prev, [coachId]: data.status }));
        if (data.status === 'pending') {
          notify.success('Solicitud de contacto enviada al entrenador');
        } else if (data.status === 'confirmed') {
          notify.info('Ya tienes contacto con este entrenador');
        }
      } else {
        setCoachContactStatus(prev => ({ ...prev, [coachId]: 'pending' }));
        notify.success('Solicitud de contacto enviada al entrenador');
      }
    } catch (err) { notify.error('Error al enviar solicitud'); }
    finally { setContactingCoach(null); }
  };

  // ============ Club Settings ============
  const handleClubLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 0.15, maxWidthOrHeight: 400, useWebWorker: true });
      setClubLogoFile(compressed);
      const reader = new FileReader();
      reader.onloadend = () => setClubLogoPreview(reader.result);
      reader.readAsDataURL(compressed);
    } catch (err) { notify.error('Error al procesar la imagen'); }
  };

  // Toggle del flag IA para un entrenador autorizado puntual.
  // Actualización optimista del estado local + persistencia en coach_club_access.
  const handleToggleCoachPoseAnalysis = async (accessId, nextValue) => {
    if (!accessId) return;
    // Optimistic update
    setApprovedCoaches(prev => prev.map(c => c.id === accessId ? { ...c, pose_analysis_enabled: nextValue } : c));
    try {
      const result = await coachClubAccessServiceProxy.setCoachPoseAnalysis(accessId, nextValue);
      if (result.error) throw new Error(result.error);
      notify.success(nextValue ? 'Análisis IA habilitado para el entrenador' : 'Análisis IA deshabilitado');
    } catch (err) {
      // Revert
      setApprovedCoaches(prev => prev.map(c => c.id === accessId ? { ...c, pose_analysis_enabled: !nextValue } : c));
      notify.error('No se pudo actualizar: ' + err.message);
    }
  };

  // Toggle del módulo de Análisis de Pose (IA) a nivel CLUB (switch maestro).
  // Se guarda inmediatamente, independiente del botón "Guardar Cambios".
  const handleTogglePoseAnalysis = async () => {
    const clubId = getClubId();
    if (!clubId) { notify.error('No se pudo identificar el club. Recarga la página.'); return; }
    const nextValue = !clubInfo?.pose_analysis_enabled;
    setTogglingPose(true);
    try {
      const result = await clubServiceProxy.update(clubId, { pose_analysis_enabled: nextValue });
      if (result.error) throw new Error(result.error);
      setClubInfo(prev => ({ ...prev, pose_analysis_enabled: nextValue }));
      notify.success(nextValue
        ? 'Análisis IA habilitado para tus entrenadores'
        : 'Análisis IA deshabilitado');
    } catch (err) {
      notify.error('No se pudo actualizar: ' + err.message);
    } finally {
      setTogglingPose(false);
    }
  };

  const handleSaveClubInfo = async () => {
    if (!clubForm.name.trim()) { notify.warning('El nombre del club es obligatorio'); return; }
    const clubId = getClubId();
    if (!clubId) { notify.error('No se pudo identificar el club. Recarga la página.'); return; }
    setSavingClub(true);
    try {
      const updateData = {
        name: clubForm.name.trim(),
        phone: clubForm.phone.trim(),
        location: clubForm.location.trim()
      };
      // Incluir disciplines si el club las tiene configuradas
      if (clubForm.disciplines) {
        updateData.disciplines = clubForm.disciplines;
      }
      // Incluir RRSS
      updateData.facebook_url = clubForm.facebook_url?.trim() || '';
      updateData.instagram_url = clubForm.instagram_url?.trim() || '';
      updateData.twitter_url = clubForm.twitter_url?.trim() || '';
      updateData.is_federated = clubForm.is_federated || false;

      const result = await clubServiceProxy.update(clubId, updateData);
      if (result.error) throw new Error(result.error);

      // Upload new logo if changed
      if (clubLogoFile) {
        const result = await storageServiceProxy.uploadClubLogo(clubId, clubLogoFile);
        if (result.success && result.data?.publicUrl) {
          const logoUrl = result.data.publicUrl;
          setClubLogoUrl(logoUrl);
          setClubLogoPreview(logoUrl);
          // Guardar referencia en tabla images
          await imageServiceProxy.setImage('club', clubId, logoUrl);
        } else {
          console.error('Error uploading logo:', result.error);
        }
        setClubLogoFile(null);
      }

      setClubInfo(prev => ({ ...prev, name: clubForm.name.trim(), phone: clubForm.phone.trim(), location: clubForm.location.trim(), disciplines: clubForm.disciplines || [], facebook_url: clubForm.facebook_url?.trim() || '', instagram_url: clubForm.instagram_url?.trim() || '', twitter_url: clubForm.twitter_url?.trim() || '' }));
      notify.success('Información del club actualizada');
    } catch (err) {
      notify.error('Error al guardar: ' + err.message);
    } finally { setSavingClub(false); }
  };

  const handleChangePassword = async () => {
    const newPwd = prompt('Ingresa la nueva contraseña (mínimo 6 caracteres):');
    if (!newPwd || newPwd.length < 6) { if (newPwd !== null) notify.warning('La contraseña debe tener al menos 6 caracteres'); return; }
    try {
      await clubAuthServiceProxy.updatePassword(getClubId(), newPwd);
      notify.success('Contraseña actualizada correctamente');
    } catch (err) { notify.error('Error al cambiar contraseña'); }
  };

  // ============ Analytics Computed Data ============
  const analyticsData = useMemo(() => {
    const validAthletes = athletes.filter(a => a.scores && Object.values(a.scores).some(s => s > 0));
    if (validAthletes.length === 0) return null;

    // Per-athlete performance
    const athletePerf = validAthletes.map(a => {
      const scores = Object.entries(a.scores || {}).filter(([, v]) => v > 0);
      const avg = scores.length > 0 ? scores.reduce((s, [, v]) => s + v, 0) / scores.length : 0;
      const total = scores.reduce((s, [, v]) => s + v, 0);
      return { id: a.id, name: a.name, avg: parseFloat(avg.toFixed(2)), total: parseFloat(total.toFixed(2)), scores: Object.fromEntries(scores), count: scores.length };
    }).sort((a, b) => b.avg - a.avg);

    // Apparatus averages across club
    const appMap = {};
    validAthletes.forEach(a => {
      Object.entries(a.scores || {}).forEach(([app, val]) => {
        if (val > 0) {
          if (!appMap[app]) appMap[app] = [];
          appMap[app].push(val);
        }
      });
    });
    const apparatusAvg = Object.entries(appMap).map(([app, vals]) => ({
      apparatus: app,
      average: parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)),
      max: parseFloat(Math.max(...vals).toFixed(2)),
      min: parseFloat(Math.min(...vals).toFixed(2)),
      count: vals.length
    }));

    // All scores flat for distribution
    const allScores = validAthletes.flatMap(a => Object.values(a.scores || {}).filter(v => v > 0));
    const globalAvg = allScores.length > 0 ? allScores.reduce((a, b) => a + b, 0) / allScores.length : 0;
    const globalMax = allScores.length > 0 ? Math.max(...allScores) : 0;

    // Distribution
    const dist = { '0-5': 0, '5-7': 0, '7-8': 0, '8-9': 0, '9-10': 0 };
    allScores.forEach(s => {
      if (s < 5) dist['0-5']++;
      else if (s < 7) dist['5-7']++;
      else if (s < 8) dist['7-8']++;
      else if (s < 9) dist['8-9']++;
      else dist['9-10']++;
    });
    const distribution = Object.entries(dist).map(([range, count]) => ({ range, count }));

    // Radar data (apparatus for top athletes)
    const apparatusNames = Object.keys(appMap);
    const radarData = apparatusNames.map(app => {
      const entry = { apparatus: app };
      athletePerf.slice(0, 5).forEach(a => {
        entry[a.name] = a.scores[app] || 0;
      });
      return entry;
    });

    // Level distribution
    const levelMap = {};
    athletes.forEach(a => {
      const lvl = a.level || 'Sin nivel';
      levelMap[lvl] = (levelMap[lvl] || 0) + 1;
    });
    const levelDist = Object.entries(levelMap).map(([name, value]) => ({ name, value }));

    // Ranking por aparato
    const apparatusRankings = apparatusNames.map(app => {
      const ranked = validAthletes
        .filter(a => (a.scores?.[app] || 0) > 0)
        .map(a => ({ id: a.id, name: a.name, level: a.level, score: parseFloat(Number(a.scores[app]).toFixed(2)) }))
        .sort((a, b) => b.score - a.score);
      return { apparatus: app, athletes: ranked };
    });

    // Comparativa por nivel
    const levelAvgMap = {};
    validAthletes.forEach(a => {
      const lvl = a.level || 'Sin nivel';
      const scores = Object.values(a.scores || {}).filter(v => v > 0);
      if (scores.length === 0) return;
      const avg = scores.reduce((s, v) => s + v, 0) / scores.length;
      if (!levelAvgMap[lvl]) levelAvgMap[lvl] = [];
      levelAvgMap[lvl].push({ id: a.id, name: a.name, avg });
    });
    const levelComparison = Object.entries(levelAvgMap).map(([level, list]) => {
      const sum = list.reduce((s, a) => s + a.avg, 0);
      const levelAvg = sum / list.length;
      const athletesList = list
        .map(a => ({ ...a, avg: parseFloat(a.avg.toFixed(2)), diff: parseFloat((a.avg - levelAvg).toFixed(2)) }))
        .sort((a, b) => b.avg - a.avg);
      return {
        level,
        athletes: athletesList,
        levelAvg: parseFloat(levelAvg.toFixed(2)),
        count: list.length,
        max: parseFloat(Math.max(...list.map(x => x.avg)).toFixed(2)),
        min: parseFloat(Math.min(...list.map(x => x.avg)).toFixed(2)),
      };
    }).sort((a, b) => b.levelAvg - a.levelAvg);

    // Alertas IA heurísticas
    const riskAlerts = [];
    athletePerf.forEach(a => {
      const sv = Object.values(a.scores || {});
      const n = sv.length;
      if (n === 0) return;
      const lowCount = sv.filter(v => v < 6).length;
      const lowRatio = lowCount / n;
      if (a.avg < 5.5) {
        riskAlerts.push({ id: a.id, name: a.name, type: 'critico', severity: 'high', message: `Promedio muy bajo (${a.avg.toFixed(2)}). Requiere plan de refuerzo urgente.` });
      } else if (lowRatio >= 0.5) {
        riskAlerts.push({ id: a.id, name: a.name, type: 'multiple_bajos', severity: 'mid', message: `${lowCount} de ${n} puntajes por debajo de 6. Revisar técnica en varios aparatos.` });
      }
      if (n < 2) {
        riskAlerts.push({ id: a.id, name: a.name, type: 'pocos_datos', severity: 'low', message: `Solo ${n} puntaje registrado. Falta historial para evaluar tendencia.` });
      }
    });
    apparatusAvg.forEach(a => {
      if (a.average < 6) {
        riskAlerts.push({ id: `app-${a.apparatus}`, name: a.apparatus, type: 'aparato_debil', severity: 'mid', message: `Promedio del club en ${a.apparatus} es ${a.average}. Considera sesiones focalizadas.` });
      }
    });

    return {
      athletePerf, apparatusAvg,
      globalAvg: parseFloat(globalAvg.toFixed(2)),
      globalMax: parseFloat(globalMax.toFixed(2)),
      totalScores: allScores.length,
      distribution, radarData, apparatusNames, levelDist,
      apparatusRankings, levelComparison, riskAlerts,
    };
  }, [athletes]);

  // Tendencia temporal del club por evento (orden cronológico)
  const temporalTrend = useMemo(() => {
    if (!temporalScores || temporalScores.length === 0) return [];
    const byEvent = {};
    temporalScores.forEach(s => {
      const evId = s.event_id;
      const evName = s.events?.name || 'Copa';
      const evDate = s.events?.date || s.created_at;
      const key = evId || evName;
      if (!byEvent[key]) byEvent[key] = { eventId: evId, name: evName, date: evDate, averages: [], totals: [] };
      if (typeof s.average === 'number' && s.average > 0) byEvent[key].averages.push(s.average);
      if (typeof s.total === 'number' && s.total > 0) byEvent[key].totals.push(s.total);
    });
    return Object.values(byEvent)
      .map(ev => ({
        name: ev.name,
        date: ev.date,
        dateLabel: ev.date ? new Date(ev.date).toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' }) : '—',
        avg: ev.averages.length > 0 ? parseFloat((ev.averages.reduce((a, b) => a + b, 0) / ev.averages.length).toFixed(2)) : 0,
        max: ev.averages.length > 0 ? parseFloat(Math.max(...ev.averages).toFixed(2)) : 0,
        athletes: ev.averages.length,
      }))
      .filter(e => e.avg > 0)
      .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
  }, [temporalScores]);

  const getInitials = (name) => (name || '').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <span className="cd-badge cd-badge-green"><CheckCircle size={12} /> Aprobado</span>;
      case 'pending': return <span className="cd-badge cd-badge-yellow"><Clock size={12} /> Pendiente</span>;
      case 'rejected': return <span className="cd-badge cd-badge-red"><XCircle size={12} /> Rechazado</span>;
      default: return <span className="cd-badge">{status}</span>;
    }
  };

  // ============ Attendance Handlers ============
  const loadAttendanceHistory = useCallback(async () => {
    const clubId = profile?.id;
    if (!clubId) return;
    const weekStart = getWeekStart(attendanceDate);
    const weekEnd = getWeekEnd(attendanceDate);
    const result = await attendanceServiceProxy.getAthleteAttendance(clubId, weekStart, weekEnd);
    if (!result.error) setAttendanceHistory(result.data);
  }, [attendanceDate, profile]);

  const formatLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const getWeekStart = (dateStr) => {
    const d = new Date(dateStr + 'T12:00'); d.setDate(d.getDate() - d.getDay() + 1);
    return formatLocalDate(d);
  };
  const getWeekEnd = (dateStr) => {
    const d = new Date(dateStr + 'T12:00'); d.setDate(d.getDate() - d.getDay() + 7);
    return formatLocalDate(d);
  };

  const toggleAttendance = (entityId, status) => {
    setAttendanceRecords(prev => ({ ...prev, [entityId]: status === prev[entityId] ? 'absent' : status }));
  };

  const handleSaveAttendance = async () => {
    setSavingAttendance(true);
    const clubId = getClubId();
    try {
      const records = Object.entries(attendanceRecords).map(([id, status]) => ({
        gymnast_id: id,
        club_id: clubId,
        date: attendanceDate,
        status
      }));
      if (records.length === 0) { notify.warning('No hay registros para guardar'); setSavingAttendance(false); return; }
      const result = await attendanceServiceProxy.markAthleteAttendance(records);
      if (result.error) throw new Error(result.error);
      notify.success('Asistencia guardada');
      loadAttendanceHistory();
    } catch (err) {
      notify.error('Error guardando asistencia: ' + err.message);
    } finally {
      setSavingAttendance(false);
    }
  };

  const handleJustifyAbsence = async () => {
    if (!justifyModal) return;
    try {
      const result = await attendanceServiceProxy.justifyAbsence(justifyModal.id, justification, rescheduledDate || null);
      if (result.error) throw new Error(result.error);
      notify.success(rescheduledDate ? 'Ausencia reagendada' : 'Ausencia justificada');
      setJustifyModal(null); setJustification(''); setRescheduledDate('');
      loadAttendanceHistory();
    } catch (err) {
      notify.error('Error: ' + err.message);
    }
  };

  // ============ Schedule Handlers ============
  const loadScheduleBlocks = useCallback(async () => {
    const clubId = getClubId();
    if (!clubId) return;
    const result = await scheduleBlockServiceProxy.getByClub(clubId);
    if (!result.error) {
      setScheduleBlocks(result.data || []);
    }
  }, [profile]);

  const handleSaveBlock = async () => {
    const clubId = getClubId();
    try {
      const blockData = {
        club_id: clubId,
        block_type: 'athlete',
        name: blockForm.title.trim(),
        title: blockForm.title.trim(),
        duration_minutes: parseInt(blockForm.duration_minutes) || 60,
        color: blockForm.color,
        days_of_week: Array.isArray(blockForm.days_of_week) && blockForm.days_of_week.length > 0
          ? blockForm.days_of_week.map(Number).sort((a, b) => a - b)
          : [1],
        // Mantenemos day_of_week por compat. = primer día seleccionado
        day_of_week: Array.isArray(blockForm.days_of_week) && blockForm.days_of_week.length > 0
          ? Number(blockForm.days_of_week[0])
          : 1,
        start_time: blockForm.start_time,
        end_time: blockForm.end_time
      };
      if (!blockData.title) { notify.warning('El nombre del bloque es obligatorio'); return; }
      const result = editingBlock
        ? await scheduleBlockServiceProxy.update(editingBlock.id, blockData)
        : await scheduleBlockServiceProxy.create(blockData);
      if (result.error) throw new Error(result.error);
      notify.success(editingBlock ? 'Bloque actualizado' : 'Bloque creado');
      resetBlockForm();
      loadScheduleBlocks();
    } catch (err) {
      notify.error('Error: ' + err.message);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    const ok = await confirm({ title: 'Eliminar Bloque', message: '¿Seguro que deseas eliminar este bloque?', type: 'danger', confirmText: 'Eliminar' });
    if (!ok) return;
    const result = await scheduleBlockServiceProxy.delete(blockId);
    if (result.error) { notify.error('Error eliminando bloque'); return; }
    notify.success('Bloque eliminado');
    loadScheduleBlocks();
  };

  const handleEditBlock = (block) => {
    setEditingBlock(block);
    setBlockForm({
      title: block.title,
      duration_minutes: block.duration_minutes,
      color: block.color,
      days_of_week: getBlockDays(block),
      start_time: block.start_time?.slice(0, 5),
      end_time: block.end_time?.slice(0, 5)
    });
    setShowBlockForm(true);
  };

  const resetBlockForm = () => {
    setBlockForm({ title: '', duration_minutes: 60, color: '#8b5cf6', days_of_week: [1], start_time: '08:00', end_time: '09:00' });
    setEditingBlock(null);
    setShowBlockForm(false);
  };

  // ============ Block Athletes Modal ============
  const openBlockAthletesModal = async (block) => {
    setBlockAthletesModal(block);
    setBlockAthleteSearch('');
    setBlockCoachSearch('');
    setBlockAthleteLoading(true);
    try {
      const [athleteResult, coachResult] = await Promise.all([
        scheduleBlockServiceProxy.getBlockAthletes(block.id),
        scheduleBlockServiceProxy.getBlockCoaches(block.id)
      ]);
      setBlockAthletes(athleteResult.data?.map(a => a.gimnasts).filter(Boolean).map(a => ({ ...a, name: formatPersonName(a.name) })) || []);
      setBlockCoaches(coachResult.data?.map(c => c.coaches).filter(Boolean).map(c => ({ ...c, name: formatPersonName(c.name) })) || []);
    } catch (e) { console.error(e); }
    setBlockAthleteLoading(false);
  };

  const handleAddAthleteToBlock = async (athlete) => {
    if (!blockAthletesModal) return;

    // Validar límite de días por semana según el plan activo
    const planInfo = athletePlanMap[athlete.id]?.plan;
    const maxDays = planInfo?.days_per_week;
    if (maxDays && maxDays > 0) {
      try {
        const { data: existingBlocks } = await scheduleBlockServiceProxy.getAthleteBlocks(athlete.id);
        const daysUsed = new Set();
        for (const b of (existingBlocks || [])) {
          (b.days_of_week || []).forEach(d => daysUsed.add(d));
        }
        // Añadir los días del bloque al que se pretende asignar
        (blockAthletesModal.days_of_week || []).forEach(d => daysUsed.add(d));
        if (daysUsed.size > maxDays) {
          notify.error(
            `${athlete.name} tiene un plan de ${maxDays} día${maxDays === 1 ? '' : 's'}/semana. ` +
            `Con este bloque quedaría asignada a ${daysUsed.size} días. Remueve otro bloque o cambia el plan.`
          );
          return;
        }
      } catch (e) {
        console.warn('No se pudo validar días del plan:', e);
      }
    } else if (!planInfo) {
      // Sin plan asignado: advertir pero permitir
      const ok = await confirm({
        title: 'Atleta sin plan',
        message: `${athlete.name} no tiene un plan asignado. ¿Deseas agregarla al bloque de todos modos?`,
        confirmText: 'Agregar',
      });
      if (!ok) return;
    }

    const result = await scheduleBlockServiceProxy.addAthleteToBlock(athlete.id, blockAthletesModal.id, getClubId());
    if (!result.error) {
      setBlockAthletes(prev => [...prev, athlete]);
      notify.success(`${athlete.name} agregada al bloque`);
    } else { notify.error('Error agregando atleta'); }
  };

  const handleRemoveAthleteFromBlock = async (athleteId) => {
    if (!blockAthletesModal) return;
    const result = await scheduleBlockServiceProxy.removeAthleteFromBlock(athleteId, blockAthletesModal.id);
    if (!result.error) {
      setBlockAthletes(prev => prev.filter(a => a.id !== athleteId));
      notify.success('Atleta removida del bloque');
    } else { notify.error('Error removiendo atleta'); }
  };

  const availableAthletesForBlock = useMemo(() => {
    const assignedIds = new Set(blockAthletes.map(a => a.id));
    let result = athletes.filter(a => !assignedIds.has(a.id));
    if (blockAthleteSearch.trim()) {
      result = result.filter(a => a.name.toLowerCase().includes(blockAthleteSearch.toLowerCase()));
    }
    return result;
  }, [athletes, blockAthletes, blockAthleteSearch]);

  const handleAddCoachToBlock = async (coach) => {
    if (!blockAthletesModal) return;
    const result = await scheduleBlockServiceProxy.addCoachToBlock(coach.id, blockAthletesModal.id, getClubId());
    if (!result.error) {
      setBlockCoaches(prev => [...prev, coach]);
      notify.success(`${coach.name} agregado/a al bloque`);
    } else { notify.error('Error agregando entrenador'); }
  };

  const handleRemoveCoachFromBlock = async (coachId) => {
    if (!blockAthletesModal) return;
    const result = await scheduleBlockServiceProxy.removeCoachFromBlock(coachId, blockAthletesModal.id);
    if (!result.error) {
      setBlockCoaches(prev => prev.filter(c => c.id !== coachId));
      notify.success('Entrenador/a removido/a del bloque');
    } else { notify.error('Error removiendo entrenador'); }
  };

  const availableCoachesForBlock = useMemo(() => {
    const assignedIds = new Set(blockCoaches.map(c => c.id));
    // approvedCoaches son filas de coach_club_access; el entrenador real está en item.coaches
    const flatCoaches = approvedCoaches
      .map(item => item.coaches || item)
      .filter(c => c && c.id);
    let result = flatCoaches.filter(c => !assignedIds.has(c.id));
    if (blockCoachSearch.trim()) {
      const q = blockCoachSearch.toLowerCase();
      result = result.filter(c => (c.name || '').toLowerCase().includes(q));
    }
    return result;
  }, [approvedCoaches, blockCoaches, blockCoachSearch]);

  // ============ Attendance by Blocks ============
  const loadAllBlocks = useCallback(async () => {
    const clubId = getClubId();
    if (!clubId) return;
    const result = await scheduleBlockServiceProxy.getByClub(clubId);
    if (!result.error) setAllBlocks(result.data || []);
  }, [profile]);

  const loadBlockAttendance = useCallback(async () => {
    const clubId = getClubId();
    if (!clubId) return;
    const dayOfWeek = new Date(attendanceDate + 'T12:00').getDay();
    const dayBlocks = allBlocks.filter(b => blockAppliesTo(b, dayOfWeek));

    const map = {};
    for (const block of dayBlocks) {
      const athleteResult = await scheduleBlockServiceProxy.getBlockAthletes(block.id);
      const blockAthletesList = athleteResult.data?.map(a => a.gimnasts).filter(Boolean).map(a => ({ ...a, name: formatPersonName(a.name) })) || [];
      map[block.id] = { athletes: blockAthletesList, records: {} };
    }

    // Load existing attendance for this date
    const attResult = await attendanceServiceProxy.getAthleteAttendance(clubId, attendanceDate, attendanceDate);
    const preJustified = {};
    if (attResult.data) {
      attResult.data.forEach(rec => {
        const blockId = rec.schedule_block_id;
        if (blockId && map[blockId]) {
          map[blockId].records[rec.gymnast_id] = rec.status;
          if (rec.status === 'justified') {
            preJustified[`${blockId}_${rec.gymnast_id}`] = true;
          }
        }
      });
    }

    setBlockAttendanceMap(map);
    setAttendanceRecords({});
    setJustifiedRecords(preJustified);
    // Auto-open first accordion
    const acc = {};
    dayBlocks.forEach((b, i) => { acc[b.id] = i === 0; });
    setAttendanceAccordion(acc);
  }, [allBlocks, attendanceDate, profile]);

  useEffect(() => { loadAllBlocks(); }, [loadAllBlocks]);
  useEffect(() => { if (activePage === 'attendance' && allBlocks.length > 0) loadBlockAttendance(); }, [activePage, loadBlockAttendance, allBlocks]);

  // ============ Finanzas ============
  const loadFinanzasData = useCallback(async () => {
    const clubId = getClubId();
    if (!clubId) return;
    setFinanciaLoading(true);
    try {
      const [planesRes, membersRes, pagosRes, alertasRes] = await Promise.all([
        clubPlanServiceProxy.getByClub(clubId),
        clubMembershipServiceProxy.getByClub(clubId),
        clubPagosServiceProxy.getByClub(clubId),
        clubPagosServiceProxy.getAlertasDeuda(clubId),
      ]);
      if (!planesRes.error) {
        setClubPlanes(planesRes.data || []);
        setClubPlans(planesRes.data || []); // sincronizar combo de atleta
      }
      if (!membersRes.error) {
        const formattedMembers = (membersRes.data || []).map(m => ({
          ...m,
          gimnasts: m.gimnasts ? { ...m.gimnasts, name: formatPersonName(m.gimnasts.name) } : m.gimnasts,
        }));
        setClubMemberships(formattedMembers);
        // Refrescar athletePlanMap con memberships activas actualizadas
        const planMap = {};
        for (const m of formattedMembers) {
          if (m.status === 'active' && m.gymnast_id) {
            planMap[m.gymnast_id] = { plan_id: m.plan_id, plan: m.club_planes || null, membership_id: m.id, gymnast: m.gimnasts };
          }
        }
        setAthletePlanMap(planMap);
      }
      if (!pagosRes.error) setClubPagos((pagosRes.data || []).map(p => ({
        ...p,
        gimnasts: p.gimnasts ? { ...p.gimnasts, name: formatPersonName(p.gimnasts.name) } : p.gimnasts,
      })));
      if (!alertasRes.error) setAlertasDeuda((alertasRes.data || []).map(a => ({
        ...a,
        gymnast: a.gymnast ? { ...a.gymnast, name: formatPersonName(a.gymnast.name) } : a.gymnast,
      })));
    } catch (err) {
      console.error('[Finanzas] Error cargando datos:', err);
    }
    setFinanciaLoading(false);
  }, [profile]);

  useEffect(() => { if (activePage === 'finanzas' || activePage === 'athletes') loadFinanzasData(); }, [activePage, loadFinanzasData]);

  // Al entrar a pestaña "Pagos": mostrar por defecto los pagos del día
  useEffect(() => {
    if (activePage === 'finanzas' && finanzasActiveTab === 'pagos') {
      const today = getTodayISO();
      setPagosFilterFrom(today);
      setPagosFilterTo(today);
      setPagosFilterAthlete('');
      setPagosFilterMethod('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, finanzasActiveTab]);

  // Cargar scores históricos cuando se abre Analítica IA
  useEffect(() => {
    if (activePage !== 'analytics') return;
    const loadTemporal = async () => {
      const athleteIds = athletes.map(a => a.id);
      if (athleteIds.length === 0) return;
      setTemporalLoading(true);
      try {
        const { data, error } = await supabase
          .from('scores')
          .select('id, event_id, gymnast_id, scores, total, average, created_at, events:event_id(id, name, date)')
          .in('gymnast_id', athleteIds)
          .order('created_at', { ascending: true });
        if (!error) setTemporalScores(data || []);
      } catch (e) {
        console.error('[analytics] Error cargando scores históricos:', e);
      }
      setTemporalLoading(false);
    };
    loadTemporal();
  }, [activePage, athletes]);

  const toggleBlockAttendance = (blockId, athleteId, status) => {
    const key = `${blockId}_${athleteId}`;
    setAttendanceRecords(prev => {
      return { ...prev, [key]: status === prev[key] ? undefined : status };
    });
    // Clear justified when switching to present or toggling off
    if (status === 'present') {
      setJustifiedRecords(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const toggleJustified = (blockId, athleteId) => {
    const key = `${blockId}_${athleteId}`;
    setJustifiedRecords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveBlockAttendance = async () => {
    setSavingAttendance(true);
    const clubId = getClubId();
    try {
      const records = [];
      Object.entries(attendanceRecords).forEach(([key, status]) => {
        if (!status) return;
        const [blockId, athleteId] = key.split('_');
        const finalStatus = (status === 'absent' && justifiedRecords[key]) ? 'justified' : status;
        records.push({
          gymnast_id: athleteId,
          schedule_block_id: blockId,
          club_id: clubId,
          date: attendanceDate,
          status: finalStatus
        });
      });
      if (records.length === 0) { notify.warning('No hay registros para guardar'); setSavingAttendance(false); return; }
      const result = await attendanceServiceProxy.markAthleteAttendance(records);
      if (result.error) throw new Error(result.error);
      notify.success('Asistencia guardada');
      loadBlockAttendance();
      loadAttendanceHistory();
    } catch (err) {
      notify.error('Error guardando asistencia: ' + err.message);
    } finally {
      setSavingAttendance(false);
    }
  };

  // ============ Reports ============
  const handlePrintWeeklyCalendar = async () => {
    const result = await scheduleBlockServiceProxy.getByClub(getClubId());
    const blocks = result.data || [];
    // Enrich with athletes
    for (const block of blocks) {
      const ar = await scheduleBlockServiceProxy.getBlockAthletes(block.id);
      block._athletes = ar.data?.map(a => a.gimnasts).filter(Boolean).map(a => ({ ...a, name: formatPersonName(a.name) })) || [];
    }
    generateWeeklyCalendarPDF(clubInfo?.name || 'Club', blocks, clubLogoUrl);
  };

  const handlePrintDailyAttendance = async (selectedBlockIds) => {
    // Guard: ignore if a mouse event was accidentally passed
    const blockIds = Array.isArray(selectedBlockIds) ? selectedBlockIds
      : (selectedBlockIds && typeof selectedBlockIds === 'object' && 'nativeEvent' in selectedBlockIds) ? null
      : null;
    const dayOfWeek = new Date(attendanceDate + 'T12:00').getDay();
    const dayBlocks = allBlocks.filter(b => blockAppliesTo(b, dayOfWeek)).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

    // If multiple blocks and no selection yet, show modal
    if (dayBlocks.length > 1 && !blockIds) {
      setShowBlockSelectModal(dayBlocks);
      return;
    }

    const clubId = getClubId();
    const blocksToProcess = blockIds
      ? dayBlocks.filter(b => blockIds.includes(b.id))
      : dayBlocks;

    // Load all attendance for the day once
    const attResult = await attendanceServiceProxy.getAthleteAttendance(clubId, attendanceDate, attendanceDate);
    const allAttRecords = attResult.data || [];

    const blocksWithAtt = [];
    for (const block of blocksToProcess) {
      const ar = await scheduleBlockServiceProxy.getBlockAthletes(block.id);
      const blockAthletesList = ar.data?.map(a => a.gimnasts).filter(Boolean).map(a => ({ ...a, name: formatPersonName(a.name) })) || [];

      // Match attendance by gymnast_id (try schedule_block_id first, fallback to just gymnast_id)
      const enriched = blockAthletesList.map(a => {
        const exactMatch = allAttRecords.find(r => r.gymnast_id === a.id && r.schedule_block_id === block.id);
        const fallbackMatch = allAttRecords.find(r => r.gymnast_id === a.id);
        const rec = exactMatch || fallbackMatch;
        return {
          ...a,
          _status: rec?.status || 'absent',
          _justification: rec?.justification || ''
        };
      });
      blocksWithAtt.push({ ...block, _attendanceAthletes: enriched });
    }
    generateDailyAttendancePDF(clubInfo?.name || 'Club', attendanceDate, blocksWithAtt, clubLogoUrl);
  };

  const handlePrintWeeklyAttendance = async () => {
    const clubId = getClubId();
    const weekStart = getWeekStart(attendanceDate);
    const weekEnd = getWeekEnd(attendanceDate);
    const result = await attendanceServiceProxy.getAthleteAttendance(clubId, weekStart, weekEnd);
    generateWeeklyAttendancePDF(clubInfo?.name || 'Club', weekStart, weekEnd, result.data || [], clubLogoUrl);
  };

  const handlePrintMonthlyAverages = async () => {
    const clubId = getClubId();
    const result = await attendanceServiceProxy.getAthleteAttendanceByMonth(clubId, reportYear, reportMonth);
    generateMonthlyAveragesPDF(clubInfo?.name || 'Club', reportYear, reportMonth, result.data || [], clubLogoUrl);
  };

  // ============ Coach Attendance Handlers ============
  const loadCoachAttendanceHistory = useCallback(async () => {
    const clubId = profile?.id;
    if (!clubId) return;
    const weekStart = getWeekStart(attendanceDate);
    const weekEnd = getWeekEnd(attendanceDate);
    const result = await attendanceServiceProxy.getCoachAttendance(clubId, weekStart, weekEnd);
    if (!result.error) setCoachAttendanceHistory(result.data);
    // Pre-fill today's records — only coaches with actual DB records
    const todayRecs = {};
    (result.data || []).filter(r => r.date === attendanceDate).forEach(r => {
      todayRecs[r.coach_id] = r.status;
    });
    setCoachAttendanceRecords(todayRecs);
  }, [attendanceDate, profile]);

  useEffect(() => {
    if (activePage === 'attendance') loadCoachAttendanceHistory();
  }, [activePage, loadCoachAttendanceHistory]);

  const toggleCoachAttendance = (coachId, status) => {
    setCoachAttendanceRecords(prev => ({
      ...prev, [coachId]: status === prev[coachId] ? undefined : status
    }));
  };

  const handleSaveCoachAttendance = async () => {
    // Require club password for manual coach attendance
    const records = [];
    Object.entries(coachAttendanceRecords).forEach(([coachId, status]) => {
      if (!status) return;
      records.push(coachId);
    });
    if (records.length === 0) { notify.warning('No hay registros para guardar'); return; }
    setCoachAttPassword('');
    setCoachAttPasswordError('');
    setCoachAttPasswordModal(true);
  };

  // Password re-authentication handled by Supabase Auth in confirmSaveCoachAttendance
  
  const confirmSaveCoachAttendance = async () => {
    if (!coachAttPassword.trim()) { setCoachAttPasswordError('Ingresa la contraseña'); return; }
    // Validate club password via Supabase Auth re-authentication
    try {
      const email = profile?.email || clubInfo?.email;
      if (!email) { setCoachAttPasswordError('Error obteniendo email de sesión'); return; }
      const { error: authErr } = await supabase.auth.signInWithPassword({
        email,
        password: coachAttPassword
      });
      if (authErr) { setCoachAttPasswordError('Contraseña incorrecta'); return; }
    } catch (err) {
      setCoachAttPasswordError('Error validando: ' + err.message);
      return;
    }

    // Password validated — save attendance
    setSavingAttendance(true);
    setCoachAttPasswordModal(false);
    const clubId = getClubId();
    try {
      const records = [];
      Object.entries(coachAttendanceRecords).forEach(([coachId, status]) => {
        if (!status) return;
        const rec = { coach_id: coachId, club_id: clubId, date: attendanceDate, status };
        if (status === 'present') rec.check_in_time = new Date().toISOString();
        records.push(rec);
      });
      const saveResult = await attendanceServiceProxy.markCoachAttendance(records);
      if (saveResult.error) throw new Error(saveResult.error);
      notify.success('Asistencia de entrenadores guardada');
      loadCoachAttendanceHistory();
    } catch (err) {
      notify.error('Error: ' + err.message);
    } finally {
      setSavingAttendance(false);
    }
  };

  const generateQrToken = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 24; i++) token += chars.charAt(Math.floor(Math.random() * chars.length));
    return token;
  };

  const getGeneralQRUrl = () => {
    if (!coachQrToken) return '';
    const base = window.location.origin;
    return `${base}/asistencia/${getClubId()}/${coachQrToken}`;
  };

  const handleGenerateNewQrToken = async () => {
    const ok = await confirm({
      title: 'Generar Nuevo QR',
      message: '¿Generar un nuevo código QR de asistencia? El QR anterior dejará de funcionar.',
      type: 'warning',
      confirmText: 'Generar Nuevo'
    });
    if (!ok) return;
    const newToken = generateQrToken();
    const clubId = getClubId();
    const result = await clubServiceProxy.update(clubId, { coach_qr_token: newToken, coach_qr_token_created_at: new Date().toISOString() });
    if (result.error) { notify.error('Error generando QR: ' + result.error); return; }
    setCoachQrToken(newToken);
    notify.success('¡Nuevo QR generado exitosamente!');
  };

  const handlePrintCoachQR = () => {
    if (!coachQrToken) return;
    const qrUrl = getGeneralQRUrl();
    generateCoachQRPDF(clubInfo?.name || 'Club', qrUrl, clubLogoUrl);
  };

  // ===== Reportes financieros =====
  const handlePrintPagosDia = async () => {
    const clubId = getClubId();
    if (!clubId) return;
    const res = await clubPagosServiceProxy.getByClub(clubId);
    const pagos = (res.data || []).filter(p => (p.payment_date || '').slice(0, 10) === attendanceDate);
    generatePagosDiaPDF(clubInfo?.name || 'Club', attendanceDate, pagos, clubLogoUrl);
  };

  const handlePrintMensualidades = async () => {
    const clubId = getClubId();
    if (!clubId) return;
    const res = await clubPagosServiceProxy.getByClub(clubId);
    const pagos = (res.data || []).filter(p => {
      if (!p.payment_date) return false;
      const d = new Date(p.payment_date);
      return d.getFullYear() === reportYear && (d.getMonth() + 1) === reportMonth;
    });
    generateMensualidadesPDF(clubInfo?.name || 'Club', reportYear, reportMonth, pagos, clubLogoUrl);
  };

  const handlePrintMembresias = async () => {
    const clubId = getClubId();
    if (!clubId) return;
    const res = await clubMembershipServiceProxy.getByClub(clubId);
    generateMembresiasPDF(clubInfo?.name || 'Club', res.data || [], clubLogoUrl);
  };

  const handlePrintDeudores = async () => {
    const clubId = getClubId();
    if (!clubId) return;
    const res = await clubPagosServiceProxy.getAlertasDeuda(clubId);
    generateDeudoresPDF(clubInfo?.name || 'Club', res.data || [], clubLogoUrl);
  };

  // Coach attendance PDFs
  const handlePrintCoachDailyAttendance = async () => {
    const clubId = getClubId();
    const result = await attendanceServiceProxy.getCoachAttendance(clubId, attendanceDate, attendanceDate);
    generateCoachDailyAttendancePDF(clubInfo?.name || 'Club', attendanceDate, result.data || [], approvedCoaches, clubLogoUrl);
  };

  const handlePrintCoachWeeklyAttendance = async () => {
    const clubId = getClubId();
    const weekStart = getWeekStart(attendanceDate);
    const weekEnd = getWeekEnd(attendanceDate);
    const result = await attendanceServiceProxy.getCoachAttendance(clubId, weekStart, weekEnd);
    generateCoachWeeklyAttendancePDF(clubInfo?.name || 'Club', weekStart, weekEnd, result.data || [], clubLogoUrl);
  };

  const handlePrintCoachMonthlyAttendance = async () => {
    const clubId = getClubId();
    const result = await attendanceServiceProxy.getCoachAttendanceByMonth(clubId, reportYear, reportMonth);
    generateCoachMonthlyAttendancePDF(clubInfo?.name || 'Club', reportYear, reportMonth, result.data || [], clubLogoUrl);
  };
  useEffect(() => { if (activePage === 'schedule') loadScheduleBlocks(); }, [activePage, loadScheduleBlocks]);

  const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const DAY_NAMES_FULL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const HOURS = Array.from({ length: 15 }, (_, i) => `${String(i + 6).padStart(2, '0')}:00`);

  // Nav items (Mi Club eliminado de aquí - ahora va en dropdown del perfil)
  const navItems = [
    { id: 'overview', label: 'Resumen', icon: Home, color: '#3b82f6' },
    { id: 'events', label: 'Copas', icon: Calendar, color: '#10b981' },
    { id: 'athletes', label: 'Atletas', icon: Users, color: '#ec4899' },
    { id: 'attendance', label: 'Asistencia', icon: ClipboardCheck, color: '#14b8a6' },
    { id: 'schedule', label: 'Calendario', icon: CalendarDays, color: '#6366f1' },
    { id: 'reports', label: 'Descargables', icon: FileBarChart, color: '#0ea5e9' },
    { id: 'coaches', label: 'Entrenadores', icon: Dumbbell, color: '#db2777' },
    { id: 'finanzas', label: 'Finanzas', icon: Wallet, color: '#059669' },
    { id: 'analytics', label: 'Analítica IA', icon: BarChart3, color: '#f59e0b' },
  ];

  // ============ Render ============
  if (loading) {
    return (
      <div className="cd-loading">
        <div className="cd-spinner"></div>
        <p>Cargando panel del club...</p>
      </div>
    );
  }

  if (!clubInfo) {
    // Durante los primeros 10s mostramos loading — evita el flash feo de
    // "No se pudo cargar..." cuando la carga simplemente está en curso.
    if (!clubInfoTimeout) {
      return (
        <div className="cd-loading">
          <div className="cd-spinner"></div>
          <p>Cargando panel del club...</p>
        </div>
      );
    }
    return (
      <div className="cd-loading">
        <p>No se pudo cargar la información del club</p>
        <button onClick={() => window.location.reload()} className="btn btn-club-primary" style={{ marginTop: 16 }}>Recargar</button>
      </div>
    );
  }

  const currentNavItem = navItems.find(n => n.id === activePage);
  const pageTitle = currentNavItem?.label || 'Panel';

  return (
    <div className={`cd-layout ${sidebarCollapsed ? 'cd-sidebar-is-collapsed' : ''} ${darkMode ? 'cd-dark' : ''}`}>
      {/* Overlay móvil */}
      {sidebarOpen && <div className="cd-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* ========== SIDEBAR ========== */}
      <aside className={`cd-sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="cd-sidebar-logo" onClick={() => navigate('/')}>
          <img src="/letracopagimnastaT.png" alt="CopaGimnasta" />
        </div>
        <nav className="cd-sidebar-nav">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activePage === item.id;
            const badge = item.id === 'athletes' ? athletes.length : item.id === 'events' ? (allRequests.filter(r => r.status === 'pending').length || approvedEvents.length || null) : item.id === 'coaches' ? coachRequests.filter(r => r.status === 'pending').length || approvedCoaches.length || null : null;
            return (
              <button
                key={item.id}
                className={`cd-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => { setActivePage(item.id); setSidebarOpen(false); }}
                style={{ '--item-color': item.color }}
              >
                <span className="cd-nav-icon"><Icon size={20} /></span>
                <span className="cd-nav-label">{item.label}</span>
                {badge > 0 && <span className="cd-nav-badge">{badge}</span>}
              </button>
            );
          })}
        </nav>

        <div className="cd-sidebar-footer">
          {/* RRSS del club - siempre 3 botones */}
          <div className="cd-social-links">
            {clubInfo?.facebook_url ? (
              <a href={clubInfo.facebook_url} target="_blank" rel="noopener noreferrer" className="cd-social-icon cd-social-facebook" title="Facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.437H7.078v-3.51h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.51h-2.796v8.437C19.613 23.028 24 18.062 24 12.073z"/></svg>
              </a>
            ) : (
              <span className="cd-social-icon cd-social-facebook cd-social-disabled" title="Facebook no registrado">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.437H7.078v-3.51h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.51h-2.796v8.437C19.613 23.028 24 18.062 24 12.073z"/></svg>
              </span>
            )}
            {clubInfo?.instagram_url ? (
              <a href={clubInfo.instagram_url} target="_blank" rel="noopener noreferrer" className="cd-social-icon cd-social-instagram" title="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.758-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
            ) : (
              <span className="cd-social-icon cd-social-instagram cd-social-disabled" title="Instagram no registrado">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.758-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </span>
            )}
            {clubInfo?.twitter_url ? (
              <a href={clubInfo.twitter_url} target="_blank" rel="noopener noreferrer" className="cd-social-icon cd-social-twitter" title="Twitter / X">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            ) : (
              <span className="cd-social-icon cd-social-twitter cd-social-disabled" title="Twitter/X no registrado">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </span>
            )}
          </div>

          <button className="cd-sidebar-logout" onClick={handleLogout}>
            <LogOut size={18} /><span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="cd-main">
        <ClubTopbar
          pageTitle={pageTitle}
          onToggleSidebar={handleToggleSidebar}
          clubName={clubInfo?.name || ''}
          clubLogoUrl={clubLogoUrl}
          initials={getInitials(clubInfo?.name)}
          darkMode={darkMode}
          onToggleDarkMode={() => setDarkMode(v => !v)}
          onGoProfile={() => { setActivePage('settings'); setSidebarOpen(false); }}
          onGoSettings={() => { setActivePage('settings'); setSidebarOpen(false); }}
          onLogout={handleLogout}
          notificationsCount={allRequests.filter(r => r.status === 'pending').length + coachRequests.filter(r => r.status === 'pending').length}
        />
        <div className="cd-page">

          {/* ============ OVERVIEW ============ */}
          {activePage === 'overview' && (
            <>
              <p className="cd-page-subtitle"><Home size={16} />¡Bienvenido, <strong>{clubInfo.name}</strong>! Panel general con accesos rápidos a todas las secciones del club.</p>

              {/* KPI Cards */}
              <div className="cd-kpi-grid">
                <div className="cd-kpi-card cd-kpi-green">
                  <div className="cd-kpi-icon"><Users size={22} /></div>
                  <div className="cd-kpi-body">
                    <span className="cd-kpi-value">{athletes.length}</span>
                    <span className="cd-kpi-label">Atletas</span>
                  </div>
                </div>
                <div className="cd-kpi-card cd-kpi-blue">
                  <div className="cd-kpi-icon"><Calendar size={22} /></div>
                  <div className="cd-kpi-body">
                    <span className="cd-kpi-value">{approvedEvents.length}</span>
                    <span className="cd-kpi-label">Copas Activas</span>
                  </div>
                </div>
                <div className="cd-kpi-card cd-kpi-orange">
                  <div className="cd-kpi-icon"><Target size={22} /></div>
                  <div className="cd-kpi-body">
                    <span className="cd-kpi-value">{analyticsData?.globalAvg || '—'}</span>
                    <span className="cd-kpi-label">Promedio General</span>
                  </div>
                </div>
                <div className="cd-kpi-card cd-kpi-purple">
                  <div className="cd-kpi-icon"><Award size={22} /></div>
                  <div className="cd-kpi-body">
                    <span className="cd-kpi-value">{analyticsData?.globalMax || '—'}</span>
                    <span className="cd-kpi-label">Puntaje Máximo</span>
                  </div>
                </div>
              </div>

              {/* Quick glance grid */}
              <div className="cd-overview-grid">
                {/* Top Athletes */}
                <div className="cd-card">
                  <div className="cd-card-header"><Medal size={16} /> Top Atletas</div>
                  {analyticsData?.athletePerf?.slice(0, 5).map((a, i) => (
                    <div key={a.id} className="cd-rank-row">
                      <span className="cd-rank-num">#{i + 1}</span>
                      <span className="cd-rank-name">{a.name}</span>
                      <span className="cd-rank-score">{a.avg}</span>
                    </div>
                  )) || <p className="cd-empty-text">Sin datos de rendimiento aún</p>}
                </div>

                {/* Upcoming Events */}
                <div className="cd-card">
                  <div className="cd-card-header"><Calendar size={16} /> Próximas Copas</div>
                  {approvedEvents.length > 0 ? approvedEvents.slice(0, 4).map(ev => {
                    const disc = getDisciplineDisplay(ev.discipline, ev.category);
                    return (
                      <div key={ev.id} className="cd-upcoming-event">
                        <div className="cd-upcoming-img">
                          {eventImages[ev.id] ? (
                            <img src={eventImages[ev.id]} alt={ev.name} />
                          ) : (
                            <div className="cd-upcoming-img-placeholder"><Calendar size={20} /></div>
                          )}
                        </div>
                        <div className="cd-upcoming-info">
                          <strong>{ev.name}</strong>
                          <span className="cd-upcoming-date">
                            <Calendar size={12} /> {new Date(ev.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          {ev.location && (
                            <span className="cd-upcoming-location">
                              <MapPin size={12} /> {ev.location}
                            </span>
                          )}
                          <div className="cd-upcoming-disciplines">
                            {disc.logo && <img src={disc.logo} alt={disc.fullName} title={disc.fullName} />}
                            <span>{disc.fullName}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }) : <p className="cd-empty-text">Sin copas aún. Solicita acceso con un código.</p>}
                </div>

                {/* Accesos Rápidos */}
                <div className="cd-card cd-card-wide">
                  <div className="cd-card-header"><Target size={16} /> Accesos Rápidos</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginTop: 8 }}>
                    {[
                      { label: 'Atletas', icon: Users, color: '#ec4899', bg: '#fdf2f8', page: 'athletes' },
                      { label: 'Asistencia', icon: ClipboardCheck, color: '#14b8a6', bg: '#f0fdfa', page: 'attendance' },
                      { label: 'Agenda', icon: CalendarDays, color: '#6366f1', bg: '#eef2ff', page: 'schedule' },
                      { label: 'Finanzas', icon: Wallet, color: '#059669', bg: '#ecfdf5', page: 'finanzas' },
                      { label: 'Copas', icon: Calendar, color: '#2563eb', bg: '#eff6ff', page: 'events' },
                      { label: 'Entrenadores', icon: Dumbbell, color: '#db2777', bg: '#fdf2f8', page: 'coaches' },
                      { label: 'Descargables', icon: FileBarChart, color: '#0ea5e9', bg: '#eff6ff', page: 'reports' },
                      { label: 'Analítica IA', icon: BarChart3, color: '#f59e0b', bg: '#fffbeb', page: 'analytics' },
                    ].map((item) => (
                      <button
                        key={item.page}
                        onClick={() => setActivePage(item.page)}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                          padding: '14px 10px', borderRadius: 10, background: item.bg,
                          border: `1px solid ${item.color}22`, cursor: 'pointer', transition: 'transform 0.1s, box-shadow 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 4px 12px ${item.color}22`; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <item.icon size={22} style={{ color: item.color }} />
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#18181b' }}>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Pending Requests */}
              {allRequests.filter(r => r.status === 'pending').length > 0 && (
                <div className="cd-card" style={{ marginTop: 20 }}>
                  <div className="cd-card-header"><Clock size={16} /> Solicitudes Pendientes</div>
                  {allRequests.filter(r => r.status === 'pending').map((req, idx) => (
                    <div key={idx} className="cd-event-row">
                      <div><strong>{req.event?.name || 'Copa'}</strong><span>{req.event?.date ? new Date(req.event.date).toLocaleDateString('es-ES') : ''}</span></div>
                      <span className="cd-badge cd-badge-yellow"><Clock size={12} /> Pendiente</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ============ EVENTS (MERGED) ============ */}
          {activePage === 'events' && (
            <>
              {managingEventAthletes ? (
                <ClubEventAthleteManager
                  event={managingEventAthletes}
                  clubId={getClubId()}
                  athletes={athletes}
                  athleteImages={athleteImages}
                  onBack={() => setManagingEventAthletes(null)}
                  notify={notify}
                />
              ) : (
              <>
              <p className="cd-page-subtitle"><Calendar size={16} />Aquí puedes postular a copas, revisar tus inscripciones y gestionar las atletas participantes.</p>

              {/* Notification banner for pending requests */}
              {allRequests.filter(r => r.status === 'pending').length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12,
                  marginBottom: 16
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, background: '#fef3c7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <Bell size={16} style={{ color: '#d97706' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: '#92400e' }}>
                      {allRequests.filter(r => r.status === 'pending').length} solicitud{allRequests.filter(r => r.status === 'pending').length > 1 ? 'es' : ''} pendiente{allRequests.filter(r => r.status === 'pending').length > 1 ? 's' : ''}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#b45309' }}>
                      {allRequests.filter(r => r.status === 'pending').map(r => r.event?.name || 'Copa').join(', ')}
                    </p>
                  </div>
                  <Clock size={14} style={{ color: '#d97706', flexShrink: 0 }} />
                </div>
              )}

              {/* Rejected requests notification */}
              {allRequests.filter(r => r.status === 'rejected').length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12,
                  marginBottom: 16
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, background: '#fee2e2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <XCircle size={16} style={{ color: '#dc2626' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: '#991b1b' }}>
                      {allRequests.filter(r => r.status === 'rejected').length} solicitud{allRequests.filter(r => r.status === 'rejected').length > 1 ? 'es' : ''} rechazada{allRequests.filter(r => r.status === 'rejected').length > 1 ? 's' : ''}
                    </p>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {allRequests.filter(r => r.status === 'rejected').map((req, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: '#dc2626' }}>
                          <span>{req.event?.name || 'Copa'}</span>
                          <button
                            onClick={() => handleDeleteRejectedRequest(req.event_id || req.event?.id)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '0 2px', fontSize: '0.7rem', textDecoration: 'underline' }}
                          >
                            eliminar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Sub-tabs */}
              <div className="cd-sol-tabs" style={{ marginBottom: 16 }}>
                <button
                  className={`cd-sol-tab ${eventsTab === 'my-events' ? 'active' : ''}`}
                  onClick={() => setEventsTab('my-events')}
                >
                  <Calendar size={15} /> Mis Copas
                  {approvedEvents.length > 0 && <span className="cd-sol-tab-badge">{approvedEvents.length}</span>}
                </button>
                <button
                  className={`cd-sol-tab ${eventsTab === 'browse' ? 'active' : ''}`}
                  onClick={() => setEventsTab('browse')}
                >
                  <Search size={15} /> Explorar
                </button>
                <button
                  className={`cd-sol-tab ${eventsTab === 'request' ? 'active' : ''}`}
                  onClick={() => setEventsTab('request')}
                >
                  <KeyRound size={15} /> Solicitar Acceso
                </button>
              </div>

              {/* ── Tab: Mis Copas ── */}
              {eventsTab === 'my-events' && (
                <>
                  {approvedEvents.length > 0 ? (
                    <div className="cd-events-grid">
                      {approvedEvents.map(event => (
                        <div key={event.id} className="cd-event-card">
                          {eventImages[event.id] && <div className="cd-event-card-img"><img src={eventImages[event.id]} alt={event.name} /></div>}
                          <div className="cd-event-card-body">
                            <h3>{event.name}</h3>
                            <span className="cd-badge cd-badge-green" style={{ marginBottom: 6 }}>
                              {new Date(event.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </span>
                            <p>{event.location || 'Sin ubicación'}</p>
                            {(() => {
                              const disc = getDisciplineDisplay(event.discipline, event.category);
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#6b7280' }}>
                                  {disc.logo && <img src={disc.logo} alt={disc.fullName} style={{ width: 18, height: 18, objectFit: 'contain' }} />}
                                  <span>{disc.fullName}</span>
                                </div>
                              );
                            })()}
                            <button
                              className="btn btn-club-primary btn-sm"
                              style={{ marginTop: 10, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}
                              onClick={() => setManagingEventAthletes(event)}
                            >
                              <Users size={14} /> Administrar Atletas
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="cd-empty-state">
                      <Calendar size={48} />
                      <p>Sin copas aprobadas</p>
                      <span>Explora copas o solicita acceso con un código</span>
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button className="btn btn-club-primary btn-sm" onClick={() => setEventsTab('browse')}>
                          <Search size={14} /> Explorar
                        </button>
                        <button className="btn btn-club-secondary btn-sm" onClick={() => setEventsTab('request')}>
                          <KeyRound size={14} /> Tengo un Código
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── Tab: Explorar ── */}
              {eventsTab === 'browse' && (
                <>
                  <div style={{ marginBottom: 16, position: 'relative' }}>
                    <Search size={18} style={{ position: 'absolute', left: 12, top: 11, color: '#9ca3af' }} />
                    <input
                      type="text"
                      value={allEventsSearch}
                      onChange={(e) => setAllEventsSearch(e.target.value)}
                      placeholder="Buscar por nombre, ubicación o disciplina..."
                      style={{
                        width: '100%', padding: '10px 14px 10px 40px',
                        border: '1.5px solid #d1d5db', borderRadius: 10,
                        fontSize: '0.9rem', background: 'white', boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {allEventsLoading ? (
                    <div className="cd-empty-state"><div className="cd-spinner" /></div>
                  ) : filteredAllEvents.length === 0 ? (
                    <div className="cd-empty-state">
                      <Calendar size={48} />
                      <p>No se encontraron copas</p>
                    </div>
                  ) : (
                    <div className="cd-events-grid">
                      {filteredAllEvents.map(event => {
                        const status = getEventRequestStatus(event.id);
                        const isApproved = approvedEvents.some(e => e.id === event.id);
                        return (
                          <div key={event.id} className="cd-event-card">
                            {(eventImages[event.id] || event.image_url) && (
                              <div className="cd-event-card-img">
                                <img src={eventImages[event.id] || event.image_url} alt={event.name} />
                              </div>
                            )}
                            <div className="cd-event-card-body">
                              <h3>{event.name}</h3>
                              <span className="cd-badge cd-badge-green" style={{ marginBottom: 6 }}>
                                {new Date(event.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </span>
                              <p>{event.location || 'Sin ubicación'}</p>
                              {(() => {
                                const disc = getDisciplineDisplay(event.discipline, event.category);
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#6b7280' }}>
                                    {disc.logo && <img src={disc.logo} alt={disc.fullName} style={{ width: 18, height: 18, objectFit: 'contain' }} />}
                                    <span>{disc.fullName}</span>
                                  </div>
                                );
                              })()}

                              {isApproved ? (
                                <span className="cd-badge cd-badge-green" style={{ marginTop: 8 }}>
                                  <CheckCircle size={12} /> Acceso aprobado
                                </span>
                              ) : status === 'pending' ? (
                                <span className="cd-badge cd-badge-yellow" style={{ marginTop: 8 }}>
                                  <Clock size={12} /> Solicitud pendiente
                                </span>
                              ) : status === 'rejected' ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                                  <span className="cd-badge cd-badge-red">
                                    <XCircle size={12} /> Rechazada
                                  </span>
                                  <button
                                    onClick={() => handleDeleteRejectedRequest(event.id)}
                                    className="btn btn-sm"
                                    style={{ fontSize: '0.75rem', padding: '4px 10px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}
                                    title="Eliminar solicitud rechazada"
                                  >
                                    <Trash2 size={12} /> Eliminar
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleRequestAccessToEvent(event.id)}
                                  disabled={requestingAccess === event.id}
                                  className="btn btn-club-primary btn-sm"
                                  style={{ marginTop: 8, fontSize: '0.8rem' }}
                                >
                                  {requestingAccess === event.id ? 'Solicitando...' : 'Solicitar Acceso'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* ── Tab: Solicitar Acceso ── */}
              {eventsTab === 'request' && (
                <>
                  <div className="cd-card">
                    <div className="cd-card-header"><KeyRound size={16} /> Código de Copa</div>
                    <p className="cd-text-muted">Ingresa el código de copa proporcionado por el organizador para solicitar acceso.</p>
                    <div className="cd-code-row">
                      <input className="cd-code-input" type="text" value={eventCode} onChange={(e) => setEventCode(e.target.value.toUpperCase())} placeholder="ABC12345" maxLength={8} disabled={codeLoading} />
                      <button className="btn btn-club-primary" onClick={handleSubmitCode} disabled={codeLoading || !eventCode.trim()}>
                        {codeLoading ? 'Validando...' : 'Enviar Solicitud'}
                      </button>
                    </div>
                    {codeResult && (
                      <div className={`cd-alert ${codeResult.type === 'success' ? 'cd-alert-success' : 'cd-alert-error'}`}>
                        {codeResult.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
                        <span>{codeResult.message}</span>
                      </div>
                    )}
                  </div>

                  {/* Historial de solicitudes */}
                  {allRequests.length > 0 && (
                    <div className="cd-card" style={{ marginTop: 16 }}>
                      <div className="cd-card-header"><FileText size={16} /> Historial de Solicitudes</div>
                      {allRequests.map((req, idx) => (
                        <div key={idx} className="cd-event-row">
                          <div>
                            <strong>{req.event?.name || 'Copa'}</strong>
                            <span>{req.event?.date ? new Date(req.event.date).toLocaleDateString('es-ES') : ''}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {req.status === 'pending' && (
                              <span className="cd-badge cd-badge-yellow"><Clock size={12} /> Pendiente</span>
                            )}
                            {req.status === 'approved' && (
                              <span className="cd-badge cd-badge-green"><CheckCircle size={12} /> Aprobada</span>
                            )}
                            {req.status === 'rejected' && (
                              <>
                                <span className="cd-badge cd-badge-red"><XCircle size={12} /> Rechazada</span>
                                <button
                                  onClick={() => handleDeleteRejectedRequest(req.event_id || req.event?.id)}
                                  className="btn btn-sm"
                                  style={{ fontSize: '0.72rem', padding: '3px 8px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}
                                  title="Eliminar solicitud rechazada"
                                >
                                  <Trash2 size={11} /> Eliminar
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              </>
              )}
            </>
          )}

          {/* ============ ATHLETES ============ */}
          {activePage === 'athletes' && (
            <>
              <div className="cd-page-title-row">
                <p className="cd-page-subtitle" style={{ margin: 0, flex: 1 }}><Users size={16} />Administra las atletas del club, sus inscripciones y análisis de rendimiento.</p>
                <button className="btn btn-club-primary btn-sm" onClick={() => { resetAthleteForm(); setShowAthleteForm(true); }}>
                  <Plus size={14} /> Nueva Atleta
                </button>
              </div>

              {/* Form */}
              {showAthleteForm && (
                <div className="cd-card" style={{ marginBottom: 20 }}>
                  <div className="cd-card-header" style={{ justifyContent: 'space-between' }}>
                    <span>{editingAthlete ? 'Editar Atleta' : 'Nueva Atleta'}</span>
                    <button className="btn btn-club-secondary btn-sm" onClick={resetAthleteForm}><X size={14} /></button>
                  </div>
                  <div className="club-photo-upload">
                    <label htmlFor="athlete-photo-input" className="club-photo-area">
                      {athletePhotoPreview ? <img src={athletePhotoPreview} alt="Foto" className="club-photo-preview" /> : <div className="club-photo-placeholder"><Camera size={20} /><span>Foto</span></div>}
                      <input id="athlete-photo-input" type="file" accept="image/*" onChange={handleAthletePhotoChange} style={{ display: 'none' }} />
                    </label>
                  </div>
                  <div className="cd-form-grid">
                    <div className="cd-form-group"><label>Nombre *</label><input type="text" value={athleteForm.name} onChange={(e) => setAthleteForm({ ...athleteForm, name: e.target.value })} placeholder="Nombre completo" /></div>
                    <div className="cd-form-group">
                      <label>Nivel USAG</label>
                      <select value={athleteForm.level} onChange={(e) => setAthleteForm({ ...athleteForm, level: e.target.value })}>
                        <option value="">Seleccionar nivel...</option>
                        {LEVEL_OPTIONS.map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
                      </select>
                    </div>
                    <div className="cd-form-group">
                      <label>Franja</label>
                      <select value={athleteForm.franja} onChange={(e) => setAthleteForm({ ...athleteForm, franja: e.target.value })}>
                        <option value="">Seleccionar franja...</option>
                        {FRANJA_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div className="cd-form-group"><label>Fecha de Nacimiento</label><input type="date" value={athleteForm.birth_date} onChange={(e) => setAthleteForm({ ...athleteForm, birth_date: e.target.value })} /></div>
                    <div className="cd-form-group">
                      <label>Grupo de Formación</label>
                      <select value={athleteForm.category} onChange={(e) => setAthleteForm({ ...athleteForm, category: e.target.value })}>
                        <option value="">Seleccionar grupo...</option>
                        {CATEGORY_OPTIONS.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    </div>
                    <div className="cd-form-group">
                      <label>Plan {clubPlans.length === 0 && <span style={{ color: '#a1a1aa', fontWeight: 400 }}>(crea planes en Finanzas)</span>}</label>
                      <select value={athleteForm.plan_id} onChange={(e) => setAthleteForm({ ...athleteForm, plan_id: e.target.value })} disabled={clubPlans.length === 0}>
                        <option value="">Sin plan asignado</option>
                        {clubPlans.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name} · {p.days_per_week ?? '?'} día{(p.days_per_week ?? 0) === 1 ? '' : 's'}/sem · ${p.price}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="cd-form-group">
                      <label>Nombre Apoderado <span style={{ color: '#a1a1aa', fontWeight: 400 }}>(privado)</span></label>
                      <input type="text" value={athleteForm.guardian_name} onChange={(e) => setAthleteForm({ ...athleteForm, guardian_name: e.target.value })} placeholder="Nombre del apoderado/tutor" />
                    </div>
                    <div className="cd-form-group">
                      <label>Teléfono Apoderado <span style={{ color: '#a1a1aa', fontWeight: 400 }}>(privado)</span></label>
                      <input type="tel" value={athleteForm.guardian_phone} onChange={(e) => setAthleteForm({ ...athleteForm, guardian_phone: e.target.value })} placeholder="+56 9 1234 5678" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-club-primary" onClick={handleSaveAthlete}>{editingAthlete ? 'Guardar' : 'Crear Atleta'}</button>
                    <button className="btn btn-club-secondary" onClick={resetAthleteForm}>Cancelar</button>
                  </div>
                </div>
              )}

              {/* Search + Level + Category Filters */}
              <div className="cd-athlete-filters">
                <div className="cd-search">
                  <Search size={16} />
                  <input type="text" placeholder="Buscar atleta..." value={athleteSearch} onChange={(e) => setAthleteSearch(e.target.value)} />
                </div>
                <select className="cd-level-filter" value={athleteLevelFilter} onChange={e => setAthleteLevelFilter(e.target.value)}>
                  <option value="">Todos los niveles</option>
                  {LEVEL_OPTIONS.map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
                </select>
                <select className="cd-level-filter" value={athleteCategoryFilter} onChange={e => setAthleteCategoryFilter(e.target.value)}>
                  <option value="">Todos los grupos</option>
                  {CATEGORY_OPTIONS.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>

              {/* Athletes Table — lazy: solo renderiza cuando hay búsqueda/filtro activo */}
              {(() => {
                const hasActiveFilter = athleteSearch.trim() !== '' || athleteLevelFilter !== '' || athleteCategoryFilter !== '';
                if (!hasActiveFilter) {
                  return (
                    <div className="cd-empty-state" style={{ padding: '48px 24px' }}>
                      <Users size={48} style={{ color: '#cbd5e1' }} />
                      <p style={{ fontSize: '1rem', fontWeight: 600, margin: '12px 0 6px' }}>
                        {athletes.length} atleta{athletes.length === 1 ? '' : 's'} registrada{athletes.length === 1 ? '' : 's'}
                      </p>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        Usa el buscador o los filtros de arriba para ver el listado
                      </span>
                    </div>
                  );
                }
                return filteredAthletes.length > 0 ? (
                <div className="cd-card">
                  <div className="cd-table-wrap">
                  <table className="cd-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Nombre</th>
                        <th>Grupo</th>
                        <th>Nivel USAG</th>
                        <th>Franja</th>
                        <th>Edad</th>
                        <th>Apoderado</th>
                        <th>Teléfono</th>
                        <th style={{ textAlign: 'center', width: 90 }}>Membresía</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAthletes.map(athlete => {
                        return (
                          <tr key={athlete.id}>
                            <td>
                              <div className="cd-avatar" style={athleteImages[athlete.id] ? { padding: 0, overflow: 'hidden' } : {}}>
                                {athleteImages[athlete.id] ? <img src={athleteImages[athlete.id]} alt={athlete.name} /> : getInitials(athlete.name)}
                              </div>
                            </td>
                            <td><strong>{athlete.name}</strong></td>
                            <td><span className="cd-category-badge">{athlete.category || '—'}</span></td>
                            <td>{athlete.level || '—'}</td>
                            <td>{athlete.franja || '—'}</td>
                            <td>{calculateAge(athlete.birth_date) ?? athlete.age ?? '—'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{athlete.guardian_name || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                            <td style={{ fontSize: '0.85rem' }}>
                              {athlete.guardian_phone ? (
                                <a href={`tel:${athlete.guardian_phone}`} style={{ color: '#0ea5e9', textDecoration: 'none' }}>{athlete.guardian_phone}</a>
                              ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {(() => {
                                const st = membershipStatus(athlete.id);
                                const cfg = {
                                  al_dia:       { color: '#16a34a', bg: '#dcfce7', title: 'Al día' },
                                  gracia:       { color: '#d97706', bg: '#fef3c7', title: 'Días de gracia' },
                                  moroso:       { color: '#dc2626', bg: '#fee2e2', title: 'Moroso' },
                                  sin_membresia:{ color: '#94a3b8', bg: '#f1f5f9', title: 'Sin membresía' },
                                }[st];
                                const lastDate = lastPaymentByGymnast[athlete.id];
                                const titleText = `${cfg.title}${lastDate ? ` · Último pago: ${new Date(lastDate).toLocaleDateString('es-CL')}` : ''}`;
                                return (
                                  <span
                                    title={titleText}
                                    style={{
                                      display: 'inline-block', width: 14, height: 14, borderRadius: '50%',
                                      background: cfg.color, boxShadow: `0 0 0 3px ${cfg.bg}`,
                                    }}
                                  />
                                );
                              })()}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 4 }}>
                                {athletePlanMap[athlete.id]?.membership_id && !isPaidThisMonth(athlete.id) && (
                                  <button
                                    className="btn btn-sm"
                                    style={{ background: '#16a34a', color: 'white', border: 'none', padding: '4px 10px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}
                                    title={`Registrar pago (${athletePlanMap[athlete.id]?.plan?.name || ''})`}
                                    onClick={() => {
                                      const info = athletePlanMap[athlete.id];
                                      const membership = {
                                        id: info.membership_id,
                                        gymnast_id: athlete.id,
                                        plan_id: info.plan_id,
                                        gimnasts: { id: athlete.id, name: athlete.name },
                                        club_planes: info.plan,
                                        status: 'active',
                                      };
                                      setSelectedMembership(membership);
                                      setPagoForm({ amount: info.plan?.price || 0, payment_method: 'efectivo', is_advance: false });
                                      setShowPagoModal(true);
                                    }}
                                  >
                                    <CreditCard size={13} /> Pagar
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm"
                                  style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: 'white', border: 'none', padding: '4px 10px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: '0.72rem' }}
                                  onClick={() => setSelectedGymnastForAnalysis(athlete)}
                                  title="Análisis de rendimiento con IA"
                                >
                                  <Activity size={12} /> IA
                                </button>
                                <button className="btn btn-club-secondary btn-sm" onClick={() => handleEditAthlete(athlete)} title="Editar"><Edit2 size={13} /></button>
                                <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteAthlete(athlete.id)} title="Eliminar"><Trash2 size={13} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              ) : (
                <div className="cd-empty-state"><Users size={48} /><p>Sin resultados</p><span>Ninguna atleta coincide con los filtros aplicados</span></div>
              );
              })()}
            </>
          )}

          {/* ============ ATTENDANCE ============ */}
          {activePage === 'attendance' && (
            <>
              <p className="cd-page-subtitle"><ClipboardCheck size={16} />Registra y consulta la asistencia diaria de tus atletas y entrenadores.</p>

              {/* Coach QR + Ver Hoy banner */}
              {approvedCoaches.length > 0 && (
                <div className="cd-card" style={{ marginBottom: 16, background: '#faf9fe', border: '1px solid #e9e5f5' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <Dumbbell size={20} style={{ color: '#8b5cf6' }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#3f3f46' }}>Asistencia de Entrenadores (QR)</p>
                      <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                        Los entrenadores escanean el QR en la entrada del gimnasio para registrar asistencia
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-club-primary btn-sm" onClick={() => setShowCoachTodayModal(true)}>
                      <Eye size={14} /> Ver Hoy
                    </button>
                    <button className="btn btn-club-secondary btn-sm" onClick={() => setShowGeneralCoachQR(!showGeneralCoachQR)}>
                      <QrCode size={14} /> {showGeneralCoachQR ? 'Ocultar QR' : 'Ver QR'}
                    </button>
                    <button className="btn btn-club-secondary btn-sm" onClick={handlePrintCoachQR} title="Imprimir QR en PDF">
                      <Printer size={14} /> Imprimir QR
                    </button>
                    <button className="btn btn-club-secondary btn-sm" onClick={handleGenerateNewQrToken} title="Generar nuevo QR">
                      <RefreshCw size={14} /> Nuevo QR
                    </button>
                  </div>
                  {showGeneralCoachQR && coachQrToken && (
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      padding: '20px 0 8px', borderTop: '1px solid #e9e5f5', marginTop: 16
                    }}>
                      <div style={{ background: 'white', padding: 16, borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        <QRCodeSVG value={getGeneralQRUrl()} size={200} level="M" includeMargin />
                      </div>
                      <p style={{ margin: '12px 0 4px', fontSize: '0.82rem', color: '#3f3f46', fontWeight: 600, textAlign: 'center' }}>
                        Escanear para marcar asistencia
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Date Picker + Print */}
              <div className="cd-card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Fecha:</label>
                    <input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} className="cd-level-filter" style={{ maxWidth: 180 }} />
                    <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                      {new Date(attendanceDate + 'T12:00').toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-club-secondary btn-sm" onClick={() => handlePrintDailyAttendance()} title="Imprimir asistencia del día">
                      <Printer size={14} /> Día
                    </button>
                    <button className="btn btn-club-secondary btn-sm" onClick={() => handlePrintWeeklyAttendance()} title="Imprimir resumen semanal">
                      <Printer size={14} /> Semana
                    </button>
                  </div>
                </div>
              </div>

              {/* Coach Attendance for Selected Date (QR-based, independent of blocks) */}
              {(() => {
                const dateCoachRecs = coachAttendanceHistory.filter(r => r.date === attendanceDate && r.status === 'present');
                if (dateCoachRecs.length === 0) return null;
                return (
                  <div className="cd-card" style={{ marginBottom: 16, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <CheckCircle size={18} style={{ color: '#16a34a' }} />
                      <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#166534' }}>
                        Entrenadores presentes — {new Date(attendanceDate + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                      </span>
                      <span className="cd-badge cd-badge-green" style={{ marginLeft: 'auto', fontSize: '0.75rem' }}>{dateCoachRecs.length}</span>
                    </div>
                    {dateCoachRecs.map(rec => {
                      const inTime = rec.check_in_time ? new Date(rec.check_in_time).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : '';
                      const outTime = rec.check_out_time ? new Date(rec.check_out_time).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : '';
                      const photoUrl = coachPhotoMap[rec.coach_id];
                      const name = rec.coaches?.name || '—';
                      return (
                        <div key={rec.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                          background: 'white', borderRadius: 10, marginBottom: 6, border: '1px solid #dcfce7'
                        }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: '50%', overflow: 'hidden',
                            background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 700, color: '#16a34a', flexShrink: 0,
                            border: '1.5px solid #bbf7d0'
                          }}>
                            {photoUrl
                              ? <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
                            }
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: '#18181b' }}>{name}</p>
                          </div>
                          <div style={{ textAlign: 'right', fontSize: '0.78rem', color: '#64748b' }}>
                            {inTime && (
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>
                              ↓ {new Date(rec.check_in_time).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            )}
                            {outTime && (
                              <span style={{ marginLeft: 8, color: '#f59e0b', fontWeight: 600 }}>
                              ↑ {new Date(rec.check_out_time).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Block Accordions */}
              {(() => {
                const dayOfWeek = new Date(attendanceDate + 'T12:00').getDay();
                const dayBlocks = allBlocks.filter(b => blockAppliesTo(b, dayOfWeek));

                if (dayBlocks.length === 0) {
                  return (
                    <div className="cd-empty-state" style={{ padding: 40 }}>
                      <CalendarDays size={40} />
                      <p>Sin bloques programados para este día</p>
                      <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Agrega bloques en el Calendario para poder tomar asistencia</span>
                    </div>
                  );
                }

                return dayBlocks.map(block => {
                  const isOpen = attendanceAccordion[block.id];
                  const blockData = blockAttendanceMap[block.id] || { athletes: [], records: {} };
                  const presentCount = blockData.athletes.filter(a => {
                    const key = `${block.id}_${a.id}`;
                    return attendanceRecords[key] === 'present' || (!attendanceRecords[key] && blockData.records[a.id] === 'present');
                  }).length;

                  return (
                    <div key={block.id} className="cd-card cd-accordion-card" style={{ marginBottom: 12 }}>
                      {/* Accordion Header */}
                      <div
                        className="cd-accordion-header"
                        style={{ borderLeft: `4px solid ${block.color || '#8b5cf6'}` }}
                        onClick={() => setAttendanceAccordion(prev => ({ ...prev, [block.id]: !prev[block.id] }))}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: block.color || '#8b5cf6', flexShrink: 0 }} />
                          <div>
                            <strong style={{ fontSize: '0.9rem' }}>{block.title}</strong>
                            <span style={{ fontSize: '0.78rem', color: '#64748b', marginLeft: 8 }}>
                              {(block.start_time || '').slice(0, 5)} - {(block.end_time || '').slice(0, 5)}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span className="cd-badge cd-badge-green" style={{ fontSize: '0.72rem' }}>{presentCount}/{blockData.athletes.length}</span>
                          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </div>

                      {/* Accordion Body */}
                      {isOpen && (
                        <div style={{ padding: '8px 0' }}>
                          {blockData.athletes.length === 0 ? (
                            <div style={{ padding: '20px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                              Sin atletas asignadas a este bloque
                            </div>
                          ) : (
                            blockData.athletes.map(a => {
                              const key = `${block.id}_${a.id}`;
                              const savedStatus = blockData.records[a.id];
                              const currentStatus = attendanceRecords[key] !== undefined ? attendanceRecords[key] : savedStatus;
                              const isAbsent = currentStatus === 'absent' || currentStatus === 'justified';
                              const isJustified = justifiedRecords[key] || currentStatus === 'justified';
                              return (
                                <div key={a.id} className="cd-attendance-row">
                                  <div className="cd-attendance-name">
                                    <div className="cd-avatar-sm" style={athleteImages[a.id] ? { padding: 0, overflow: 'hidden' } : {}}>
                                      {athleteImages[a.id]
                                        ? <img src={athleteImages[a.id]} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                        : getInitials(a.name)}
                                    </div>
                                    <span>{a.name}</span>
                                    {a.category && <span className="cd-category-badge">{a.category}</span>}
                                  </div>
                                  <div className="cd-attendance-actions">
                                    <button className={`cd-att-btn present ${currentStatus === 'present' ? 'active' : ''}`} onClick={() => toggleBlockAttendance(block.id, a.id, 'present')}>
                                      <CheckCircle size={14} /> Presente
                                    </button>
                                    <button className={`cd-att-btn absent ${isAbsent ? 'active' : ''}`} onClick={() => toggleBlockAttendance(block.id, a.id, 'absent')}>
                                      <XCircle size={14} /> Ausente
                                    </button>
                                    {isAbsent && (
                                      <label className="cd-justified-check" onClick={() => toggleJustified(block.id, a.id)}>
                                        <span className={`cd-justified-box ${isJustified ? 'checked' : ''}`}>
                                          {isJustified && <CheckCircle size={12} />}
                                        </span>
                                        <span>Justificado</span>
                                      </label>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}

              {/* Save Button */}
              {allBlocks.filter(b => blockAppliesTo(b, new Date(attendanceDate + 'T12:00').getDay())).length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <button className="btn btn-club-primary" onClick={handleSaveBlockAttendance} disabled={savingAttendance}>
                    <Save size={14} /> {savingAttendance ? 'Guardando...' : 'Guardar Asistencia'}
                  </button>
                </div>
              )}

              {/* History */}
              {attendanceHistory.length > 0 && (
                <div className="cd-card" style={{ marginTop: 16 }}>
                  <div className="cd-card-header"><span>Historial de la Semana</span></div>
                  <div className="cd-table-wrap">
                  <table className="cd-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Fecha</th>
                        <th>Estado</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceHistory.map(record => (
                        <tr key={record.id}>
                          <td>{record.gimnasts?.name || '—'}</td>
                          <td>{new Date(record.date + 'T12:00').toLocaleDateString('es', { day: '2-digit', month: 'short' })}</td>
                          <td>
                            <span className={`cd-badge ${record.status === 'present' ? 'cd-badge-green' : record.status === 'justified' || record.status === 'rescheduled' ? 'cd-badge-yellow' : 'cd-badge-red'}`}>
                              {record.status === 'present' ? 'Presente' : record.status === 'justified' ? 'Justificado' : record.status === 'rescheduled' ? 'Reagendado' : 'Ausente'}
                            </span>
                          </td>
                          <td>
                            {record.status === 'absent' && (
                              <button className="btn btn-club-secondary btn-sm" onClick={() => setJustifyModal(record)}>Justificar</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {/* Modal: Asistencia de Hoy */}
              {showCoachTodayModal && (() => {
                const todayStr = attendanceDate;
                const todayLabel = new Date(todayStr + 'T12:00').toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                const todayRecs = coachAttendanceHistory.filter(r => r.date === todayStr && r.status === 'present');

                return (
                  <div className="cd-modal-overlay" onClick={() => setShowCoachTodayModal(false)}>
                    <div className="cd-coach-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440, padding: 0 }}>
                      {/* Header */}
                      <div style={{
                        textAlign: 'center', padding: '24px 24px 16px',
                        borderBottom: '1px solid #f1f5f9', background: '#f0fdf4',
                        borderRadius: '16px 16px 0 0'
                      }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: 12,
                          background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          margin: '0 auto 10px'
                        }}>
                          <ClipboardCheck size={22} style={{ color: '#16a34a' }} />
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#3f3f46' }}>Asistencia de Hoy</h3>
                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
                          {todayLabel}
                        </p>
                      </div>
                      {/* Body */}
                      <div style={{ padding: '16px 24px 20px' }}>
                        {todayRecs.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <Dumbbell size={32} style={{ color: '#d4d4d8', marginBottom: 8 }} />
                            <p style={{ color: '#a1a1aa', fontSize: '0.88rem', margin: 0 }}>Sin asistencias registradas hoy</p>
                          </div>
                        ) : (
                          <>
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                              marginBottom: 16, padding: '10px 16px', background: '#f0fdf4',
                              borderRadius: 10, border: '1px solid #bbf7d0'
                            }}>
                              <CheckCircle size={16} style={{ color: '#16a34a' }} />
                              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#166534' }}>{todayRecs.length}</span>
                              <span style={{ fontSize: '0.85rem', color: '#166534' }}>
                                {todayRecs.length === 1 ? 'entrenador presente' : 'entrenadores presentes'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {todayRecs.map(r => {
                                const inTime = r.check_in_time ? new Date(r.check_in_time).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : '';
                                const outTime = r.check_out_time ? new Date(r.check_out_time).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : '';
                                const photoUrl = coachPhotoMap[r.coach_id];
                                const name = r.coaches?.name || '—';
                                return (
                                  <div key={r.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 12,
                                    padding: '10px 14px', background: '#fafafa',
                                    borderRadius: 10, border: '1px solid #f1f5f9'
                                  }}>
                                    <div style={{
                                      width: 36, height: 36, borderRadius: '50%', overflow: 'hidden',
                                      background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      border: '1.5px solid #bbf7d0', flexShrink: 0, fontSize: '0.7rem', fontWeight: 700, color: '#16a34a'
                                    }}>
                                      {photoUrl
                                        ? <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        : name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
                                      }
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: '#18181b' }}>{name}</p>
                                      {inTime && (
                                        <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#16a34a' }}>
                                          Entrada: {inTime}{outTime ? ` · Salida: ${outTime}` : ''}
                                        </p>
                                      )}
                                    </div>
                                    <CheckCircle size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                      {/* Footer */}
                      <div style={{ padding: '0 24px 20px' }}>
                        <button className="btn btn-club-secondary" style={{ width: '100%' }} onClick={() => setShowCoachTodayModal(false)}>
                          Cerrar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Coach Attendance Password Modal */}
              {coachAttPasswordModal && (
                <div className="cd-modal-overlay" onClick={() => setCoachAttPasswordModal(false)}>
                  <div className="cd-coach-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, padding: 0 }}>
                    {/* Header */}
                    <div style={{
                      textAlign: 'center', padding: '24px 24px 16px',
                      borderBottom: '1px solid #f1f5f9', background: '#faf9fe',
                      borderRadius: '16px 16px 0 0'
                    }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: '#fef3c7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: 10, border: '1px solid #fde68a'
                      }}>
                        <KeyRound size={22} style={{ color: '#f59e0b' }} />
                      </div>
                      <h3 style={{ margin: '0 0 4px', fontSize: '0.95rem', color: '#18181b' }}>Confirmar Marcación Manual</h3>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
                        Ingresa la contraseña del club para registrar asistencia
                      </p>
                    </div>
                    {/* Body */}
                    <div style={{ padding: '20px 24px' }}>
                      <div className="cd-form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6, display: 'block', color: '#3f3f46' }}>Contraseña del Club</label>
                        <input
                          type="password"
                          value={coachAttPassword}
                          onChange={e => { setCoachAttPassword(e.target.value); setCoachAttPasswordError(''); }}
                          placeholder="Contraseña..."
                          autoFocus
                          onKeyDown={e => e.key === 'Enter' && confirmSaveCoachAttendance()}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${coachAttPasswordError ? '#fca5a5' : '#e2e8f0'}`, fontFamily: 'inherit', fontSize: '0.9rem', outline: 'none', transition: 'border 0.15s' }}
                        />
                        {coachAttPasswordError && (
                          <p style={{ color: '#dc2626', fontSize: '0.78rem', margin: '6px 0 0' }}>{coachAttPasswordError}</p>
                        )}
                      </div>
                    </div>
                    {/* Footer */}
                    <div style={{ padding: '0 24px 20px', display: 'flex', gap: 8 }}>
                      <button className="btn btn-club-primary" style={{ flex: 1 }} onClick={confirmSaveCoachAttendance} disabled={savingAttendance}>
                        {savingAttendance ? 'Guardando...' : 'Confirmar y Guardar'}
                      </button>
                      <button className="btn btn-club-secondary" onClick={() => setCoachAttPasswordModal(false)}>Cancelar</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Justify Modal */}
              {justifyModal && (
                <div className="cd-modal-overlay" onClick={() => setJustifyModal(null)}>
                  <div className="cd-coach-modal" onClick={e => e.stopPropagation()}>
                    <h2 style={{ marginBottom: 16 }}>Justificar Ausencia</h2>
                    <div className="cd-form-group">
                      <label>Motivo</label>
                      <textarea value={justification} onChange={e => setJustification(e.target.value)} placeholder="Razón de la ausencia..." rows={3} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontFamily: 'inherit', fontSize: '0.88rem' }} />
                    </div>
                    <div className="cd-form-group">
                      <label>Reagendar a (opcional)</label>
                      <input type="date" value={rescheduledDate} onChange={e => setRescheduledDate(e.target.value)} className="cd-level-filter" style={{ maxWidth: '100%' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                      <button className="btn btn-club-primary" onClick={handleJustifyAbsence}>Guardar</button>
                      <button className="btn btn-club-secondary" onClick={() => { setJustifyModal(null); setJustification(''); setRescheduledDate(''); }}>Cancelar</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ============ SCHEDULE ============ */}
          {activePage === 'schedule' && (
            <>
              <p className="cd-page-subtitle"><CalendarDays size={16} />Organiza horarios, bloques de entrenamiento y visualiza el calendario semanal del club.</p>
              {/* Toolbar — compact, right-aligned */}
              <div className="cd-schedule-toolbar">
                <button className="cd-schedule-btn" onClick={() => { resetBlockForm(); setShowBlockForm(true); }}>
                  <Plus size={14} /> Nuevo Bloque
                </button>
                <button className="cd-schedule-btn cd-schedule-btn-outline" onClick={handlePrintWeeklyCalendar}>
                  <Printer size={14} /> Imprimir
                </button>
              </div>

              {showBlockForm && (
                <div className="cd-card" style={{ marginBottom: 16 }}>
                  <div className="cd-card-header" style={{ justifyContent: 'space-between' }}>
                    <span>{editingBlock ? 'Editar Bloque' : 'Nuevo Bloque'}</span>
                    <button className="btn btn-club-secondary btn-sm" onClick={resetBlockForm}><X size={14} /></button>
                  </div>
                  <div className="cd-form-grid">
                    <div className="cd-form-group">
                      <label>Nombre del Bloque *</label>
                      <input type="text" value={blockForm.title} onChange={e => setBlockForm({ ...blockForm, title: e.target.value })} placeholder="Ej: Pre Infantil" />
                    </div>
                    <div className="cd-form-group">
                      <label>Duración (min)</label>
                      <input type="number" value={blockForm.duration_minutes} onChange={e => setBlockForm({ ...blockForm, duration_minutes: e.target.value })} min="15" step="15" />
                    </div>
                    <div className="cd-form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Días de la semana <span style={{ color: '#64748b', fontWeight: 400 }}>(puedes seleccionar varios)</span></label>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                        {DAY_NAMES_FULL.map((d, i) => {
                          const isSelected = (blockForm.days_of_week || []).map(Number).includes(i);
                          return (
                            <button
                              type="button"
                              key={i}
                              onClick={() => {
                                const current = (blockForm.days_of_week || []).map(Number);
                                const next = isSelected
                                  ? current.filter(x => x !== i)
                                  : [...current, i].sort((a, b) => a - b);
                                setBlockForm({ ...blockForm, days_of_week: next });
                              }}
                              style={{
                                padding: '8px 14px',
                                borderRadius: 8,
                                border: isSelected ? '2px solid #8b5cf6' : '1px solid #e2e8f0',
                                background: isSelected ? '#8b5cf6' : '#fff',
                                color: isSelected ? '#fff' : '#475569',
                                fontWeight: isSelected ? 600 : 500,
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                minWidth: 52,
                              }}
                            >
                              {DAY_NAMES[i]}
                            </button>
                          );
                        })}
                      </div>
                      {(!blockForm.days_of_week || blockForm.days_of_week.length === 0) && (
                        <span style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: 4, display: 'block' }}>Selecciona al menos un día</span>
                      )}
                    </div>
                    <div className="cd-form-group">
                      <label>Hora Inicio</label>
                      <input type="time" value={blockForm.start_time} onChange={e => setBlockForm({ ...blockForm, start_time: e.target.value })} />
                    </div>
                    <div className="cd-form-group">
                      <label>Hora Fin</label>
                      <input type="time" value={blockForm.end_time} onChange={e => setBlockForm({ ...blockForm, end_time: e.target.value })} />
                    </div>
                    <div className="cd-form-group">
                      <label>Color</label>
                      <input type="color" value={blockForm.color} onChange={e => setBlockForm({ ...blockForm, color: e.target.value })} style={{ width: 50, height: 36, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-club-primary" onClick={handleSaveBlock}>{editingBlock ? 'Guardar' : 'Crear Bloque'}</button>
                    <button className="btn btn-club-secondary" onClick={resetBlockForm}>Cancelar</button>
                  </div>
                </div>
              )}

              {/* Calendar Profesional (Schedule-X) */}
              <ClubCalendar
                blocks={scheduleBlocks}
                onEventClick={(block) => openBlockAthletesModal(block)}
                onEventDrop={async (block, changes) => {
                  try {
                    const result = await scheduleBlockServiceProxy.update(block.id, changes);
                    if (result.error) throw new Error(result.error);
                    notify.success('Bloque reubicado');
                    loadScheduleBlocks();
                  } catch (err) {
                    notify.error('Error al mover el bloque');
                  }
                }}
                defaultView="week"
              />

              {/* Block Athletes Modal */}
              {blockAthletesModal && (
                <div className="cd-modal-overlay" onClick={() => setBlockAthletesModal(null)}>
                  <div className="cd-block-modal" onClick={e => e.stopPropagation()}>
                    {/* Header */}
                    <div className="cd-block-modal-header" style={{ borderLeft: `4px solid ${blockAthletesModal.color || '#8b5cf6'}` }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 className="cd-block-modal-title">{blockAthletesModal.title}</h3>
                        <p className="cd-block-modal-sub">
                          {getBlockDays(blockAthletesModal).map(d => DAY_NAMES_FULL[d]).join(', ') || '—'} · {(blockAthletesModal.start_time || '').slice(0, 5)} - {(blockAthletesModal.end_time || '').slice(0, 5)} · {blockAthletesModal.duration_minutes}min
                        </p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <button
                          className="btn btn-sm"
                          style={{ padding: '6px 10px', fontSize: '0.75rem', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => {
                            const blockToEdit = blockAthletesModal;
                            setBlockAthletesModal(null);
                            handleEditBlock(blockToEdit);
                          }}
                          title="Editar bloque"
                        >
                          <Edit2 size={13} /> Editar
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ padding: '6px 10px', fontSize: '0.75rem', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontWeight: 600 }}
                          onClick={async () => {
                            const blockId = blockAthletesModal.id;
                            setBlockAthletesModal(null);
                            await handleDeleteBlock(blockId);
                          }}
                          title="Eliminar bloque"
                        >
                          <Trash2 size={13} /> Eliminar
                        </button>
                        <button className="cd-block-modal-close" onClick={() => setBlockAthletesModal(null)}><X size={16} /></button>
                      </div>
                    </div>

                    {/* Assigned Athletes */}
                    <div className="cd-block-modal-section">
                      <div className="cd-block-modal-section-title">
                        <Users size={14} />
                        <span>Atletas en este bloque ({blockAthletes.length})</span>
                      </div>
                      {blockAthleteLoading ? (
                        <div className="cd-block-modal-empty">Cargando...</div>
                      ) : blockAthletes.length === 0 ? (
                        <div className="cd-block-modal-empty">Sin atletas asignadas a este bloque</div>
                      ) : (
                        <div className="cd-block-modal-list">
                          {blockAthletes.map(a => {
                            const photo = athleteImages[a.id] || a.photo_url;
                            return (
                            <div key={a.id} className="cd-block-modal-row">
                              <div className="cd-attendance-name">
                                <div className="cd-avatar-sm" style={photo ? { padding: 0, overflow: 'hidden' } : {}}>
                                  {photo
                                    ? <img src={photo} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                    : getInitials(a.name)}
                                </div>
                                <span>{a.name}</span>
                                {a.category && <span className="cd-category-badge">{a.category}</span>}
                              </div>
                              <button className="cd-block-remove-btn" onClick={() => handleRemoveAthleteFromBlock(a.id)} title="Remover">
                                <MinusCircle size={16} />
                              </button>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Search + Add */}
                    <div className="cd-block-modal-section">
                      <div className="cd-block-modal-section-title">
                        <PlusCircle size={14} />
                        <span>Agregar Atletas</span>
                      </div>
                      <div className="cd-search" style={{ marginBottom: 8 }}>
                        <Search size={16} />
                        <input type="text" placeholder="Buscar por nombre..." value={blockAthleteSearch} onChange={e => setBlockAthleteSearch(e.target.value)} />
                      </div>
                      <div className="cd-block-modal-list">
                        {availableAthletesForBlock.length === 0 ? (
                          <div className="cd-block-modal-empty">
                            {blockAthleteSearch ? 'Sin resultados' : 'Todas las atletas ya están asignadas'}
                          </div>
                        ) : (
                          availableAthletesForBlock.map(a => {
                            const photo = athleteImages[a.id] || a.photo_url;
                            return (
                            <div key={a.id} className="cd-block-modal-row">
                              <div className="cd-attendance-name">
                                <div className="cd-avatar-sm" style={photo ? { padding: 0, overflow: 'hidden' } : {}}>
                                  {photo
                                    ? <img src={photo} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                    : getInitials(a.name)}
                                </div>
                                <span>{a.name}</span>
                                {a.category && <span className="cd-category-badge">{a.category}</span>}
                                {a.level && <span style={{ fontSize: '0.72rem', color: '#8b5cf6', fontWeight: 600 }}>{a.level}</span>}
                              </div>
                              <button className="cd-block-add-btn" onClick={() => handleAddAthleteToBlock(a)} title="Agregar">
                                <PlusCircle size={16} />
                              </button>
                            </div>
                            );
                          }))
                        }
                      </div>
                    </div>

                    {/* Assigned Coaches */}
                    <div className="cd-block-modal-section">
                      <div className="cd-block-modal-section-title">
                        <Dumbbell size={14} />
                        <span>Entrenadores en este bloque ({blockCoaches.length})</span>
                      </div>
                      {blockAthleteLoading ? (
                        <div className="cd-block-modal-empty">Cargando...</div>
                      ) : blockCoaches.length === 0 ? (
                        <div className="cd-block-modal-empty">Sin entrenadores asignados a este bloque</div>
                      ) : (
                        <div className="cd-block-modal-list">
                          {blockCoaches.map(c => {
                            const photo = coachPhotoMap[c.id] || c.photo_url;
                            return (
                            <div key={c.id} className="cd-block-modal-row">
                              <div className="cd-attendance-name">
                                <div className="cd-avatar-sm" style={photo ? { padding: 0, overflow: 'hidden' } : { background: '#faf9fe', color: '#8b5cf6' }}>
                                  {photo
                                    ? <img src={photo} alt={c.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                    : getInitials(c.name)}
                                </div>
                                <span>{c.name}</span>
                              </div>
                              <button className="cd-block-remove-btn" onClick={() => handleRemoveCoachFromBlock(c.id)} title="Remover">
                                <MinusCircle size={16} />
                              </button>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Search + Add Coaches */}
                    <div className="cd-block-modal-section">
                      <div className="cd-block-modal-section-title">
                        <PlusCircle size={14} />
                        <span>Agregar Entrenadores</span>
                      </div>
                      <div className="cd-search" style={{ marginBottom: 8 }}>
                        <Search size={16} />
                        <input type="text" placeholder="Buscar entrenador..." value={blockCoachSearch} onChange={e => setBlockCoachSearch(e.target.value)} />
                      </div>
                      <div className="cd-block-modal-list">
                        {availableCoachesForBlock.length === 0 ? (
                          <div className="cd-block-modal-empty">
                            {blockCoachSearch ? 'Sin resultados' : 'Todos los entrenadores ya están asignados'}
                          </div>
                        ) : (
                          availableCoachesForBlock.map(c => {
                            const photo = coachPhotoMap[c.id] || c.photo_url;
                            return (
                            <div key={c.id} className="cd-block-modal-row">
                              <div className="cd-attendance-name">
                                <div className="cd-avatar-sm" style={photo ? { padding: 0, overflow: 'hidden' } : { background: '#faf9fe', color: '#8b5cf6' }}>
                                  {photo
                                    ? <img src={photo} alt={c.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                    : getInitials(c.name)}
                                </div>
                                <span>{c.name}</span>
                              </div>
                              <button className="cd-block-add-btn" onClick={() => handleAddCoachToBlock(c)} title="Agregar">
                                <PlusCircle size={16} />
                              </button>
                            </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ============ REPORTS ============ */}
          {activePage === 'reports' && (
            <>
              <p className="cd-page-subtitle"><FileBarChart size={16} />Genera PDFs imprimibles de asistencia, pagos, membresías, copas y analítica.</p>

              {/* Shared date/month controls */}
              <div className="cd-card" style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Calendar size={15} style={{ color: '#64748b' }} />
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#3f3f46' }}>Fecha:</label>
                  <input type="date" value={attendanceDate} onChange={e => setAttendanceDate(e.target.value)} className="cd-level-filter" style={{ maxWidth: 165 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CalendarDays size={15} style={{ color: '#64748b' }} />
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#3f3f46' }}>Mes:</label>
                  <select value={reportMonth} onChange={e => setReportMonth(parseInt(e.target.value))} className="cd-level-filter" style={{ maxWidth: 130 }}>
                    {[...Array(12)].map((_, i) => <option key={i} value={i + 1}>{new Date(2000, i, 1).toLocaleDateString('es', { month: 'long' })}</option>)}
                  </select>
                  <select value={reportYear} onChange={e => setReportYear(parseInt(e.target.value))} className="cd-level-filter" style={{ maxWidth: 95 }}>
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              {/* ── Calendario ── */}
              <div className="cd-card" style={{ marginBottom: 16 }}>
                <div
                  onClick={handlePrintWeeklyCalendar}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                    borderRadius: 10, background: '#faf9fe', border: '1px solid #f1f0f9',
                    cursor: 'pointer', transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f3f0ff'}
                  onMouseLeave={e => e.currentTarget.style.background = '#faf9fe'}
                >
                  <CalendarDays size={16} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: '#18181b' }}>Calendario Semanal</p>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>Bloques de clase por día y hora</p>
                  </div>
                  <ChevronRight size={16} style={{ color: '#c4b5fd', flexShrink: 0 }} />
                </div>
              </div>

              {/* ── Atletas ── */}
              <div className="cd-card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 10, borderBottom: '2px solid #ede9fe' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Users size={17} style={{ color: '#8b5cf6' }} />
                  </div>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#3f3f46' }}>Atletas</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { label: 'Asistencia Diaria', desc: `Listado por bloque — ${new Date(attendanceDate + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}`, icon: ClipboardCheck, color: '#14b8a6', action: () => handlePrintDailyAttendance() },
                    { label: 'Resumen Semanal', desc: `Asistencia por día — semana del ${new Date(attendanceDate + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}`, icon: BarChart3, color: '#0ea5e9', action: () => handlePrintWeeklyAttendance() },
                    { label: 'Promedios Mensuales', desc: `% asistencia por atleta — ${new Date(2000, reportMonth - 1, 1).toLocaleDateString('es', { month: 'long' })} ${reportYear}`, icon: TrendingUp, color: '#f59e0b', action: () => handlePrintMonthlyAverages() },
                  ].map((item, i) => (
                    <div key={i}
                      onClick={item.action}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                        borderRadius: 10, background: '#faf9fe', border: '1px solid #f1f0f9',
                        cursor: 'pointer', transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f3f0ff'}
                      onMouseLeave={e => e.currentTarget.style.background = '#faf9fe'}
                    >
                      <item.icon size={16} style={{ color: item.color, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: '#18181b' }}>{item.label}</p>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>{item.desc}</p>
                      </div>
                      <ChevronRight size={16} style={{ color: '#c4b5fd', flexShrink: 0 }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Entrenadores ── */}
              <div className="cd-card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 10, borderBottom: '2px solid #fce7f3' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fce7f3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Dumbbell size={17} style={{ color: '#db2777' }} />
                  </div>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#3f3f46' }}>Entrenadores</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { label: 'Asistencia Diaria', desc: `Entrenadores presentes — ${new Date(attendanceDate + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}`, icon: ClipboardCheck, color: '#db2777', action: () => handlePrintCoachDailyAttendance() },
                    { label: 'Resumen Semanal', desc: `Asistencia semanal — semana del ${new Date(attendanceDate + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}`, icon: BarChart3, color: '#db2777', action: () => handlePrintCoachWeeklyAttendance() },
                    { label: 'Promedios Mensuales', desc: `Asistencia mensual — ${new Date(2000, reportMonth - 1, 1).toLocaleDateString('es', { month: 'long' })} ${reportYear}`, icon: TrendingUp, color: '#db2777', action: () => handlePrintCoachMonthlyAttendance() },
                    { label: 'QR de Asistencia', desc: 'Código QR para imprimir y pegar en la entrada', icon: QrCode, color: '#8b5cf6', action: () => handlePrintCoachQR() },
                  ].map((item, i) => (
                    <div key={i}
                      onClick={item.action}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                        borderRadius: 10, background: '#fdf2f8', border: '1px solid #fce7f3',
                        cursor: 'pointer', transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fce7f3'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fdf2f8'}
                    >
                      <item.icon size={16} style={{ color: item.color, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: '#18181b' }}>{item.label}</p>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>{item.desc}</p>
                      </div>
                      <ChevronRight size={16} style={{ color: '#f9a8d4', flexShrink: 0 }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Finanzas ── */}
              <div className="cd-card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 10, borderBottom: '2px solid #d1fae5' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Wallet size={17} style={{ color: '#059669' }} />
                  </div>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#3f3f46' }}>Finanzas</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { label: 'Pagos del Día', desc: `Detalle y total — ${new Date(attendanceDate + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}`, icon: Banknote, color: '#10b981', action: handlePrintPagosDia },
                    { label: 'Mensualidades del Mes', desc: `Pagos agrupados por atleta — ${new Date(2000, reportMonth - 1, 1).toLocaleDateString('es', { month: 'long' })} ${reportYear}`, icon: CreditCard, color: '#0ea5e9', action: handlePrintMensualidades },
                    { label: 'Membresías Activas', desc: 'Listado + distribución por plan e ingreso potencial', icon: Users, color: '#8b5cf6', action: handlePrintMembresias },
                    { label: 'Atletas con Deuda', desc: 'Morosos y próximos a vencer con monto adeudado', icon: AlertTriangle, color: '#dc2626', action: handlePrintDeudores },
                  ].map((item, i) => (
                    <div key={i}
                      onClick={item.action}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                        borderRadius: 10, background: '#f0fdf4', border: '1px solid #d1fae5',
                        cursor: 'pointer', transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#dcfce7'}
                      onMouseLeave={e => e.currentTarget.style.background = '#f0fdf4'}
                    >
                      <item.icon size={16} style={{ color: item.color, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: '#18181b' }}>{item.label}</p>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>{item.desc}</p>
                      </div>
                      <ChevronRight size={16} style={{ color: '#86efac', flexShrink: 0 }} />
                    </div>
                  ))}
                </div>
              </div>

            </>
          )}

                  {/* ============ COACHES ============ */}
          {activePage === 'coaches' && (
            <>
              <p className="cd-page-subtitle"><Dumbbell size={16} />Gestiona tus entrenadores, revisa solicitudes y genera el QR de asistencia.</p>

              {/* Tabs — modern underlined */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                {[
                  { id: 'hired', label: `Autorizados (${approvedCoaches.length})`, icon: Users, onClick: () => setCoachesTab('hired') },
                  { id: 'requests', label: `Solicitudes (${coachRequests.filter(r => r.status === 'pending').length})`, icon: Clock, onClick: () => setCoachesTab('requests') },
                  { id: 'applicants', label: `Postulaciones (${jobApplications.length})`, icon: Mail, onClick: () => { setCoachesTab('applicants'); loadJobApplications(); } },
                  { id: 'looking', label: `Busco Club (${coachesLooking.length})`, icon: Search, onClick: () => { setCoachesTab('looking'); if (coachesLooking.length === 0) loadCoachesLookingForClub(); } },
                  { id: 'myjobs', label: `Mis Vacantes (${clubJobPostings.length})`, icon: Briefcase, onClick: () => { setCoachesTab('myjobs'); loadClubJobPostings(); } },
                ].map(tab => {
                  const isActive = coachesTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={tab.onClick}
                      style={{
                        background: 'transparent',
                        color: isActive ? '#8b5cf6' : '#64748b',
                        border: 'none',
                        padding: '10px 16px',
                        marginBottom: -1,
                        borderBottom: isActive ? '2px solid #8b5cf6' : '2px solid transparent',
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'color 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#0f172a'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#64748b'; }}
                    >
                      <tab.icon size={15} /> {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Tab: Autorizados (solo entrenadores aprobados) */}
              {coachesTab === 'hired' && (
                approvedCoaches.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {approvedCoaches.map(item => {
                      const coach = item.coaches || {};
                      const coachId = coach.id || item.id;
                      return (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                          <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', background: '#f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {coachPhotoMap[coachId] || coach.photo_url ? (
                              <img src={coachPhotoMap[coachId] || coach.photo_url} alt={coach.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: '1.25rem', fontWeight: 600, color: '#64748b' }}>{getInitials(coach.name)}</span>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1e293b', marginBottom: 4 }}>{coach.name || 'Entrenador'}</div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 4 }}>{coach.role || item.role || 'Entrenador/a'}</div>
                            {coach.specialties && (() => {
                              const specs = Array.isArray(coach.specialties) ? coach.specialties : [coach.specialties];
                              return specs.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                  {specs.slice(0, 3).map((specKey, i) => {
                                    const tab = DISCIPLINE_TABS.find(t => t.key === specKey);
                                    return (
                                      <span key={i} style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {tab?.logo && <img src={tab.logo} alt="" style={{ width: 12, height: 12 }} />}
                                        {tab?.fullName || specKey}
                                      </span>
                                    );
                                  })}
                                  {specs.length > 3 && <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem' }}>+{specs.length - 3}</span>}
                                </div>
                              ) : null;
                            })()}
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                            {/* Toggle Análisis IA per-coach. Solo habilitado si
                                el club tiene el switch maestro activado. */}
                            {(() => {
                              const masterEnabled = !!clubInfo?.pose_analysis_enabled;
                              const coachEnabled = !!item.pose_analysis_enabled;
                              const disabled = !masterEnabled;
                              const title = disabled
                                ? 'Contacta al administrador de la plataforma para habilitar Análisis IA en tu club'
                                : coachEnabled
                                  ? 'Desactivar Análisis IA para este entrenador'
                                  : 'Activar Análisis IA para este entrenador';
                              return (
                                <button
                                  type="button"
                                  onClick={() => !disabled && handleToggleCoachPoseAnalysis(item.id, !coachEnabled)}
                                  title={title}
                                  disabled={disabled}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '8px 12px', borderRadius: 8,
                                    border: `1px solid ${coachEnabled && !disabled ? '#8b5cf6' : '#d1d5db'}`,
                                    background: disabled ? '#f3f4f6' : coachEnabled ? '#ede9fe' : 'white',
                                    color: disabled ? '#9ca3af' : coachEnabled ? '#6d28d9' : '#6b7280',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    fontWeight: 600, fontSize: '0.85rem',
                                  }}
                                >
                                  <Sparkles size={14} /> {coachEnabled ? 'IA Activa' : 'Activar IA'}
                                </button>
                              );
                            })()}
                            <button className="btn btn-primary" onClick={() => setCoachProfileModal(coach)} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                              <User size={14} /> Ver perfil
                            </button>
                            {coach.cv_url && (
                              <button className="btn" onClick={() => setCoachCvModal(coach.cv_url)} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                <FileText size={14} /> CV
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="cd-empty-state">
                    <Dumbbell size={48} />
                    <p>No tienes entrenadores autorizados aún.</p>
                    <span>Las solicitudes de entrenadores aparecerán en la pestaña "Solicitudes"</span>
                  </div>
                )
              )}

              {/* Tab: Solicitudes Pendientes - Diseño Minimalista */}
              {coachesTab === 'requests' && (
                loadingRequestPhotos ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                    <span className="loading-spinner" /> Cargando solicitudes...
                  </div>
                ) : coachRequests.filter(r => r.status === 'pending').length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {coachRequests.filter(r => r.status === 'pending').map(req => {
                      const coach = req.coaches || {};
                      const coachId = coach?.id || req.coach_id;
                      console.log('[Render] coachId:', coachId, 'coachData:', coachRequestData[coachId], 'photo:', coachPhotoMap[coachId]);
                      const coachData = coachRequestData[coachId] || {};
                      return (
                        <div key={req.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                          <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', background: '#f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {coachPhotoMap[coachId] || coachData.photo_url ? (
                              <img src={coachPhotoMap[coachId] || coachData.photo_url} alt={coachData.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: '1.25rem', fontWeight: 600, color: '#64748b' }}>{getInitials(coachData.name || coachData.email || 'E')}</span>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1e293b', marginBottom: 4 }}>{coachData.name || coachData.email || 'Entrenador'}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#64748b' }}>
                              <Calendar size={12} />
                              <span>Solicitud: {req.requested_at ? new Date(req.requested_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Reciente'}</span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                            <button className="btn btn-primary" onClick={() => setCoachProfileModal(coachData)} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                              <User size={14} /> Ver perfil
                            </button>
                            <button 
                              className="btn btn-success"
                              onClick={() => handleApproveCoach(req.id)}
                              disabled={coachActionLoading === req.id}
                              style={{ fontSize: '0.85rem', padding: '8px 12px' }}
                            >
                              <CheckCircle size={14} /> Aprobar
                            </button>
                            <button 
                              className="btn"
                              onClick={() => handleRejectCoach(req.id)}
                              disabled={coachActionLoading === req.id}
                              style={{ fontSize: '0.85rem', padding: '8px 12px' }}
                            >
                              <XCircle size={14} /> Rechazar
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="cd-empty-state">
                    <Clock size={48} />
                    <p>No hay solicitudes pendientes</p>
                    <span>Cuando un entrenador solicite acceso, aparecerá aquí</span>
                  </div>
                )
              )}

              {/* Tab: Postulaciones */}
              {coachesTab === 'applicants' && (
                loadingApplications ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                    <span className="loading-spinner" /> Cargando...
                  </div>
                ) : jobApplications.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {jobApplications.map(app => {
                      const coach = app.coaches || {};
                      const job = app.job_postings || {};
                      const contactStatus = coachContactStatus[coach.id];
                      const isPending = app.status === 'pending';
                      const isAccepted = app.status === 'accepted';
                      const isRejected = app.status === 'rejected';
                      const isContacted = app.status === 'contacted';
                      const isRevealed = app.status === 'revealed';
                      const isAppConfirmed = contactStatus === 'confirmed';
                      return (
                        <div key={app.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                          <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', background: '#f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {coachPhotoMap[coach.id] || coach.photo_url ? (
                              <img src={coachPhotoMap[coach.id] || coach.photo_url} alt={coach.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: '1.25rem', fontWeight: 600, color: '#64748b' }}>{getInitials(coach.name || 'E')}</span>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1e293b', marginBottom: 4 }}>{coach.name || 'Entrenador'}</div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 4 }}>Vacante: {job.title || 'N/A'}</div>
                            {coach.specialties && (() => {
                              const specs = Array.isArray(coach.specialties) ? coach.specialties : [coach.specialties];
                              return specs.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                  {specs.slice(0, 3).map((specKey, i) => {
                                    const tab = DISCIPLINE_TABS.find(t => t.key === specKey);
                                    return (
                                      <span key={i} style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {tab?.logo && <img src={tab.logo} alt="" style={{ width: 12, height: 12 }} />}
                                        {tab?.fullName || specKey}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : null;
                            })()}
                            {app.message && <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>{app.message}</p>}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                            {isAccepted && !isAppConfirmed && <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>Nueva postulación</span>}
                            {isRejected && <span style={{ background: '#fee2e2', color: '#dc2626', padding: '4px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>No procede</span>}
                            {isContacted && <span style={{ background: '#dbeafe', color: '#2563eb', padding: '4px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>Esperando</span>}
                            {isAppConfirmed && <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>Contacto agregado</span>}
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="btn btn-primary" onClick={() => setCoachProfileModal(coach)} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                <User size={14} /> Ver perfil
                              </button>
                              {coach.cv_url && (
                                <button className="btn" onClick={() => setCoachCvModal(coach.cv_url)} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                  <FileText size={14} /> CV
                                </button>
                              )}
                              {isPending && (
                                <>
                                  <button className="btn btn-success" onClick={() => handleAcceptApplication(app.id)} disabled={applicationActionLoading === app.id} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                    <CheckCircle size={14} /> Aceptar
                                  </button>
                                  <button className="btn" onClick={() => handleRejectApplication(app.id)} disabled={applicationActionLoading === app.id} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                    <XCircle size={14} /> Rechazar
                                  </button>
                                </>
                              )}
                              {(isAccepted && !isAppConfirmed) && (
                                <button className="btn btn-primary" onClick={() => handleContactFromApplication(app)} disabled={contactingCoach === coach.id} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                  <Mail size={14} /> Contactar
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="cd-empty-state">
                    <Mail size={48} />
                    <p>No hay postulaciones aún.</p>
                    <span>Crea una vacante para recibir postulaciones de entrenadores</span>
                  </div>
                )
              )}

              {/* Tab: Buscando Empleo */}
              {coachesTab === 'looking' && (
                coachesLooking.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {coachesLooking.map(coach => {
                      const contactStatus = coachContactStatus[coach.id];
                      const isConfirmed = contactStatus === 'confirmed';
                      const isPending = contactStatus === 'pending';
                      return (
                        <div key={coach.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                          <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', background: '#f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {coachPhotoMap[coach.id] || coach.photo_url ? (
                              <img src={coachPhotoMap[coach.id] || coach.photo_url} alt={coach.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: '1.25rem', fontWeight: 600, color: '#64748b' }}>{getInitials(coach.name)}</span>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1e293b', marginBottom: 4 }}>{coach.name}</div>
                            <div style={{ display: 'flex', gap: 12, fontSize: '0.85rem', color: '#64748b', marginBottom: 4 }}>
                              {coach.location && <span><MapPin size={12} /> {coach.location}</span>}
                              {coach.experience_years && <span><Briefcase size={12} /> {coach.experience_years} años exp.</span>}
                            </div>
                            {coach.specialties && (() => {
                              const specs = Array.isArray(coach.specialties) ? coach.specialties : [coach.specialties];
                              return specs.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                  {specs.slice(0, 3).map((specKey, i) => {
                                    const tab = DISCIPLINE_TABS.find(t => t.key === specKey);
                                    return (
                                      <span key={i} style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {tab?.logo && <img src={tab.logo} alt="" style={{ width: 12, height: 12 }} />}
                                        {tab?.fullName || specKey}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : null;
                            })()}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                            {isConfirmed ? (
                              <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>Contacto Agregado</span>
                            ) : isPending ? (
                              <span style={{ background: '#fef3c7', color: '#d97706', padding: '4px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>Pendiente</span>
                            ) : (
                              <button className="btn btn-primary" onClick={() => handleRequestContact(coach.id)} disabled={contactingCoach === coach.id} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                <Mail size={14} /> {contactingCoach === coach.id ? 'Enviando...' : 'Contactar'}
                              </button>
                            )}
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="btn btn-primary" onClick={() => setCoachProfileModal(coach)} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                <User size={14} /> Ver perfil
                              </button>
                              {coach.cv_url && (
                                <button className="btn" onClick={() => setCoachCvModal(coach.cv_url)} style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                  <FileText size={14} /> CV
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="cd-empty-state">
                    <Search size={48} />
                    <p>No hay entrenadores buscando empleo en este momento.</p>
                  </div>
                )
              )}

              {/* Tab: Mis Vacantes */}
              {coachesTab === 'myjobs' && (
                <>
                  {/* Botón Crear Vacante dentro de la tab */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    <button 
                      className="btn btn-club-primary btn-sm" 
                      onClick={() => { 
                        setEditingJob(null); 
                        setJobForm({ title: '', description: '', disciplines: [], requirements: '', location: '', salary_range: '' }); 
                        setCoachesTab('create');
                      }}
                    >
                      <Plus size={14} /> Crear Vacante
                    </button>
                  </div>

                  {loadingClubJobs ? (
                    <div className="cd-loading"><div className="cd-spinner"></div><p>Cargando...</p></div>
                  ) : clubJobPostings.length > 0 ? (
                    <div className="cd-card">
                      <div className="cd-card-header"><Briefcase size={16} /> Mis Vacantes ({clubJobPostings.length})</div>
                      <div className="cd-coaches-list">
                        {clubJobPostings.map(job => (
                          <div key={job.id} className="cd-coach-card">
                            <div className="cd-coach-card-header">
                              <div style={{ flex: 1 }}>
                                <strong>{job.title}</strong>
                                <div className="cd-coach-meta">
                                  {job.location && <span><MapPin size={11} /> {job.location}</span>}
                                  {job.salary_range && <span><Award size={11} /> {job.salary_range}</span>}
                                </div>
                                {job.disciplines && job.disciplines.length > 0 && (
                                  <div className="cd-coach-specialties">
                                    {job.disciplines.map((specKey, i) => {
                                      const tab = DISCIPLINE_TABS.find(t => t.key === specKey);
                                      return (
                                        <span key={i} className="cd-coach-spec-tag">
                                          {tab?.logo && <img src={tab.logo} alt="" />}
                                          {tab?.fullName || specKey}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                                <span className={job.is_active ? 'cd-badge cd-badge-green' : 'cd-badge'}>{job.is_active ? 'Vacante Activa' : 'Vacante Inactiva'}</span>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button 
                                    className="btn btn-club-primary btn-sm" 
                                    onClick={() => handleEditJob(job)}
                                  >
                                    <Edit2 size={13} /> Editar
                                  </button>
                                  <button 
                                    className="btn btn-club-secondary btn-sm" 
                                    onClick={async () => {
                                      const ok = await confirm({ title: 'Eliminar Vacante', message: '¿Eliminar esta vacante?', type: 'danger', confirmText: 'Eliminar' });
                                      if (!ok) return;
                                      try {
                                        const { error } = await db('job_postings').delete().eq('id', job.id).execute();
                                        if (error) throw new Error(error);
                                        notify.success('Vacante eliminada');
                                        loadClubJobPostings();
                                      } catch (err) { notify.error('Error al eliminar'); }
                                    }}
                                  >
                                    <Trash2 size={12} /> Eliminar
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="cd-empty-state">
                      <Briefcase size={48} />
                      <p>No has creado ninguna vacante todavía.</p>
                      <button className="btn btn-club-primary" onClick={() => { setCoachesTab('create'); setEditingJob(null); setJobForm({ title: '', description: '', disciplines: [], requirements: '', location: '', salary_range: '' }); }}>
                        <Plus size={16} /> Crear Vacante
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Tab: Crear/Editar Vacante (modal dentro de la misma página) */}
              {coachesTab === 'create' && (
                <div className="cd-card" style={{ marginTop: 16 }}>
                  <div className="cd-card-header">
                    <Briefcase size={16} /> {editingJob ? 'Editar Vacante' : 'Crear Nueva Vacante'}
                    <button className="btn btn-club-secondary btn-sm" onClick={() => { setCoachesTab('myjobs'); setEditingJob(null); }}>
                      <X size={14} /> Cancelar
                    </button>
                  </div>
                  <div style={{ padding: 16 }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem' }}>Título de la Vacante *</label>
                      <input
                        type="text"
                        value={jobForm.title}
                        onChange={e => setJobForm({ ...jobForm, title: e.target.value })}
                        placeholder="Ej: Entrenador de Gimnasia Artística"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.95rem' }}
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem' }}>Descripción</label>
                      <textarea
                        value={jobForm.description}
                        onChange={e => setJobForm({ ...jobForm, description: e.target.value })}
                        placeholder="Describe las funciones y responsabilidades del puesto..."
                        rows={4}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.95rem', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem' }}>Requisitos</label>
                      <textarea
                        value={jobForm.requirements}
                        onChange={e => setJobForm({ ...jobForm, requirements: e.target.value })}
                        placeholder="Requisitos y cualificaciones..."
                        rows={3}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.95rem', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem' }}>Ubicación</label>
                        <input
                          type="text"
                          value={jobForm.location}
                          onChange={e => setJobForm({ ...jobForm, location: e.target.value })}
                          placeholder="Ciudad, Barrio..."
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.95rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem' }}>Rango Salarial</label>
                        <input
                          type="text"
                          value={jobForm.salary_range}
                          onChange={e => setJobForm({ ...jobForm, salary_range: e.target.value })}
                          placeholder="Ej: $50,000 - $80,000"
                          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.95rem' }}
                        />
                      </div>
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem' }}>Disciplinas *</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {DISCIPLINE_TABS.map(tab => (
                          <label key={tab.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 20, border: jobForm.disciplines.includes(tab.key) ? '2px solid #8b5cf6' : '1px solid #e2e8f0', background: jobForm.disciplines.includes(tab.key) ? '#f5f3ff' : 'white', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={jobForm.disciplines.includes(tab.key)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setJobForm({ ...jobForm, disciplines: [...jobForm.disciplines, tab.key] });
                                } else {
                                  setJobForm({ ...jobForm, disciplines: jobForm.disciplines.filter(d => d !== tab.key) });
                                }
                              }}
                              style={{ display: 'none' }}
                            />
                            {tab.logo && <img src={tab.logo} alt="" style={{ width: 18, height: 18 }} />}
                            <span style={{ fontSize: '0.85rem' }}>{tab.fullName}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button
                        className="btn btn-club-primary"
                        onClick={async () => {
                          if (!jobForm.title.trim()) { notify.error('El título es obligatorio'); return; }
                          if (jobForm.disciplines.length === 0) { notify.error('Selecciona al menos una disciplina'); return; }
                          try {
                            const clubId = getClubId();
                            if (!clubId) { notify.error('Error del club'); return; }
                            if (editingJob) {
                              const { error } = await db('job_postings').update({
                                title: jobForm.title,
                                description: jobForm.description,
                                requirements: jobForm.requirements,
                                location: jobForm.location,
                                salary_range: jobForm.salary_range,
                                disciplines: jobForm.disciplines
                              }).eq('id', editingJob.id).execute();
                              if (error) throw new Error(error);
                              notify.success('Vacante actualizada');
                            } else {
                              const { error } = await db('job_postings').insert({
                                club_id: clubId,
                                title: jobForm.title,
                                description: jobForm.description,
                                requirements: jobForm.requirements,
                                location: jobForm.location,
                                salary_range: jobForm.salary_range,
                                disciplines: jobForm.disciplines,
                                is_active: true
                              }).execute();
                              if (error) throw new Error(error);
                              notify.success('Vacante creada');
                            }
                            setJobForm({ title: '', description: '', disciplines: [], requirements: '', location: '', salary_range: '' });
                            setEditingJob(null);
                            setCoachesTab('myjobs');
                            loadClubJobPostings();
                          } catch (err) { notify.error('Error al guardar: ' + err.message); }
                        }}
                        style={{ flex: 1 }}
                      >
                        <Save size={16} /> {editingJob ? 'Actualizar' : 'Publicar'} Vacante
                      </button>
                      <button
                        className="btn btn-club-secondary"
                        onClick={() => { setCoachesTab('myjobs'); setEditingJob(null); setJobForm({ title: '', description: '', disciplines: [], requirements: '', location: '', salary_range: '' }); }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ============ FINANZAS ============ */}
          {activePage === 'finanzas' && (
            <div className="cd-finance-view">
              <p className="cd-page-subtitle"><Wallet size={16} />Administra membresías, planes, pagos del día y alertas de deuda de tus atletas.</p>
              
              <div
                className="cd-finance-tabs"
                style={{
                  display: 'flex',
                  gap: 4,
                  marginBottom: 20,
                  borderBottom: '1px solid #e2e8f0',
                  flexWrap: 'wrap',
                }}
              >
                {[
                  { id: 'planes', label: 'Planes', icon: CreditCard },
                  { id: 'memberships', label: 'Membresías', icon: Users },
                  { id: 'pagos', label: 'Pagos', icon: Banknote },
                  { id: 'alertas', label: 'Alertas', icon: AlertTriangle },
                ].map(tab => {
                  const isActive = finanzasActiveTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setFinanzasActiveTab(tab.id)}
                      style={{
                        background: 'transparent',
                        color: isActive ? '#8b5cf6' : '#64748b',
                        border: 'none',
                        padding: '10px 16px',
                        marginBottom: -1,
                        borderBottom: isActive ? '2px solid #8b5cf6' : '2px solid transparent',
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'color 0.15s, border-color 0.15s',
                        position: 'relative',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#0f172a'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#64748b'; }}
                    >
                      <tab.icon size={15} /> {tab.label}
                      {tab.id === 'alertas' && alertasDeuda.length > 0 && (
                        <span style={{
                          background: '#dc2626', color: 'white',
                          borderRadius: 10, padding: '1px 7px',
                          fontSize: '0.68rem', fontWeight: 700,
                          minWidth: 18, textAlign: 'center',
                        }}>
                          {alertasDeuda.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {financiaLoading && <div className="cd-loading"><div className="cd-spinner"></div><p>Cargando...</p></div>}

              {!financiaLoading && finanzasActiveTab === 'planes' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Planes de Pago</span>
                    <button className="btn btn-club-primary btn-sm" onClick={() => { setEditingPlan(null); setShowPlanModal(true); }}>
                      <Plus size={14} /> Nuevo Plan
                    </button>
                  </div>
                  {clubPlanes.length === 0 ? (
                    <div className="cd-empty-state"><CreditCard size={40} /><p>No hay planes</p></div>
                  ) : (
                    <div className="cd-table-wrap">
                      <table className="cd-table">
                        <thead><tr><th>Plan</th><th>Precio</th><th>Frecuencia</th><th>Días Gracia</th><th>Acciones</th></tr></thead>
                        <tbody>
                          {clubPlanes.map(plan => (
                            <tr key={plan.id}>
                              <td style={{ fontWeight: 600 }}>{plan.name}</td>
                              <td>CLP $ {Number(plan.price).toLocaleString('es-CL')}</td>
                              <td style={{ textTransform: 'capitalize' }}>{plan.frequency}</td>
                              <td>{plan.grace_days} días</td>
                              <td>
                                <button className="cd-action-btn" onClick={() => { setEditingPlan(plan); setShowPlanModal(true); }}><Edit2 size={14} /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {!financiaLoading && finanzasActiveTab === 'memberships' && (() => {
                const hasFilter = membershipSearch.trim() !== '' || membershipStatusFilter !== '';
                const filteredMemberships = hasFilter
                  ? clubMemberships.filter(m => {
                      const nameMatch = !membershipSearch.trim() || (m.gimnasts?.name || '').toLowerCase().includes(membershipSearch.toLowerCase());
                      const statusMatch = !membershipStatusFilter || m.status === membershipStatusFilter;
                      return nameMatch && statusMatch;
                    })
                  : [];
                return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Atletas con Plan ({clubMemberships.length})</span>
                    <button className="btn btn-club-primary btn-sm" onClick={() => setShowAssignModal(true)}>
                      <Plus size={14} /> Asignar Plan
                    </button>
                  </div>

                  {/* Filtros */}
                  <div className="cd-athlete-filters" style={{ marginBottom: 16 }}>
                    <div className="cd-search">
                      <Search size={16} />
                      <input type="text" placeholder="Buscar por atleta..." value={membershipSearch} onChange={e => setMembershipSearch(e.target.value)} />
                    </div>
                    <select className="cd-level-filter" value={membershipStatusFilter} onChange={e => setMembershipStatusFilter(e.target.value)}>
                      <option value="">Todos los estados</option>
                      <option value="active">Activa</option>
                      <option value="paused">Pausada</option>
                      <option value="cancelled">Cancelada</option>
                    </select>
                  </div>

                  {!hasFilter ? (
                    <div className="cd-empty-state" style={{ padding: '48px 24px' }}>
                      <Users size={48} style={{ color: '#cbd5e1' }} />
                      <p style={{ fontSize: '1rem', fontWeight: 600, margin: '12px 0 6px' }}>
                        {clubMemberships.length} membresía{clubMemberships.length === 1 ? '' : 's'}
                      </p>
                      <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        Usa el buscador o filtro de estado para ver el listado
                      </span>
                    </div>
                  ) : clubMemberships.length === 0 ? (
                    <div className="cd-empty-state"><Users size={40} /><p>Sin membresías</p></div>
                  ) : filteredMemberships.length === 0 ? (
                    <div className="cd-empty-state"><Users size={40} /><p>Sin resultados</p></div>
                  ) : (
                    <div className="cd-table-wrap">
                      <table className="cd-table">
                        <thead><tr><th>Atleta</th><th>Plan</th><th>Precio</th><th>Estado</th><th>Acción</th></tr></thead>
                        <tbody>
                          {filteredMemberships.map(m => (
                            <tr key={m.id}>
                              <td style={{ fontWeight: 600 }}>{m.gimnasts?.name || '—'}</td>
                              <td>{m.club_planes?.name || '—'}</td>
                              <td>CLP $ {Number(m.club_planes?.price || 0).toLocaleString('es-CL')}</td>
                              <td><span className={`cd-badge ${m.status === 'active' ? 'cd-badge-green' : 'cd-badge-gray'}`}>{m.status === 'active' ? 'Activa' : m.status === 'cancelled' ? 'Cancelada' : m.status === 'paused' ? 'Pausada' : m.status}</span></td>
                              <td>
                                <button className="btn btn-club-primary btn-xs" onClick={() => { setSelectedMembership(m); setPagoForm({ amount: m.club_planes?.price || 0, payment_method: 'efectivo', is_advance: false }); setShowPagoModal(true); }}>
                                  Registrar Pago
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                );
              })()}

              {!financiaLoading && finanzasActiveTab === 'pagos' && (() => {
                const filteredPagos = clubPagos.filter(p => {
                  if (pagosFilterAthlete && !(p.gimnasts?.name || '').toLowerCase().includes(pagosFilterAthlete.toLowerCase())) return false;
                  if (pagosFilterMethod && (p.payment_method || 'efectivo') !== pagosFilterMethod) return false;
                  if (pagosFilterFrom && p.payment_date && p.payment_date < pagosFilterFrom) return false;
                  if (pagosFilterTo && p.payment_date && p.payment_date > pagosFilterTo) return false;
                  return true;
                });
                const totalFiltered = filteredPagos.reduce((s, p) => s + Number(p.amount || 0), 0);
                const hasFilters = pagosFilterAthlete || pagosFilterMethod || pagosFilterFrom || pagosFilterTo;
                return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>Historial de Pagos</span>
                    <div style={{ display: 'flex', gap: 12, fontSize: '0.82rem', color: '#475569' }}>
                      <span><strong style={{ color: '#0f172a' }}>{filteredPagos.length}</strong> pagos</span>
                      <span>·</span>
                      <span>Total: <strong style={{ color: '#059669' }}>CLP $ {totalFiltered.toLocaleString('es-CL')}</strong></span>
                    </div>
                  </div>

                  {/* Filtros */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(180px, 1fr) 160px 160px 140px auto',
                    gap: 8, marginBottom: 14,
                  }}>
                    <div style={{ position: 'relative' }}>
                      <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                      <input
                        type="text"
                        placeholder="Buscar por atleta..."
                        value={pagosFilterAthlete}
                        onChange={e => setPagosFilterAthlete(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                      />
                    </div>
                    <input
                      type="date"
                      value={pagosFilterFrom}
                      onChange={e => setPagosFilterFrom(e.target.value)}
                      title="Desde"
                      style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                    />
                    <input
                      type="date"
                      value={pagosFilterTo}
                      onChange={e => setPagosFilterTo(e.target.value)}
                      title="Hasta"
                      style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                    />
                    <select
                      value={pagosFilterMethod}
                      onChange={e => setPagosFilterMethod(e.target.value)}
                      style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem', background: 'white' }}
                    >
                      <option value="">Todos los métodos</option>
                      <option value="efectivo">Efectivo</option>
                      <option value="transferencia">Transferencia</option>
                      <option value="debito">Débito</option>
                      <option value="credito">Crédito</option>
                    </select>
                    <button
                      onClick={() => { setPagosFilterAthlete(''); setPagosFilterMethod(''); setPagosFilterFrom(''); setPagosFilterTo(''); }}
                      disabled={!hasFilters}
                      style={{
                        padding: '8px 12px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600,
                        border: '1px solid #e2e8f0',
                        background: hasFilters ? '#fef2f2' : '#f8fafc',
                        color: hasFilters ? '#dc2626' : '#94a3b8',
                        cursor: hasFilters ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Limpiar
                    </button>
                  </div>

                  {clubPagos.length === 0 ? (
                    <div className="cd-empty-state"><Banknote size={40} /><p>Sin pagos registrados</p></div>
                  ) : filteredPagos.length === 0 ? (
                    (() => {
                      const today = getTodayISO();
                      const isTodayOnly = pagosFilterFrom === today && pagosFilterTo === today && !pagosFilterAthlete && !pagosFilterMethod;
                      return (
                        <div className="cd-empty-state">
                          <Banknote size={40} style={{ color: '#cbd5e1' }} />
                          <p>{isTodayOnly ? 'Sin pagos hoy' : 'Sin resultados'}</p>
                          <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>
                            {isTodayOnly ? 'Aún no se han registrado pagos el día de hoy' : 'Ajusta los filtros para ver pagos'}
                          </span>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="cd-table-wrap">
                      <table className="cd-table">
                        <thead><tr><th>Fecha</th><th>Atleta</th><th>Monto</th><th>Método</th><th style={{ width: 60, textAlign: 'center' }}>Acciones</th></tr></thead>
                        <tbody>
                          {filteredPagos.map(p => (
                            <tr key={p.id}>
                              <td>{p.payment_date ? new Date(p.payment_date).toLocaleDateString('es-CL') : '—'}</td>
                              <td style={{ fontWeight: 600 }}>{p.gimnasts?.name || '—'}</td>
                              <td style={{ color: '#059669', fontWeight: 700 }}>CLP $ {Number(p.amount).toLocaleString('es-CL')}</td>
                              <td style={{ textTransform: 'capitalize' }}>{p.payment_method || 'efectivo'}</td>
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  title="Eliminar este pago"
                                  onClick={() => {
                                    setDeletePagoModal({
                                      id: p.id,
                                      athleteName: p.gimnasts?.name || 'este pago',
                                      amountStr: `CLP $ ${Number(p.amount).toLocaleString('es-CL')}`,
                                      dateStr: p.payment_date ? new Date(p.payment_date).toLocaleDateString('es-CL') : '',
                                      methodStr: p.payment_method || 'efectivo',
                                    });
                                  }}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 30, height: 30, borderRadius: 6,
                                    background: '#fef2f2', color: '#dc2626',
                                    border: '1px solid #fecaca', cursor: 'pointer',
                                  }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                );
              })()}

              {!financiaLoading && finanzasActiveTab === 'alertas' && (
                <div>
                  <div style={{ marginBottom: 16 }}><span style={{ fontWeight: 600 }}>Atletas con Deuda</span></div>
                  {alertasDeuda.length === 0 ? (
                    <div className="cd-empty-state" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                      <CheckCircle size={40} style={{ color: '#16a34a' }} />
                      <p style={{ color: '#166534' }}>¡Todo al día!</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {alertasDeuda.map((a, idx) => (
                        <div key={idx} className="cd-card" style={{ borderLeft: a.is_overdue ? '4px solid #dc2626' : '4px solid #f59e0b', background: '#fef2f2' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {a.is_overdue ? <AlertTriangle size={20} style={{ color: '#dc2626' }} /> : <Clock size={20} style={{ color: '#f59e0b' }} />}
                            <div style={{ flex: 1 }}>
                              <p style={{ margin: 0, fontWeight: 700 }}>{a.gymnast?.name || a.gimnasts?.name || '—'}</p>
                              <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                                Plan: {a.plan?.name} • {a.days_since_payment} días sin pagar
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Modal Plan */}
              {showPlanModal && (
                <div className="cd-modal-overlay" onClick={() => setShowPlanModal(false)}>
                  <div className="cd-modal" onClick={e => e.stopPropagation()}>
                    <div className="cd-modal-header">
                      <h3>{editingPlan?.id ? 'Editar Plan' : 'Nuevo Plan'}</h3>
                      <button onClick={() => setShowPlanModal(false)} className="cd-modal-close-btn"><X size={20} /></button>
                    </div>
                    <div className="cd-modal-body">
                      <input type="text" className="cd-form-input" placeholder="Nombre del Plan" value={editingPlan?.name || ''} onChange={e => setEditingPlan(p => ({ ...p, name: e.target.value }))} />
                      <input type="number" className="cd-form-input" placeholder="Precio en CLP (ej: 50000)" value={editingPlan?.price || ''} onChange={e => setEditingPlan(p => ({ ...p, price: Number(e.target.value) }))} />
                      <select className="cd-form-input" value={editingPlan?.frequency || 'mensual'} onChange={e => setEditingPlan(p => ({ ...p, frequency: e.target.value }))}>
                        <option value="quincenal">Quincenal (dos cuotas al mes)</option>
                        <option value="mensual">Mensual (una cuota al mes)</option>
                      </select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <label style={{ fontSize: '0.85rem', color: '#52525b', whiteSpace: 'nowrap', minWidth: 140 }}>Días por semana:</label>
                        <input type="number" className="cd-form-input" style={{ width: 70, textAlign: 'center' }} min="1" max="7" value={editingPlan?.days_per_week ?? 2} onChange={e => setEditingPlan(p => ({ ...p, days_per_week: Math.max(1, Math.min(7, Number(e.target.value) || 1)) }))} />
                        <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>máximo de días de entrenamiento a la semana</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <label style={{ fontSize: '0.85rem', color: '#52525b', whiteSpace: 'nowrap', minWidth: 140 }}>Días de gracia:</label>
                        <input type="number" className="cd-form-input" style={{ width: 70, textAlign: 'center' }} min="0" value={editingPlan?.grace_days ?? 3} onChange={e => setEditingPlan(p => ({ ...p, grace_days: Number(e.target.value) }))} />
                        <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>días para pagar después del vencimiento</span>
                      </div>
                    </div>
                    <div className="cd-modal-footer">
                      <button className="btn btn-club-secondary" onClick={() => setShowPlanModal(false)}>Cancelar</button>
                      <button className="btn btn-club-primary" onClick={async () => {
                        if (!editingPlan?.name || !editingPlan?.price) {
                          notify.warning('Nombre y precio son requeridos');
                          return;
                        }
                        const clubId = getClubId();
                        const data = { 
                          name: editingPlan.name, 
                          price: editingPlan.price, 
                          club_id: clubId, 
                          frequency: editingPlan.frequency || 'mensual',
                          grace_days: Math.max(0, editingPlan.grace_days || 3),
                          days_per_week: Math.max(1, Math.min(7, editingPlan.days_per_week || 2)),
                          is_active: true
                        };
                        let result;
                        if (editingPlan?.id) {
                          console.log('[PLAN] Using UPDATE, id:', editingPlan.id);
                          result = await clubPlanServiceProxy.update(editingPlan.id, data);
                        } else {
                          console.log('[PLAN] Using CREATE');
                          result = await clubPlanServiceProxy.create(data);
                        }
                        console.log('[PLAN] Result:', result);
                        if (result.error) {
                          notify.error('Error: ' + result.error);
                        } else {
                          notify.success(editingPlan?.id ? 'Plan actualizado' : 'Plan creado');
                          setShowPlanModal(false);
                          loadFinanzasData();
                        }
                      }}>Guardar</button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Modal Confirmar Eliminación de Pago */}
          {deletePagoModal && (
            <div className="cd-modal-overlay" onClick={() => !deletingPago && setDeletePagoModal(null)}>
              <div className="cd-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                <div style={{ padding: '24px 24px 8px', textAlign: 'center' }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 56, height: 56, borderRadius: '50%',
                    background: '#fee2e2', color: '#dc2626', marginBottom: 12,
                  }}>
                    <AlertTriangle size={28} />
                  </div>
                  <h3 style={{ margin: '0 0 6px', fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
                    ¿Eliminar este pago?
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                    Esta acción no se puede deshacer.
                  </p>
                </div>
                <div style={{
                  margin: '16px 24px', padding: '12px 14px',
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                  fontSize: '0.85rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={{ color: '#64748b' }}>Atleta</span>
                    <strong style={{ color: '#0f172a' }}>{deletePagoModal.athleteName}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={{ color: '#64748b' }}>Monto</span>
                    <strong style={{ color: '#059669' }}>{deletePagoModal.amountStr}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={{ color: '#64748b' }}>Fecha</span>
                    <strong style={{ color: '#0f172a' }}>{deletePagoModal.dateStr}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={{ color: '#64748b' }}>Método</span>
                    <strong style={{ color: '#0f172a', textTransform: 'capitalize' }}>{deletePagoModal.methodStr}</strong>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, padding: '0 24px 24px' }}>
                  <button
                    className="btn btn-club-secondary"
                    style={{ flex: 1 }}
                    disabled={deletingPago}
                    onClick={() => setDeletePagoModal(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    style={{
                      flex: 1, padding: '10px 16px', borderRadius: 6,
                      background: deletingPago ? '#fca5a5' : '#dc2626', color: 'white',
                      border: 'none', fontWeight: 700, cursor: deletingPago ? 'not-allowed' : 'pointer',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                    disabled={deletingPago}
                    onClick={async () => {
                      setDeletingPago(true);
                      const result = await clubPagosServiceProxy.delete(deletePagoModal.id);
                      setDeletingPago(false);
                      if (result.error) {
                        notify.error('Error al eliminar: ' + result.error);
                      } else {
                        const deletedId = deletePagoModal.id;
                        notify.success('Pago eliminado');
                        setClubPagos(prev => prev.filter(x => x.id !== deletedId));
                        setDeletePagoModal(null);
                        loadFinanzasData();
                      }
                    }}
                  >
                    <Trash2 size={14} /> {deletingPago ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal Registrar Pago (global — accesible desde Atletas y Finanzas) */}
          {showPagoModal && selectedMembership && (
            <div className="cd-modal-overlay" onClick={() => setShowPagoModal(false)}>
              <div className="cd-modal" onClick={e => e.stopPropagation()}>
                <div className="cd-modal-header">
                  <h3>Registrar Pago</h3>
                  <button onClick={() => setShowPagoModal(false)} className="cd-modal-close-btn"><X size={20} /></button>
                </div>
                <div className="cd-modal-body">
                  <p style={{ margin: '0 0 16px', color: '#64748b' }}>
                    <strong>{selectedMembership.gimnasts?.name}</strong> - Plan: {selectedMembership.club_planes?.name}
                  </p>
                  <input 
                    type="number" 
                    className="cd-form-input" 
                    placeholder="Monto en CLP" 
                    value={pagoForm.amount || ''} 
                    onChange={e => setPagoForm(p => ({ ...p, amount: Number(e.target.value) }))} 
                  />
                  <select 
                    className="cd-form-input" 
                    value={pagoForm.payment_method} 
                    onChange={e => setPagoForm(p => ({ ...p, payment_method: e.target.value }))}
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="yape">Yape</option>
                    <option value="plin">Plin</option>
                    <option value="bcp">BCP</option>
                    <option value="bbva">BBVA</option>
                    <option value="otro">Otro</option>
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={pagoForm.is_advance} 
                      onChange={e => setPagoForm(p => ({ ...p, is_advance: e.target.checked }))}
                    />
                    <span style={{ fontSize: '0.9rem' }}>Pago anticipado para el próximo mes</span>
                  </label>
                </div>
                <div className="cd-modal-footer">
                  <button className="btn btn-club-secondary" onClick={() => setShowPagoModal(false)}>Cancelar</button>
                  <button className="btn btn-club-primary" onClick={async () => {
                    const clubId = getClubId();
                    const plan = selectedMembership.club_planes;
                    const gymnastId = selectedMembership.gymnast_id;
                    const membershipId = selectedMembership.id;
                    
                    // Calculate period based on frequency and advance payment
                    const today = new Date();
                    let periodFrom, periodTo;
                    
                    if (pagoForm.is_advance) {
                      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                      if (plan?.frequency === 'quincenal') {
                        periodFrom = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
                        periodTo = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 15);
                      } else {
                        periodFrom = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
                        periodTo = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0);
                      }
                    } else {
                      if (plan?.frequency === 'quincenal') {
                        periodFrom = new Date(today.getFullYear(), today.getMonth(), 1);
                        periodTo = new Date(today.getFullYear(), today.getMonth(), 15);
                      } else {
                        periodFrom = new Date(today.getFullYear(), today.getMonth(), 1);
                        periodTo = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                      }
                    }
                    
                    const pagoData = {
                      club_id: clubId,
                      membership_id: membershipId,
                      gymnast_id: gymnastId,
                      plan_id: plan?.id,
                      amount: pagoForm.amount,
                      payment_date: today.toISOString().split('T')[0],
                      period_from: periodFrom.toISOString().split('T')[0],
                      period_to: periodTo.toISOString().split('T')[0],
                      payment_method: pagoForm.payment_method
                    };
                    
                    console.log('[PAGO] Saving:', pagoData);
                    const result = await clubPagosServiceProxy.create(pagoData);
                    if (result.error) {
                      notify.error('Error: ' + result.error);
                    } else {
                      notify.success('Pago registrado');
                      setShowPagoModal(false);
                      // Optimistic: añadir al estado local para que la UI refleje "Al día" al instante
                      setClubPagos(prev => [{ ...pagoData, id: result.data?.id || `tmp-${Date.now()}` }, ...(prev || [])]);
                      loadFinanzasData();
                    }
                  }}>Guardar Pago</button>
                </div>
              </div>
            </div>
          )}

          {/* ============ ANALYTICS ============ */}
          {activePage === 'analytics' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <p className="cd-page-subtitle" style={{ margin: 0, flex: 1 }}><BarChart3 size={16} />Estadísticas, comparativas y recomendaciones inteligentes sobre el rendimiento del club.</p>
                {analyticsData && (
                  <button
                    className="btn btn-club-primary"
                    onClick={() => generateAnalyticsExecutivePDF(clubInfo?.name || 'Club', analyticsData, temporalTrend, clubLogoUrl)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <Printer size={15} /> PDF Ejecutivo
                  </button>
                )}
              </div>

              {/* Tabs — modern underlined */}
              <div style={{ display: 'flex', gap: 4, margin: '20px 0', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                {[
                  { key: 'rendimiento', label: 'Rendimiento', icon: BarChart3 },
                  { key: 'aparato', label: 'Por Aparato', icon: Activity },
                  { key: 'nivel', label: 'Por Nivel', icon: Users },
                  { key: 'tendencias', label: 'Tendencias', icon: TrendingUp },
                  { key: 'alertas', label: 'Alertas IA', icon: AlertTriangle },
                  { key: 'entrenadores', label: 'Entrenadores', icon: Dumbbell },
                ].map(t => {
                  const isActive = analyticsTab === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setAnalyticsTab(t.key)}
                      style={{
                        background: 'transparent',
                        color: isActive ? '#8b5cf6' : '#64748b',
                        border: 'none',
                        padding: '10px 16px',
                        marginBottom: -1,
                        borderBottom: isActive ? '2px solid #8b5cf6' : '2px solid transparent',
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'color 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#0f172a'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#64748b'; }}
                    >
                      <t.icon size={15} /> {t.label}
                      {t.key === 'alertas' && analyticsData?.riskAlerts?.length > 0 && (
                        <span style={{
                          background: '#dc2626', color: 'white',
                          borderRadius: 10, padding: '1px 7px',
                          fontSize: '0.68rem', fontWeight: 700,
                          minWidth: 18, textAlign: 'center', marginLeft: 2,
                        }}>
                          {analyticsData.riskAlerts.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {!analyticsData && analyticsTab !== 'entrenadores' && (
                  <div className="cd-empty-state">
                    <BarChart3 size={48} />
                    <p>Sin datos de rendimiento</p>
                    <span>Los datos aparecerán cuando haya puntajes registrados en las copas</span>
                  </div>
                )}

                {analyticsTab === 'rendimiento' && analyticsData && (
                <>
                  {/* Global Stats */}
                  <div className="cd-kpi-grid" style={{ marginBottom: 20 }}>
                    <div className="cd-kpi-card cd-kpi-purple">
                      <div className="cd-kpi-icon"><Activity size={22} /></div>
                      <div className="cd-kpi-body">
                        <span className="cd-kpi-value">{analyticsData.globalAvg}</span>
                        <span className="cd-kpi-label">Promedio Global</span>
                      </div>
                    </div>
                    <div className="cd-kpi-card cd-kpi-green">
                      <div className="cd-kpi-icon"><Award size={22} /></div>
                      <div className="cd-kpi-body">
                        <span className="cd-kpi-value">{analyticsData.globalMax}</span>
                        <span className="cd-kpi-label">Máximo Puntaje</span>
                      </div>
                    </div>
                    <div className="cd-kpi-card cd-kpi-blue">
                      <div className="cd-kpi-icon"><Target size={22} /></div>
                      <div className="cd-kpi-body">
                        <span className="cd-kpi-value">{analyticsData.totalScores}</span>
                        <span className="cd-kpi-label">Total Puntajes</span>
                      </div>
                    </div>
                    <div className="cd-kpi-card cd-kpi-orange">
                      <div className="cd-kpi-icon"><Users size={22} /></div>
                      <div className="cd-kpi-body">
                        <span className="cd-kpi-value">{analyticsData.athletePerf.length}</span>
                        <span className="cd-kpi-label">Atletas con Datos</span>
                      </div>
                    </div>
                  </div>

                  {/* Charts Grid */}
                  <div className="cd-overview-grid">
                    {/* Apparatus Average */}
                    <div className="cd-card cd-card-wide">
                      <div className="cd-card-header"><Activity size={16} /> Promedio por Aparato</div>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={analyticsData.apparatusAvg} barSize={32}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="apparatus" tick={{ fontSize: 12 }} />
                          <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} />
                          <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #eee' }} />
                          <Bar dataKey="average" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Score Distribution */}
                    <div className="cd-card">
                      <div className="cd-card-header"><BarChart3 size={16} /> Distribución de Puntajes</div>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={analyticsData.distribution} barSize={28}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #eee' }} />
                          <Bar dataKey="count" fill="#10b981" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Level Distribution */}
                    <div className="cd-card">
                      <div className="cd-card-header"><Users size={16} /> Distribución por Nivel</div>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={analyticsData.levelDist} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                            {analyticsData.levelDist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Radar Chart (top athletes) */}
                    {analyticsData.radarData.length > 0 && analyticsData.athletePerf.length > 0 && (
                      <div className="cd-card cd-card-wide">
                        <div className="cd-card-header"><Target size={16} /> Radar — Top Atletas</div>
                        <ResponsiveContainer width="100%" height={280}>
                          <RadarChart data={analyticsData.radarData}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="apparatus" tick={{ fontSize: 11 }} />
                            <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                            {analyticsData.athletePerf.slice(0, 5).map((a, i) => (
                              <Radar key={a.id} name={a.name} dataKey={a.name} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.15} />
                            ))}
                            <Tooltip />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  {/* Athlete Rankings Table */}
                  <div className="cd-card" style={{ marginTop: 20 }}>
                    <div className="cd-card-header"><Medal size={16} /> Ranking de Atletas</div>
                    <div className="cd-table-wrap">
                      <table className="cd-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Atleta</th>
                            <th>Nivel</th>
                            <th>Promedio</th>
                            <th>vs Nivel</th>
                            <th>Total</th>
                            <th>Puntajes</th>
                            <th style={{ textAlign: 'center' }}>Análisis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.athletePerf.map((a, i) => {
                            const athlete = athletes.find(g => g.id === a.id);
                            const level = athlete?.level || '—';
                            const lvlGroup = analyticsData.levelComparison.find(l => l.level === level);
                            const diff = lvlGroup ? parseFloat((a.avg - lvlGroup.levelAvg).toFixed(2)) : null;
                            return (
                              <tr key={a.id}>
                                <td><span className="cd-rank-num">#{i + 1}</span></td>
                                <td><strong>{a.name}</strong></td>
                                <td>{level}</td>
                                <td><span className={`cd-score-pill ${a.avg >= 8 ? 'high' : a.avg >= 6 ? 'mid' : ''}`}>{a.avg}</span></td>
                                <td>
                                  {diff !== null ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: diff > 0 ? '#16a34a' : diff < 0 ? '#dc2626' : '#64748b', fontWeight: 600, fontSize: '0.82rem' }}>
                                      {diff > 0 ? <TrendingUp size={12} /> : diff < 0 ? <TrendingDown size={12} /> : null}
                                      {diff > 0 ? '+' : ''}{diff}
                                    </span>
                                  ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                </td>
                                <td>{a.total}</td>
                                <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{a.count}</td>
                                <td style={{ textAlign: 'center' }}>
                                  <button
                                    className="cd-action-btn cd-action-purple"
                                    onClick={() => setSelectedGymnastForAnalysis(athletes.find(g => g.id === a.id) || { id: a.id, name: a.name })}
                                    title="Ver análisis de rendimiento"
                                  >
                                    <Activity size={14} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {/* ===== TAB: POR APARATO ===== */}
              {analyticsTab === 'aparato' && analyticsData && (
                <>
                  <div className="cd-card cd-card-wide" style={{ marginBottom: 20 }}>
                    <div className="cd-card-header"><Activity size={16} /> Promedio del Club por Aparato</div>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={analyticsData.apparatusAvg.slice().sort((a,b) => b.average - a.average)} barSize={32}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="apparatus" tick={{ fontSize: 12 }} />
                        <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #eee' }} />
                        <Bar dataKey="average" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Sub-tabs de aparatos */}
                  <div className="cd-tabs" style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                    {analyticsData.apparatusRankings.map(r => {
                      const active = (selectedApparatusTab || analyticsData.apparatusRankings[0]?.apparatus) === r.apparatus;
                      return (
                        <button
                          key={r.apparatus}
                          onClick={() => setSelectedApparatusTab(r.apparatus)}
                          style={{
                            background: active ? '#8b5cf6' : '#f1f5f9',
                            color: active ? 'white' : '#475569',
                            border: 'none', padding: '7px 12px', borderRadius: 6, fontWeight: 600,
                            cursor: 'pointer', fontSize: '0.82rem'
                          }}
                        >
                          {r.apparatus} <span style={{ opacity: 0.7, marginLeft: 4 }}>({r.athletes.length})</span>
                        </button>
                      );
                    })}
                  </div>

                  {(() => {
                    const selApp = selectedApparatusTab || analyticsData.apparatusRankings[0]?.apparatus;
                    const rank = analyticsData.apparatusRankings.find(r => r.apparatus === selApp);
                    if (!rank) return null;
                    return (
                      <div className="cd-card">
                        <div className="cd-card-header"><Medal size={16} /> Ranking — {rank.apparatus}</div>
                        <div className="cd-table-wrap">
                          <table className="cd-table">
                            <thead>
                              <tr><th>#</th><th>Atleta</th><th>Nivel</th><th>Puntaje</th><th style={{ textAlign: 'center' }}>Análisis</th></tr>
                            </thead>
                            <tbody>
                              {rank.athletes.map((a, i) => (
                                <tr key={a.id}>
                                  <td><span className="cd-rank-num">#{i + 1}</span></td>
                                  <td><strong>{a.name}</strong></td>
                                  <td>{a.level || '—'}</td>
                                  <td><span className={`cd-score-pill ${a.score >= 8 ? 'high' : a.score >= 6 ? 'mid' : ''}`}>{a.score}</span></td>
                                  <td style={{ textAlign: 'center' }}>
                                    <button
                                      className="cd-action-btn cd-action-purple"
                                      onClick={() => setSelectedGymnastForAnalysis(athletes.find(g => g.id === a.id) || { id: a.id, name: a.name })}
                                      title="Análisis de rendimiento"
                                    >
                                      <Activity size={14} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}

              {/* ===== TAB: POR NIVEL ===== */}
              {analyticsTab === 'nivel' && analyticsData && (
                <>
                  <div className="cd-card cd-card-wide" style={{ marginBottom: 20 }}>
                    <div className="cd-card-header"><Users size={16} /> Promedio por Nivel</div>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={analyticsData.levelComparison} barSize={40}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="level" tick={{ fontSize: 12 }} />
                        <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #eee' }} />
                        <Bar dataKey="levelAvg" fill="#6366f1" radius={[6, 6, 0, 0]} name="Promedio" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {analyticsData.levelComparison.map(lv => (
                    <div key={lv.level} className="cd-card" style={{ marginBottom: 16 }}>
                      <div className="cd-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span><Users size={16} /> Nivel: {lv.level}</span>
                        <div style={{ display: 'flex', gap: 12, fontSize: '0.82rem', color: '#64748b' }}>
                          <span><strong>{lv.count}</strong> atletas</span>
                          <span>Prom: <strong style={{ color: '#6366f1' }}>{lv.levelAvg}</strong></span>
                          <span>Máx: <strong style={{ color: '#16a34a' }}>{lv.max}</strong></span>
                          <span>Mín: <strong style={{ color: '#dc2626' }}>{lv.min}</strong></span>
                        </div>
                      </div>
                      <div className="cd-table-wrap">
                        <table className="cd-table">
                          <thead>
                            <tr><th>#</th><th>Atleta</th><th>Promedio</th><th>vs Nivel</th></tr>
                          </thead>
                          <tbody>
                            {lv.athletes.map((a, i) => (
                              <tr key={a.id}>
                                <td><span className="cd-rank-num">#{i + 1}</span></td>
                                <td><strong>{a.name}</strong></td>
                                <td><span className={`cd-score-pill ${a.avg >= 8 ? 'high' : a.avg >= 6 ? 'mid' : ''}`}>{a.avg}</span></td>
                                <td>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: a.diff > 0 ? '#16a34a' : a.diff < 0 ? '#dc2626' : '#64748b', fontWeight: 600, fontSize: '0.82rem' }}>
                                    {a.diff > 0 ? <TrendingUp size={12} /> : a.diff < 0 ? <TrendingDown size={12} /> : null}
                                    {a.diff > 0 ? '+' : ''}{a.diff}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* ===== TAB: TENDENCIAS ===== */}
              {analyticsTab === 'tendencias' && analyticsData && (
                <>
                  {temporalLoading ? (
                    <div className="cd-empty-state">
                      <div className="cd-spinner" />
                      <p>Cargando historial de copas…</p>
                    </div>
                  ) : temporalTrend.length === 0 ? (
                    <div className="cd-empty-state">
                      <TrendingUp size={48} />
                      <p>Sin historial de copas</p>
                      <span>Las tendencias aparecen cuando hay puntajes registrados en varias copas</span>
                    </div>
                  ) : (
                    <>
                      <div className="cd-card cd-card-wide" style={{ marginBottom: 20 }}>
                        <div className="cd-card-header"><TrendingUp size={16} /> Evolución del Promedio por Copa</div>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={temporalTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
                            <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #eee' }} />
                            <Line type="monotone" dataKey="avg" stroke="#8b5cf6" strokeWidth={3} name="Promedio" dot={{ fill: '#8b5cf6', r: 4 }} />
                            <Line type="monotone" dataKey="max" stroke="#10b981" strokeWidth={2} name="Máximo" dot={{ fill: '#10b981', r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="cd-card">
                        <div className="cd-card-header"><Calendar size={16} /> Detalle por Copa</div>
                        <div className="cd-table-wrap">
                          <table className="cd-table">
                            <thead>
                              <tr><th>Copa</th><th>Fecha</th><th>Promedio</th><th>Máximo</th><th>Atletas</th><th>Δ vs anterior</th></tr>
                            </thead>
                            <tbody>
                              {temporalTrend.map((ev, i) => {
                                const prev = i > 0 ? temporalTrend[i - 1] : null;
                                const delta = prev ? parseFloat((ev.avg - prev.avg).toFixed(2)) : null;
                                return (
                                  <tr key={i}>
                                    <td><strong>{ev.name}</strong></td>
                                    <td>{ev.dateLabel}</td>
                                    <td><span className={`cd-score-pill ${ev.avg >= 8 ? 'high' : ev.avg >= 6 ? 'mid' : ''}`}>{ev.avg}</span></td>
                                    <td>{ev.max}</td>
                                    <td>{ev.athletes}</td>
                                    <td>
                                      {delta === null ? <span style={{ color: '#cbd5e1' }}>—</span> : (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: delta > 0 ? '#16a34a' : delta < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                                          {delta > 0 ? <TrendingUp size={12} /> : delta < 0 ? <TrendingDown size={12} /> : null}
                                          {delta > 0 ? '+' : ''}{delta}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ===== TAB: ALERTAS IA ===== */}
              {analyticsTab === 'alertas' && analyticsData && (
                <>
                  <div className="cd-kpi-grid" style={{ marginBottom: 20 }}>
                    <div className="cd-kpi-card">
                      <div className="cd-kpi-icon" style={{ background: '#fef2f2', color: '#dc2626' }}><AlertTriangle size={22} /></div>
                      <div className="cd-kpi-body">
                        <span className="cd-kpi-value">{analyticsData.riskAlerts.filter(r => r.severity === 'high').length}</span>
                        <span className="cd-kpi-label">Severidad Alta</span>
                      </div>
                    </div>
                    <div className="cd-kpi-card">
                      <div className="cd-kpi-icon" style={{ background: '#fffbeb', color: '#f59e0b' }}><AlertTriangle size={22} /></div>
                      <div className="cd-kpi-body">
                        <span className="cd-kpi-value">{analyticsData.riskAlerts.filter(r => r.severity === 'mid').length}</span>
                        <span className="cd-kpi-label">Severidad Media</span>
                      </div>
                    </div>
                    <div className="cd-kpi-card">
                      <div className="cd-kpi-icon" style={{ background: '#eff6ff', color: '#0ea5e9' }}><AlertTriangle size={22} /></div>
                      <div className="cd-kpi-body">
                        <span className="cd-kpi-value">{analyticsData.riskAlerts.filter(r => r.severity === 'low').length}</span>
                        <span className="cd-kpi-label">Severidad Baja</span>
                      </div>
                    </div>
                  </div>

                  {analyticsData.riskAlerts.length === 0 ? (
                    <div className="cd-empty-state">
                      <CheckCircle size={48} style={{ color: '#16a34a' }} />
                      <p style={{ color: '#16a34a' }}>¡Todo en orden!</p>
                      <span>No hay alertas IA en este momento</span>
                    </div>
                  ) : (
                    <div className="cd-card">
                      <div className="cd-card-header"><AlertTriangle size={16} /> Alertas Detectadas</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {analyticsData.riskAlerts
                          .slice()
                          .sort((a, b) => ({ high: 0, mid: 1, low: 2 }[a.severity] ?? 3) - ({ high: 0, mid: 1, low: 2 }[b.severity] ?? 3))
                          .map((alert, i) => {
                            const sevColor = alert.severity === 'high' ? '#dc2626' : alert.severity === 'mid' ? '#f59e0b' : '#0ea5e9';
                            const sevBg = alert.severity === 'high' ? '#fef2f2' : alert.severity === 'mid' ? '#fffbeb' : '#eff6ff';
                            const sevBorder = alert.severity === 'high' ? '#fecaca' : alert.severity === 'mid' ? '#fde68a' : '#bfdbfe';
                            const isAthleteAlert = alert.type !== 'aparato_debil';
                            return (
                              <div key={i} style={{ display: 'flex', gap: 12, padding: 12, background: sevBg, border: `1px solid ${sevBorder}`, borderRadius: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <AlertTriangle size={18} style={{ color: sevColor }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                    <strong style={{ fontSize: '0.9rem' }}>{alert.name}</strong>
                                    <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: sevColor, color: 'white', textTransform: 'uppercase' }}>
                                      {alert.severity === 'high' ? 'Alta' : alert.severity === 'mid' ? 'Media' : 'Baja'}
                                    </span>
                                  </div>
                                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569' }}>{alert.message}</p>
                                </div>
                                {isAthleteAlert && (
                                  <button
                                    className="cd-action-btn cd-action-purple"
                                    onClick={() => setSelectedGymnastForAnalysis(athletes.find(g => g.id === alert.id) || { id: alert.id, name: alert.name })}
                                    title="Ver análisis detallado"
                                    style={{ flexShrink: 0 }}
                                  >
                                    <Activity size={14} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ===== TAB: ENTRENADORES (toggle IA por entrenador) ===== */}
              {analyticsTab === 'entrenadores' && (() => {
                const masterEnabled = !!clubInfo?.pose_analysis_enabled;
                return (
                  <>
                    <div className="cd-card" style={{ marginBottom: 16, background: masterEnabled ? '#f5f3ff' : '#fff7ed', borderLeft: `3px solid ${masterEnabled ? '#8b5cf6' : '#f59e0b'}` }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <Sparkles size={20} style={{ color: masterEnabled ? '#8b5cf6' : '#f59e0b', flexShrink: 0, marginTop: 2 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: masterEnabled ? '#6d28d9' : '#92400e', marginBottom: 4 }}>
                            {masterEnabled ? 'Análisis IA activado para tu club' : 'Análisis IA no activado para tu club'}
                          </div>
                          <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569' }}>
                            {masterEnabled
                              ? 'Activa el switch de cada entrenador para permitirle usar el módulo de Análisis IA.'
                              : 'Contacta al administrador de la plataforma para habilitar el Análisis IA en tu club. Una vez activado, podrás decidir desde acá qué entrenadores lo usan.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {approvedCoaches.length === 0 ? (
                      <div className="cd-empty-state">
                        <Dumbbell size={48} />
                        <p>No tienes entrenadores autorizados aún</p>
                        <span>Los entrenadores aprobados aparecerán aquí con el toggle de Análisis IA</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {approvedCoaches.map(item => {
                          const coach = item.coaches || {};
                          const coachId = coach.id || item.id;
                          const coachEnabled = !!item.pose_analysis_enabled;
                          const disabled = !masterEnabled;
                          return (
                            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, background: 'white', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                              <div style={{ width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', background: '#f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {coachPhotoMap[coachId] || coach.photo_url ? (
                                  <img src={coachPhotoMap[coachId] || coach.photo_url} alt={coach.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#64748b' }}>{getInitials(coach.name)}</span>
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>{coach.name || 'Entrenador'}</div>
                                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{coach.role || item.role || 'Entrenador/a'}</div>
                              </div>
                              {/* Switch toggle */}
                              <button
                                type="button"
                                role="switch"
                                aria-checked={coachEnabled}
                                disabled={disabled}
                                onClick={() => !disabled && handleToggleCoachPoseAnalysis(item.id, !coachEnabled)}
                                title={disabled
                                  ? 'Contacta al administrador de la plataforma para habilitar Análisis IA en tu club'
                                  : coachEnabled ? 'Desactivar IA para este entrenador' : 'Activar IA para este entrenador'}
                                style={{
                                  position: 'relative',
                                  width: 48, height: 26, borderRadius: 13,
                                  border: 'none', padding: 0, flexShrink: 0,
                                  background: disabled ? '#e5e7eb' : coachEnabled ? '#8b5cf6' : '#cbd5e1',
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                  transition: 'background 0.2s',
                                  opacity: disabled ? 0.6 : 1,
                                }}
                              >
                                <span style={{
                                  position: 'absolute',
                                  top: 3, left: coachEnabled ? 25 : 3,
                                  width: 20, height: 20, borderRadius: '50%',
                                  background: 'white', transition: 'left 0.2s',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                }} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {/* ============ ANALYSIS MODAL ============ */}
          {selectedGymnastForAnalysis && (
            <GymnastAnalysisModal
              gymnast={selectedGymnastForAnalysis}
              onClose={() => setSelectedGymnastForAnalysis(null)}
            />
          )}

          {/* ============ SETTINGS ============ */}
          {activePage === 'settings' && (
            <>
              <div className="cd-settings-grid">
                {/* Club Info Form */}
                <div className="cd-card">
                  <div className="cd-card-header"><Settings size={16} /> Información del Club</div>

                  {/* Club Logo */}
                  <div className="cd-settings-logo">
                    <label htmlFor="club-logo-edit" className="cd-settings-logo-area">
                      {clubLogoPreview ? <img src={clubLogoPreview} alt="Logo" className="cd-settings-logo-img" /> : clubLogoUrl ? <img src={clubLogoUrl} alt="Logo actual" className="cd-settings-logo-img" /> : <div className="cd-settings-logo-placeholder"><Camera size={24} /><span>Logo</span></div>}
                      <input id="club-logo-edit" type="file" accept="image/*" onChange={handleClubLogoChange} style={{ display: 'none' }} />
                    </label>
                    <span className="cd-settings-logo-hint">Haz clic para cambiar el logo</span>
                  </div>

                  <div className="cd-form-grid">
                    <div className="cd-form-group"><label>Nombre del Club *</label><input type="text" value={clubForm.name} onChange={e => setClubForm({ ...clubForm, name: e.target.value })} placeholder="Nombre del club" /></div>
                    <div className="cd-form-group"><label>Correo Electrónico</label><input type="email" value={clubForm.email} disabled className="cd-input-disabled" title="El correo no se puede cambiar" /></div>
                    <div className="cd-form-group"><label><Phone size={12} /> Teléfono</label><input type="tel" value={clubForm.phone} onChange={e => setClubForm({ ...clubForm, phone: e.target.value })} placeholder="Teléfono de contacto" /></div>
                    <div className="cd-form-group"><label><MapPin size={12} /> Ubicación</label><input type="text" value={clubForm.location} onChange={e => setClubForm({ ...clubForm, location: e.target.value })} placeholder="Ciudad, País" /></div>
                    <div className="cd-form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={clubForm.is_federated} onChange={e => setClubForm({ ...clubForm, is_federated: e.target.checked })} style={{ width: 18, height: 18, accentColor: '#8b5cf6' }} />
                        Club Federado
                      </label>
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: 26 }}>Indica si tu club está afiliado a la federación</span>
                    </div>
                  </div>

                  {/* Social Media */}
                  <div style={{ marginTop: 16 }}>
                    <label className="cd-form-label-section">Redes Sociales <span style={{ fontSize: '0.65rem', fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: '#9ca3af', background: '#f4f4f5', padding: '2px 8px', borderRadius: 6, marginLeft: 8 }}>Opcional</span></label>
                    <div className="cd-form-grid" style={{ marginTop: 8 }}>
                      <div className="cd-form-group">
                        <label><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 512 512" fill="#71717a"><path d="M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256c0 120 82.7 220.8 194.2 248.5V330.1h-58.5v-74h58.5v-56.4c0-57.7 34.4-89.6 87-89.6 25.2 0 51.5 4.5 51.5 4.5v56.6h-29c-28.5 0-37.4 17.7-37.4 35.8V256h63.9l-10.2 74h-53.7v174.4C429.3 476.8 512 376 512 256z"/></svg> Facebook</label>
                        <input type="url" value={clubForm.facebook_url} onChange={e => setClubForm({ ...clubForm, facebook_url: e.target.value })} placeholder="https://facebook.com/tuclub" />
                      </div>
                      <div className="cd-form-group">
                        <label><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 448 512" fill="#71717a"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9S160.5 370.9 224.1 370.9 339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1S3.8 127.9 2 163.8c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/></svg> Instagram</label>
                        <input type="url" value={clubForm.instagram_url} onChange={e => setClubForm({ ...clubForm, instagram_url: e.target.value })} placeholder="https://instagram.com/tuclub" />
                      </div>
                      <div className="cd-form-group">
                        <label><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 512 512" fill="#71717a"><path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8l164.9-188.5L26.8 48h145.6l100.5 132.9zm-24.8 373.8h39.1L151.1 88h-42z"/></svg> X (Twitter)</label>
                        <input type="url" value={clubForm.twitter_url} onChange={e => setClubForm({ ...clubForm, twitter_url: e.target.value })} placeholder="https://x.com/tuclub" />
                      </div>
                    </div>
                  </div>

                  {/* Disciplines */}
                  <div style={{ marginTop: 16 }}>
                    <label className="cd-form-label-section">Disciplinas del Club</label>
                    <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0 0 8px' }}>Selecciona las disciplinas que practica tu club</p>
                    <div className="cd-discipline-grid">
                      {DISCIPLINE_TABS.map(tab => {
                        const isSelected = (clubForm.disciplines || []).includes(tab.key);
                        return (
                          <button
                            key={tab.key}
                            type="button"
                            className={`cd-discipline-chip ${isSelected ? 'active' : ''}`}
                            onClick={() => {
                              const current = clubForm.disciplines || [];
                              const updated = isSelected ? current.filter(k => k !== tab.key) : [...current, tab.key];
                              setClubForm({ ...clubForm, disciplines: updated });
                            }}
                          >
                            {tab.logo && <img src={tab.logo} alt={tab.name} className="cd-discipline-logo" />}
                            <span className="cd-discipline-name">{tab.name}</span>
                            <span className="cd-discipline-full">{tab.fullName}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button className="btn btn-club-primary" onClick={handleSaveClubInfo} disabled={savingClub}>
                      <Save size={14} /> {savingClub ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                  </div>
                </div>

                {/* ===== Funciones Premium ===== */}
                <div className="cd-card" style={{ borderLeft: '3px solid #8b5cf6' }}>
                  <div className="cd-card-header">
                    <Sparkles size={16} style={{ color: '#8b5cf6' }} /> Funciones Premium
                  </div>

                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                    padding: 14, borderRadius: 12,
                    background: clubInfo?.pose_analysis_enabled ? '#f0f9ff' : '#fafafa',
                    border: `1px solid ${clubInfo?.pose_analysis_enabled ? '#bae6fd' : '#e4e4e7'}`,
                    transition: 'all 0.2s',
                  }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                      background: clubInfo?.pose_analysis_enabled ? '#e0f2fe' : '#f4f4f5',
                      color: clubInfo?.pose_analysis_enabled ? '#0284c7' : '#a1a1aa',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Video size={22} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <strong style={{ fontSize: '0.95rem', color: '#18181b' }}>Análisis IA para entrenadores</strong>
                        {clubInfo?.pose_analysis_enabled && (
                          <span style={{ fontSize: '0.68rem', background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: 100, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <CheckCircle size={10} /> Activo
                          </span>
                        )}
                      </div>
                      <p style={{ margin: '0 0 8px', fontSize: '0.82rem', color: '#52525b', lineHeight: 1.5 }}>
                        Habilita el módulo de análisis biomecánico con cámara + IA para <strong>todos los entrenadores aprobados</strong> de tu club. Podrán evaluar ejercicios USAG en tiempo real y comparar ejecuciones con videos modelo.
                      </p>
                      <p style={{ margin: '0 0 12px', fontSize: '0.75rem', color: '#71717a' }}>
                        Todo el procesamiento ocurre en el dispositivo del entrenador — ningún video se sube a servidores.
                      </p>

                      {/* El flag de IA a nivel club lo administra SOLAMENTE el
                          super-admin de la plataforma. Desde acá mostramos
                          estado read-only + instrucción de contacto. */}
                      {clubInfo?.pose_analysis_enabled ? (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 8,
                          padding: '8px 14px', borderRadius: 100,
                          background: '#ecfdf5', border: '1px solid #a7f3d0',
                          color: '#065f46', fontWeight: 700, fontSize: '0.82rem',
                        }}>
                          <CheckCircle size={14} /> Módulo habilitado — gestiona los entrenadores en Analítica IA → Entrenadores
                        </div>
                      ) : (
                        <div style={{
                          padding: '10px 14px', borderRadius: 10,
                          background: '#fef9c3', border: '1px solid #fde68a',
                          color: '#713f12', fontSize: '0.82rem', lineHeight: 1.5,
                        }}>
                          <strong>Contacta al administrador de la plataforma</strong> para habilitar el Análisis IA en tu club. Una vez activado, podrás decidir cuáles de tus entrenadores tendrán acceso.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Security */}
                <div className="cd-card">
                  <div className="cd-card-header"><KeyRound size={16} /> Seguridad</div>
                  <p className="cd-text-muted" style={{ marginBottom: 16 }}>Gestiona la contraseña de acceso de tu club.</p>
                  <button className="btn btn-club-primary" onClick={handleChangePassword}>
                    Cambiar Contraseña
                  </button>
                </div>

                {/* Club Summary Stats */}
                <div className="cd-card">
                  <div className="cd-card-header"><Activity size={16} /> Resumen del Club</div>
                  <div className="cd-settings-stats">
                    <div className="cd-settings-stat">
                      <Users size={18} />
                      <div><strong>{athletes.length}</strong><span>Atletas</span></div>
                    </div>
                    <div className="cd-settings-stat">
                      <Calendar size={18} />
                      <div><strong>{approvedEvents.length}</strong><span>Copas</span></div>
                    </div>
                    <div className="cd-settings-stat">
                      <Target size={18} />
                      <div><strong>{analyticsData?.globalAvg || '—'}</strong><span>Promedio</span></div>
                    </div>
                    <div className="cd-settings-stat">
                      <Clock size={18} />
                      <div><strong>{allRequests.filter(r => r.status === 'pending').length}</strong><span>Pendientes</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Block Selection Modal for Daily Print — GLOBAL (accessible from any page) */}
        {showBlockSelectModal && (
          <div className="cd-modal-overlay" onClick={() => setShowBlockSelectModal(null)}>
            <div className="cd-coach-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440, padding: 0 }}>
              <div style={{
                textAlign: 'center', padding: '24px 24px 16px',
                borderBottom: '1px solid #f1f5f9', background: '#faf9fe',
                borderRadius: '16px 16px 0 0'
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 10px'
                }}>
                  <Printer size={22} style={{ color: '#8b5cf6' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#3f3f46' }}>Seleccionar Bloque</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                  ¿Qué bloque deseas imprimir?
                </p>
              </div>
              <div style={{ padding: '16px 24px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  className="btn btn-club-primary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => {
                    const ids = showBlockSelectModal.map(b => b.id);
                    setShowBlockSelectModal(null);
                    handlePrintDailyAttendance(ids);
                  }}
                >
                  <Printer size={14} /> Todo el Día ({showBlockSelectModal.length} bloques)
                </button>
                <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />
                {showBlockSelectModal.map(block => (
                  <button
                    key={block.id}
                    className="btn btn-club-secondary"
                    style={{
                      width: '100%', justifyContent: 'flex-start', gap: 10,
                      borderLeft: `4px solid ${block.color || '#8b5cf6'}`
                    }}
                    onClick={() => {
                      setShowBlockSelectModal(null);
                      handlePrintDailyAttendance([block.id]);
                    }}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: block.color || '#8b5cf6', flexShrink: 0 }} />
                    <span style={{ flex: 1, textAlign: 'left' }}>{block.title}</span>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                      {(block.start_time || '').slice(0, 5)}-{(block.end_time || '').slice(0, 5)}
                    </span>
                  </button>
                ))}
              </div>
              <div style={{ padding: '12px 24px 20px' }}>
                <button className="btn btn-club-secondary" style={{ width: '100%' }} onClick={() => setShowBlockSelectModal(null)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

          {/* ============ Coach Profile Modal ============ */}
          {coachProfileModal && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setCoachProfileModal(null)}>
              <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, maxWidth: 400, width: '100%', padding: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Perfil del Entrenador</h3>
                  <button onClick={() => setCoachProfileModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={20} /></button>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', margin: '0 auto 16px', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {coachPhotoMap[coachProfileModal.id] || coachProfileModal.photo_url ? (
                      <img src={coachPhotoMap[coachProfileModal.id] || coachProfileModal.photo_url} alt={coachProfileModal.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#64748b' }}>
                        {(coachProfileModal.name || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    )}
                  </div>
                  <h2 style={{ margin: '0 0 8px', fontSize: '1.25rem' }}>{coachProfileModal.name}</h2>
                  {coachProfileModal.location && (
                    <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.9rem' }}><MapPin size={14} /> {coachProfileModal.location}</p>
                  )}
                  
                  {(coachProfileModal.experience_years || (coachProfileModal.specialties && coachProfileModal.specialties.length > 0)) && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 16 }}>
                      {coachProfileModal.experience_years && (
                        <div style={{ textAlign: 'center' }}>
                          <Briefcase size={18} />
                          <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{coachProfileModal.experience_years}</div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Años exp.</div>
                        </div>
                      )}
                      {coachProfileModal.specialties && (() => {
                        const specs = Array.isArray(coachProfileModal.specialties) ? coachProfileModal.specialties : [coachProfileModal.specialties];
                        return specs.length > 0 ? (
                          <div style={{ textAlign: 'center' }}>
                            <Award size={18} />
                            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{specs.length}</div>
                            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Especialidades</div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                  
                  {coachProfileModal.specialties && (() => {
                    const specs = Array.isArray(coachProfileModal.specialties) ? coachProfileModal.specialties : [coachProfileModal.specialties];
                    return specs.length > 0 ? (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6, marginTop: 8 }}>
                          {specs.map((specKey, i) => {
                            const tab = DISCIPLINE_TABS.find(t => t.key === specKey);
                            return (
                              <span key={i} style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: 16, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                                {tab?.logo && <img src={tab.logo} alt="" style={{ width: 14, height: 14 }} />}
                                {tab?.fullName || specKey}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  {coachProfileModal.bio && (
                    <div style={{ textAlign: 'left', marginTop: 12 }}>
                      <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 6 }}>Biografía</p>
                      <p style={{ background: '#f8fafc', padding: 10, borderRadius: 8, fontSize: '0.85rem', margin: 0, borderLeft: '3px solid #8b5cf6' }}>{coachProfileModal.bio}</p>
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                  <button className="btn btn-club-secondary" style={{ flex: 1 }} onClick={() => setCoachProfileModal(null)}>Cerrar</button>
                  {coachProfileModal.cv_url && (
                    <button className="btn btn-club-primary" style={{ flex: 1 }} onClick={() => { setCoachProfileModal(null); setCoachCvModal(coachProfileModal.cv_url); }}>
                      <FileText size={14} /> Currículo
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ============ Coach CV Viewer Modal ============ */}
          {coachCvModal && (
            <div className="cd-modal-overlay" onClick={() => setCoachCvModal(null)}>
              <div className="cd-cv-viewer" onClick={e => e.stopPropagation()}>
                <div className="cd-cv-viewer-header">
                  <h3><FileText size={16} /> Currículo del Entrenador</h3>
                  <button className="cd-cv-close-btn" onClick={() => setCoachCvModal(null)}><X size={18} /></button>
                </div>
                <iframe src={coachCvModal} className="cd-cv-iframe" title="Currículo del Entrenador" />
              </div>
            </div>
          )}

        <CategoryFooter />
      </main>
      <IrmaFloatingButton />
    </div>
  );
};

export default ClubDashboard;
